import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, ElementRef, ViewEncapsulation } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import CurrencyList from 'currency-list';
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx-js-style';
import { FeeManagementService } from '../feesmanage/feesmanage.service';
import { BrandService } from '../brand/brand.service';

declare var customToastr: any;
declare var $: any;

interface AcademicYearItem {
  pkAcademicId: number;
  Year: string;
  InActive: number;
  StartDate?: string;
  EndDate?: string;
  current?: boolean;
  notstarted?: boolean;
  completed?: boolean;
}

interface ClassFeeRow {
  classNumber: string;
  classDivision: string;
  totalFee: number;
  discountFee: number;
  collectedFee: number;
  pendingFee: number;
  collectionPercent: number;
}

// Shapes below mirror FeesManagementComponent's own (untyped) fields
// exactly — see that component's discountDetails()/addDiscountpopup()/
// appliedStudentDiscount() and feesmanage.component.html's #add-discount/
// #apply-discount/#appliedDiscountStudent modals, which this tab ports.
interface DiscountRow {
  pkSchoolDiscountId: number;
  discountName: string;
  FeeStructureName?: string;
  DiscountAmountType: 'AMOUNT' | 'PERCENT';
  discountAmount: number;
  discountPercentage: number;
  appliedTo: number;
  fkFeeStructuresId?: number | string;
  pkStudentsCategoryId?: number;
  createdOn?: string;
  isLoading?: boolean;
}

interface DiscountFeeTerm {
  pkFeesTermId: number;
  TermName: string;
  selected?: boolean;
}

interface DiscountFeeType {
  pkFeesTypeId: number;
  Name: string;
  isIncludedInTerm: boolean;
  selected?: boolean;
  expand?: boolean;
  terms: DiscountFeeTerm[];
}

interface DiscountBusTerm {
  pkBusStopTermId: number;
  termName: string;
  selected?: boolean;
}

interface DiscountStudentRow {
  studentId: number;
  studentName: string;
  parentName: string;
  registrationId: string;
  mobileNumber: string;
  className: string;
  select?: boolean;
}

interface AppliedDiscountStudentRow {
  studentId: number;
  studentName: string;
  registrationId: string;
  DiscountAmount: number;
  classNumber: string;
  classDivision: string;
  TypeName?: string;
  TermName?: string;
  busTermName?: string;
  CreatedDate: string;
  ReceiptNumber: string;
  isBlocked?: number;
}

// Fee Collection shapes mirror FeesManagementComponent's own (untyped)
// fields exactly — see that component's searchStudent()/
// fetchFeeCollectionInfo()/getStudentFeeList()/makePayment()/
// proceedToPaymentMode()/proceedToPayment() and feesmanage.component.html's
// search box + fee-selection tables + payment wizard, which this tab ports.
interface FeeCollectionStudent {
  studentId: number;
  classId: number;
  studentName: string;
  parentName?: string;
  registrationId?: string;
  mobileNumber?: string;
  className?: string;
  classNumber?: string;
  classDivision?: string;
  BusStopName?: string;
  isLoading?: boolean;
}

interface FeeCollectionLineItem {
  // Raw API fields (names vary by feeType — kept as-is rather than
  // normalized, since that's how the old component's own templates
  // read them: t.discount || t.Discount || 0, etc.)
  feeType: 'TERM' | 'OTHER' | 'BUS';
  Name?: string;
  TermName?: string;
  termName?: string;
  DueDate?: string;
  termDueDate?: string;
  Balance: number;
  Paid?: number;
  Discount?: number;
  discount?: number;
  lateFee?: number;
  pkLateFeesId?: number;
  waiverFees?: boolean;
  isBusFee?: boolean;
  busId?: number;
  busStopId?: number;
  busTermId?: number;
  fkFeesTermId?: number;
  fkFeesTypeId?: number;
  termId?: number;
  termTypeId?: number;
  typeId?: number;
  pkOtherFeesId?: number;
  pkOtherFeeTermId?: number;
  typename?: string;
  expandedTerms?: { feesTermAmount: number; Paid: number; discount: number; fkFeesTypeId?: number }[];
  // Client-side selection state
  selected?: boolean;
  disabled?: boolean;
  isOldFee?: boolean;
  feeFault?: boolean;
}

interface FeeCollectionHistoryRow {
  pkFeesCollectionId?: number;
  ReceiptNumber: string;
  studentId?: number;
  fkStudentId?: number;
  Name?: string;
  isBusFee?: boolean;
  Discount?: number;
  discount?: number;
  DueDate?: string;
  Amount: number;
  lateFees?: number;
  CreatedDate: string;
  ActualCollectionDate?: string;
  ModeOfPayment?: string;
  username?: string;
  isLoading?: boolean;
}

// Fees Board shapes mirror FeesManagementComponent's own (untyped) fields
// exactly — see that component's #feeboard tab-pane (feesmanage.
// component.html lines 285-684) and its backing TS (roughly lines
// 1900-3120, 6400-7400), which this tab ports. Scope: the 7 sub-tabs that
// are actually live/clickable today (Standards view, Fee Structure,
// Student Categories, Fee Components, Terms, Late Fees, Other Fees). The
// orphaned #term-fee-configuration tab-pane, the half-built "merge
// classes into groups" feature, and the disabled (*ngIf="false") Fee
// Structure/Fee Component delete buttons are NOT ported — confirmed dead/
// unreachable in the old component.
interface FbStandardRow {
  classId: number;
  classNumber: string;
  classDivision: string;
  structures: { pkFeeStructuresId: number; Name: string; isConfigured?: 'YES' | 'NO' }[];
}

interface FbFeeStructureRow {
  pkFeeStructuresId: number;
  Name: string;
  Description?: string;
  appliesTo?: string;
  fkStudentsCategoryId?: number;
  collectedAmount?: number;
}

interface FbStudentCategoryRow {
  pkStudentsCategoryId: number;
  Name: string;
  Description?: string;
  studentCount?: number;
  collectedAmount?: number;
}

interface FbFeeComponentRow {
  feesTypeId: number;
  Name: string;
  DueDate?: string;
  includeInTerm: number;
  AppliesTo?: string;
}

interface FbTermRow {
  pkFeesTermId: number;
  TermName: string;
  DueDate?: string;
}

interface FbFeeBoardType {
  pkFeesTypeId: number;
  Name: string;
  Amount: number | null;
}

interface FbFeeBoardStructure {
  pkFeeStructuresId: number;
  Name: string;
  feeTypes: FbFeeBoardType[];
}

interface FbTermFeeTerm {
  pkFeesTermId: number;
  TermName: string;
  PaymentAmount: number | null;
}

interface FbTermFeeConfiguration {
  pkFeesTypeId: number;
  fkClassId: number;
  Name: string;
  Amount: number;
  termFees: FbTermFeeTerm[];
}

interface FbLateFeeRow {
  pkLateFeesId: number;
  lateDays: number;
  lateFees: number;
}

interface FbOtherFeeRow {
  pkOtherFeesId: number;
  Name: string;
  amount: number;
  collectedAmount?: number;
  studentsCount?: number;
  numberOfTerms?: number;
}

interface FbOtherFeeTermRow {
  pkOtherFeeTermId?: number;
  termName: string;
  termAmount: number | null;
  termDueDate?: string;
  paidAmount?: number;
  termStatus?: 'NEW' | 'UPDATE' | 'DELETE';
}

type FeesV2Tab = 'overview' | 'feesBoard' | 'feeCollection' | 'discounts' | 'settings';
type FeesBoardSubTab = 'standards' | 'structures' | 'categories' | 'components' | 'terms' | 'lateFees' | 'otherFees';

// Overview tab only, wired to backend endpoints the existing (huge,
// ~9000-line) FeesManagementComponent already uses — see
// FeeManagementService.getSchoolFee('summarywholeclass', ...)/
// fetchAcademicList. The 5 summary stat cards instead call
// getSchoolWiseFeesOverview, a single endpoint returning total/active/
// deactive slices in one response (replacing the old two-call
// fetchFeeDashDetails + getPaidLateFeeCount combo, which only ever
// covered the school-wide total). The other 4 tabs are placeholder shells
// for now (not ported from the old component), per the agreed scope: a
// new, separate v2 component rather than a rewrite of the existing one.
//
// Two real data gaps confirmed against the old component's own template
// (feesmanage.component.html) rather than assumed:
//   - No per-class student-count field exists in the
//     wholeSummaryFeeDetailsByWholeClass response — omitted from the
//     breakdown table rather than fabricated.
//   - No per-class late-fee field exists either — late fees are only
//     available as a single school-wide total (getPaidLateFeeCount), so
//     the breakdown table has no Late Fees column; the stat card covers it.
@Component({
  selector: 'app-feesmanage-v2',
  templateUrl: './feesmanage-v2.component.html',
  // Every real "-v2"/legacy routed page (DashboardV2Component,
  // FeesManagementComponent, ...) re-lists these same legacy theme files
  // in its OWN styleUrls, even though angular.json already loads them
  // globally — with ViewEncapsulation.None, Angular injects a component's
  // styleUrls as a fresh <style> block at the moment that component first
  // mounts, which re-applies these rules AFTER whatever else has been
  // inserted into <head> by then. Without this, #menubar's width rule
  // (.menubar-pin #menubar { width: 240px }, defined in materialadmin.css)
  // was losing the cascade on this page — relying solely on angular.json's
  // global bundle wasn't enough once Angular's lazy per-route styles
  // outscoped it. Keep this array identical to DashboardV2Component's.
  styleUrls: [
    './../../assets/css/theme-default/bootstrap.css',
    './../../assets/css/theme-default/materialadmin.css',
    './../../assets/css/theme-default/font-awesome.min.css',
    './../../assets/css/theme-default/material-design-iconic-font.min.css',
    './../../assets/css/theme-default/libs/DataTables/jquery.dataTables.css',
    './../../assets/css/theme-default/libs/bootstrap-datepicker/datepicker3.css',
    './../../assets/css/theme-default/libs/DataTables/extensions/dataTables.colVis.css',
    './../../assets/css/theme-default/libs/DataTables/extensions/dataTables.tableTools.css',
    './../../_crop/css/style.css',
    './../../_crop/css/style-example.css',
    './feesmanage-v2.component.css',
  ],
  encapsulation: ViewEncapsulation.None,
  providers: [FeeManagementService],
})
export class FeesManageV2Component implements OnInit, AfterViewInit, OnDestroy {
  brand = this.brandService.brand;

  @ViewChild('tabsTrack') private tabsTrackRef?: ElementRef<HTMLElement>;
  @ViewChild('tabIndicator') private tabIndicatorRef?: ElementRef<HTMLElement>;
  private resizeListener = () => this.placeTabIndicator();

  activeTab: FeesV2Tab = 'overview';

  getSchoolId: string | null = null;
  getAuthToken: string | null = null;

  // Ports FeesManagementComponent's academicPermissions (ts:237, populated
  // in openEnabledTabs() from localStorage['adminPermissionlist'],
  // feesmanage.component.ts:819-826) 1:1 — gates tab/sub-tab visibility
  // exactly like the old component's own .html [style.display] bindings.
  academicPermissions: Record<string, string> = {};
  currentAcademicState: 'RUNNING' | 'NOT_STARTED' | 'COMPLETED' | '' = '';

  get canSeeOverview(): boolean {
    return this.academicPermissions.SCHOOL_FEES_DASHBOARD === 'ENABLED';
  }
  get canSeeFeesBoard(): boolean {
    return (
      this.academicPermissions.FEES_BOARD === 'ENABLED' &&
      (this.currentAcademicState === 'RUNNING' ||
        this.currentAcademicState === 'NOT_STARTED' ||
        this.academicPermissions.FEES_CONFIG_PAST_ACADEMIC === 'ENABLED')
    );
  }
  get canSeeFeeCollection(): boolean {
    return (
      this.academicPermissions.FEES_COLLECTION === 'ENABLED' ||
      this.academicPermissions.SCHOOL_ADMIN_FEE_COLLECTION_VIEW === 'ENABLED'
    );
  }
  get canSeeDiscounts(): boolean {
    return this.academicPermissions.FEES_DISCOUNT === 'ENABLED';
  }
  get canSeeSettings(): boolean {
    return (
      this.academicPermissions.FEES_SETTINGS === 'ENABLED' &&
      (this.currentAcademicState === 'RUNNING' || this.currentAcademicState === 'NOT_STARTED')
    );
  }
  // Sub-tab-level gates
  get canSeeLateFeeSubTab(): boolean {
    return this.academicPermissions.LATE_FEE_COLLECTION === 'ENABLED';
  }
  get canSeeOtherFeesSubTab(): boolean {
    return this.academicPermissions.OTHER_FEES === 'ENABLED';
  }
  get canCollectFees(): boolean {
    return this.academicPermissions.FEES_COLLECTION === 'ENABLED';
  }
  get canViewFeeCollectionHistory(): boolean {
    return this.academicPermissions.SCHOOL_ADMIN_FEE_COLLECTION_VIEW === 'ENABLED';
  }
  get canEditStudent(): boolean {
    return this.academicPermissions.EDIT_STUDENT === 'ENABLED';
  }
  get canUseOldFeeCollection(): boolean {
    return this.academicPermissions.OLD_FEE_COLLECTION === 'ENABLED';
  }
  get canDeleteCollectedFee(): boolean {
    return this.academicPermissions.DELETE_COLLECTED_FEE === 'ENABLED';
  }

  private loadAcademicPermissions(): void {
    const raw = localStorage.getItem('adminPermissionlist');
    this.academicPermissions = raw ? JSON.parse(raw) : {};
  }

  // Ports openEnabledTabs()'s priority-chain fallback exactly
  // (feesmanage.component.ts:819-868): if the admin can't see Overview,
  // walk Fees Board → Fee Collection → Discounts → Settings and land on
  // the first one they're actually permitted to see, instead of always
  // defaulting to (and always permitting) 'overview' regardless of
  // permissions.
  private applyDefaultTabFromPermissions(): void {
    if (this.canSeeOverview) {
      this.activeTab = 'overview';
      return;
    }
    if (this.canSeeFeesBoard) {
      this.activeTab = 'feesBoard';
    } else if (this.canSeeFeeCollection) {
      this.activeTab = 'feeCollection';
    } else if (this.canSeeDiscounts) {
      this.activeTab = 'discounts';
    } else if (this.canSeeSettings) {
      this.activeTab = 'settings';
    } else {
      // A sub-admin with only the school-level Fees Management permission and
      // none of the section permissions: nothing to show but the year selector.
      this.activeTab = null as any;
      return;
    }
    // setActiveTab (not just assigning activeTab) so the lazy-load
    // triggers for whichever tab the admin actually lands on.
    this.setActiveTab(this.activeTab);
  }

  /** True when the admin may open at least one section (tab). */
  get hasAnyFeesTab(): boolean {
    return this.canSeeOverview || this.canSeeFeesBoard || this.canSeeFeeCollection || this.canSeeDiscounts || this.canSeeSettings;
  }

  academicList: AcademicYearItem[] = [];
  currentAcademicId: number | null = null;
  currentAcademicYearLabel = '';

  currencyCode = 'INR';
  get currencySymbol(): string {
    // currency-list's own INR entry has a data bug — its "symbol" field is
    // the ASCII string "Rs" for every locale (not the ₹ sign), unlike
    // every other currency's entry, which is correct. Special-cased here
    // rather than distrusting the whole package.
    if (this.currencyCode === 'INR') {
      return '₹';
    }
    try {
      return CurrencyList.get(this.currencyCode)?.symbol || this.currencyCode;
    } catch {
      return this.currencyCode;
    }
  }

  loadingStats = true;
  totalAmount = 0;
  collectedAmount = 0;
  discountAmount = 0;
  pendingAmount = 0;
  lateFeesAmount = 0;

  // Raw response.body from getSchoolWiseFeesOverview — kept around so
  // switching studentStatusFilter can re-derive the 5 stat amounts without
  // another API call (the endpoint always returns all three slices in one
  // response, not just the one currently selected).
  private feesOverviewByStatus: { total: any; active: any; deactive: any } | null = null;
  studentStatusFilter: 'total' | 'active' | 'inactive' = 'total';

  setStudentStatusFilter(filter: 'total' | 'active' | 'inactive'): void {
    if (this.studentStatusFilter === filter) {
      return;
    }
    this.studentStatusFilter = filter;
    this.applyFeesOverviewForCurrentStatus();
  }

  // 'inactive' (UI/toggle naming) maps to the API's 'deactive' response key.
  private applyFeesOverviewForCurrentStatus(): void {
    if (!this.feesOverviewByStatus) {
      return;
    }
    const slice = this.studentStatusFilter === 'inactive'
      ? this.feesOverviewByStatus.deactive
      : this.feesOverviewByStatus[this.studentStatusFilter];
    this.totalAmount = Number(slice?.totalExpectedCollection) || 0;
    this.collectedAmount = Number(slice?.totalCollectedAmount) || 0;
    this.discountAmount = Number(slice?.totalDiscountAmount) || 0;
    this.pendingAmount = Number(slice?.totalPendingAmount) || 0;
    this.lateFeesAmount = Number(slice?.totalLateFees) || 0;
  }

  get collectionPercent(): number {
    return this.totalAmount > 0 ? (this.collectedAmount / this.totalAmount) * 100 : 0;
  }

  // Bar segment widths as % of (collected + discount + pending + late) —
  // matches the old component's own four amounts exactly, just visualized
  // as a stacked bar instead of four separate cards only.
  get barTotal(): number {
    return this.collectedAmount + this.discountAmount + this.pendingAmount + this.lateFeesAmount;
  }
  barSegmentPercent(value: number): number {
    if (!value || this.barTotal <= 0) {
      return 0;
    }
    return Math.max((value / this.barTotal) * 100, 1.2);
  }

  // Period/Scope filter groups — ports the old component's
  // fetchFeeDetailOfStandard()'s #selectInterval (TODAY/WEEK/SUMMARY) ×
  // #selectStandard (entire school / one class standard) matrix exactly,
  // branching into the same getSchoolFee()/todayFeeByClass() endpoint
  // calls (feesmanage.component.ts:1344-1369). MONTH is commented out in
  // the old component's own HTML (dead option), so it's not ported here
  // either.
  periodOptions: { value: 'TODAY' | 'WEEK' | 'SUMMARY'; label: string }[] = [
    { value: 'TODAY', label: 'Today' },
    { value: 'WEEK', label: 'This Week' },
    { value: 'SUMMARY', label: 'Entire Academic Year' },
  ];
  selectedPeriod: 'TODAY' | 'WEEK' | 'SUMMARY' = 'SUMMARY';
  periodDropdownOpen = false;

  get selectedPeriodLabel(): string {
    return this.periodOptions.find((o) => o.value === this.selectedPeriod)?.label || '';
  }

  closePeriodDropdown(): void {
    this.periodDropdownOpen = false;
  }

  selectPeriod(value: 'TODAY' | 'WEEK' | 'SUMMARY'): void {
    this.selectedPeriod = value;
    this.periodDropdownOpen = false;
    if (this.detailsLoaded) {
      this.loadClassBreakdown();
    }
  }

  // Scope (class) options are populated from the class-standards list as
  // soon as the component loads — same as the old component's own
  // getAvailableClass() call populating #selectStandard — rather than
  // waiting for "View Fee Details" to be clicked once, so the dropdown
  // isn't empty the first time a user opens it.
  scopeOptions: { value: string; label: string; classStandardsId: number | null }[] = [
    { value: 'all', label: 'Entire School', classStandardsId: null },
  ];
  selectedScope = 'all';
  scopeDropdownOpen = false;

  detailsLoaded = false;
  detailsLoading = false;
  detailsFading = false;
  classFeeRows: ClassFeeRow[] = [];

  constructor(
    private feesService: FeeManagementService,
    private brandService: BrandService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.getSchoolId = localStorage.getItem('schoolinfo');
    this.getAuthToken = localStorage.getItem('usertoken');
    this.currencyCode = localStorage.getItem('currency') || 'INR';
    this.loadAcademicPermissions();
    this.loadAcademicList();
    this.loadScopeClassOptions();
  }

  private loadScopeClassOptions(): void {
    this.feesService.getAvailableClass().subscribe({
      next: (data: any) => {
        const standards = data?.schoolStandards || [];
        this.scopeOptions = [
          { value: 'all', label: 'Entire School', classStandardsId: null },
          ...standards.map((std: any) => ({
            value: String(std.pkClassStandardsId),
            label: std.ClassStandard,
            classStandardsId: std.pkClassStandardsId,
          })),
        ];
      },
      error: () => {
        // Keep the "Entire School" fallback already in scopeOptions.
      },
    });
  }

  ngAfterViewInit(): void {
    this.placeTabIndicator();
    window.addEventListener('resize', this.resizeListener);
    (document as any).fonts?.ready?.then(() => this.placeTabIndicator());
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.resizeListener);
  }

  setActiveTab(tab: FeesV2Tab): void {
    const permitted =
      tab === 'overview'
        ? this.canSeeOverview
        : tab === 'feesBoard'
        ? this.canSeeFeesBoard
        : tab === 'feeCollection'
        ? this.canSeeFeeCollection
        : tab === 'discounts'
        ? this.canSeeDiscounts
        : this.canSeeSettings;
    if (!permitted) {
      return;
    }
    this.activeTab = tab;
    setTimeout(() => this.placeTabIndicator());
    if (tab === 'discounts' && !this.discountsLoaded) {
      this.loadDiscounts();
    }
    if (tab === 'settings' && !this.settingsLoaded) {
      this.openFeesSettingsTab();
    }
    if (tab === 'feeCollection') {
      this.fcInitSection();
    }
    if (tab === 'feesBoard' && !this.feesBoardLoaded) {
      this.openFeesBoardTab();
    }
  }

  // Tabs are *ngIf'd by permission and the default tab is chosen after permissions load, so keep
  // the indicator glued to the active tab after every render (cheap no-op when nothing moved).
  ngAfterViewChecked(): void {
    const track = this.tabsTrackRef?.nativeElement;
    const indicator = this.tabIndicatorRef?.nativeElement;
    const active = track?.querySelector<HTMLElement>('.fv2-tab.active');
    if (!indicator || !active) {
      return;
    }
    const want = `${active.offsetLeft}|${active.offsetWidth}`;
    if (indicator.dataset['pos'] !== want) {
      indicator.dataset['pos'] = want;
      this.placeTabIndicator();
    }
  }

  private placeTabIndicator(): void {
    const track = this.tabsTrackRef?.nativeElement;
    const indicator = this.tabIndicatorRef?.nativeElement;
    if (!track || !indicator) {
      return;
    }
    const activeTabEl = track.querySelector<HTMLElement>('.fv2-tab.active');
    if (!activeTabEl) {
      indicator.style.width = '0';
      return;
    }
    indicator.style.transform = `translateX(${activeTabEl.offsetLeft}px)`;
    indicator.style.width = `${activeTabEl.offsetWidth}px`;
  }

  private loadAcademicList(): void {
    this.feesService.fetchAcademicList().subscribe({
      next: (data: any) => {
        if (data?.errorcode) {
          return;
        }
        const list: AcademicYearItem[] = data?.academicList || [];
        this.academicList = list;
        let current: AcademicYearItem | undefined;
        for (const item of list) {
          item.current = false;
          item.notstarted = false;
          item.completed = false;
          if (item.InActive === 0) {
            item.current = true;
            current = item;
          } else if (item.InActive === 1) {
            const endDate = new Date(item.EndDate);
            if (endDate < new Date()) {
              item.completed = true;
            } else {
              item.notstarted = true;
            }
          }
        }
        const active = current || list[0];
        if (active) {
          this.currentAcademicId = active.pkAcademicId;
          this.currentAcademicYearLabel = active.Year;
          this.currentAcademicState = active.current ? 'RUNNING' : active.completed ? 'COMPLETED' : 'NOT_STARTED';
        }
        this.loadStats();
        this.applyDefaultTabFromPermissions();
      },
      error: () => {
        this.loadingStats = false;
      },
    });
  }

  academicChanged(newAcademicId: number): void {
    this.currentAcademicId = newAcademicId;
    const match = this.academicList.find((item) => item.pkAcademicId === newAcademicId);
    this.currentAcademicYearLabel = match?.Year || '';
    this.currentAcademicState = match?.current ? 'RUNNING' : match?.completed ? 'COMPLETED' : 'NOT_STARTED';
    // Same as the old component: a year change closes the open student and returns to a
    // fresh search, and the Standards board must be requested again for the new year.
    const st = this.fcSelectedStudent;
    const keepText = st
      ? this.fcSearchBy === 'REGISTRATION_ID' ? st.registrationId : this.fcSearchBy === 'MOBILE_NUMBER' ? st.mobileNumber : st.studentName
      : this.fcSearchText;
    this.fcIsStudentDeactivated = false;
    this.fcResetSearch();
    if (this.activeTab === 'feeCollection' && keepText) {
      // Old component keeps the search text and silently re-runs it for the new year.
      this.fcSearchText = String(keepText);
      this.fcSearchStudent(true);
    }
    this.fbStandardsRequested = false;
    this.fbStandards = [];
    this.fbStandardsStrList = [];
    this.fbStandardsPage = 1;
    this.fbStandardsMaxPage = 1;
    this.studentStatusFilter = 'total';
    this.loadStats();
    if (this.detailsLoaded) {
      this.loadClassBreakdown();
    }
    if (this.discountsLoaded) {
      this.loadDiscounts();
    }
    // Re-run the priority chain — e.g. switching to a past/completed
    // academic year can make the currently-active tab (Fees Board/
    // Settings) no longer permitted, same as the old component re-gating
    // these per academic year.
    if (!this.isActiveTabStillPermitted()) {
      this.applyDefaultTabFromPermissions();
    }
  }

  private isActiveTabStillPermitted(): boolean {
    switch (this.activeTab) {
      case 'overview':
        return this.canSeeOverview;
      case 'feesBoard':
        return this.canSeeFeesBoard;
      case 'feeCollection':
        return this.canSeeFeeCollection;
      case 'discounts':
        return this.canSeeDiscounts;
      case 'settings':
        return this.canSeeSettings;
      default:
        return true;
    }
  }

  private loadStats(): void {
    if (this.currentAcademicId === null) {
      return;
    }
    this.loadingStats = true;
    this.feesService.getSchoolWiseFeesOverview({ academicId: this.currentAcademicId, lang: 'en' }).subscribe({
      next: (res: any) => {
        this.feesOverviewByStatus = res?.status && res?.body ? res.body : null;
        this.applyFeesOverviewForCurrentStatus();
        this.loadingStats = false;
      },
      error: () => {
        this.feesOverviewByStatus = null;
        this.loadingStats = false;
      },
    });
  }

  closeScopeDropdown(): void {
    this.scopeDropdownOpen = false;
  }

  selectScope(value: string): void {
    this.selectedScope = value;
    this.scopeDropdownOpen = false;
    if (this.detailsLoaded) {
      this.loadClassBreakdown();
    }
  }

  get selectedScopeLabel(): string {
    return this.scopeOptions.find((o) => o.value === this.selectedScope)?.label || '';
  }

  resetScope(event: MouseEvent): void {
    this.selectedScope = 'all';
    (event.currentTarget as HTMLElement)?.blur();
    if (this.detailsLoaded) {
      this.loadClassBreakdown();
    }
  }

  viewFeeDetails(): void {
    if (this.currentAcademicId === null) {
      return;
    }
    this.loadClassBreakdown();
  }

  // Ports fetchFeeDetailOfStandard()'s exact dataFor/API branching
  // (feesmanage.component.ts:1344-1369): TODAY uses a separate
  // todayFeeByClass()/todayFeeDetailsByClass endpoint when a specific
  // class standard is selected (and a dedicated whole-school endpoint
  // otherwise), while WEEK/SUMMARY both go through getSchoolFee() with
  // the matching wholeclass/forclass dataFor key.
  private loadClassBreakdown(): void {
    if (this.currentAcademicId === null) {
      return;
    }
    this.detailsLoading = true;
    this.detailsFading = true;

    const scopeOption = this.scopeOptions.find((o) => o.value === this.selectedScope);
    const classStandardsId = scopeOption?.classStandardsId ?? null;

    const request$ =
      this.selectedPeriod === 'TODAY'
        ? classStandardsId === null
          ? this.feesService.getSchoolFee('wholeclass', null, this.currentAcademicId)
          : this.feesService.todayFeeByClass(new Date().toISOString(), classStandardsId, this.currentAcademicId)
        : this.selectedPeriod === 'WEEK'
        ? this.feesService.getSchoolFee(classStandardsId === null ? 'weekwholeclass' : 'weekforclass', classStandardsId, this.currentAcademicId)
        : this.feesService.getSchoolFee(classStandardsId === null ? 'summarywholeclass' : 'summaryforclass', classStandardsId, this.currentAcademicId);

    request$.subscribe({
      next: (data: any) => {
        const rows: any[] = Array.isArray(data) ? data : [];
        this.classFeeRows = rows.map((row) => {
          const totalFee = Number(row.totalFee) || 0;
          const discountFee = Number(row.discountFee) || 0;
          const collectedFee = Number(row.collectedFee) || 0;
          const pendingFee = totalFee - discountFee - collectedFee;
          return {
            classNumber: row.classNumber,
            classDivision: row.classDivision,
            totalFee,
            discountFee,
            collectedFee,
            pendingFee,
            collectionPercent: totalFee > 0 ? (collectedFee / totalFee) * 100 : 0,
          };
        });
        this.detailsLoaded = true;
        this.detailsLoading = false;
        setTimeout(() => {
          this.detailsFading = false;
        }, 250);
      },
      error: () => {
        this.detailsLoading = false;
        this.detailsFading = false;
      },
    });
  }

  get filteredClassFeeRows(): ClassFeeRow[] {
    return this.classFeeRows;
  }

  get breakdownTotals() {
    const rows = this.filteredClassFeeRows;
    return rows.reduce(
      (acc, row) => ({
        totalFee: acc.totalFee + row.totalFee,
        discountFee: acc.discountFee + row.discountFee,
        collectedFee: acc.collectedFee + row.collectedFee,
        pendingFee: acc.pendingFee + row.pendingFee,
      }),
      { totalFee: 0, discountFee: 0, collectedFee: 0, pendingFee: 0 }
    );
  }

  get breakdownTotalPercent(): number {
    const totals = this.breakdownTotals;
    return totals.totalFee > 0 ? (totals.collectedFee / totals.totalFee) * 100 : 0;
  }

  // Export popup for the class breakdown table — ports
  // FeesManagementComponent's openStandardFeeExportPopUp()/
  // exportStandardFeeDetailsToExcel()/exportStandardFeeDetailsToPrint()
  // (ts:1195-1340ish) against the SAME in-memory table data (no extra
  // fetch — it exports whatever filteredClassFeeRows currently shows,
  // same as the old component exporting standardFeeDetails as-is).
  standardFeeExportPopupOpen = false;

  openStandardFeeExportPopup(): void {
    this.standardFeeExportPopupOpen = true;
  }

  closeStandardFeeExportPopup(): void {
    this.standardFeeExportPopupOpen = false;
  }

  private buildStandardFeeDetailsRows() {
    const rows = this.filteredClassFeeRows;
    const headerRow = [
      'Class',
      `Total Fees (${this.currencySymbol})`,
      `Discount Amount (${this.currencySymbol})`,
      `Collected Amount (${this.currencySymbol})`,
      `Pending Amount (${this.currencySymbol})`,
    ];
    const dataRows = rows.map((row) => [
      `${row.classNumber}-${row.classDivision}`,
      row.totalFee,
      row.discountFee,
      row.collectedFee,
      row.pendingFee,
    ]);
    const totals = this.breakdownTotals;
    const totalRow = ['Total', totals.totalFee, totals.discountFee, totals.collectedFee, totals.pendingFee];
    return { headerRow, dataRows, totalRow };
  }

  exportStandardFeeDetailsToExcel(): void {
    const schoolInfo = JSON.parse(localStorage.getItem('schoolAllInfo') || '{}');
    const schoolName = schoolInfo.schoolName || '';
    const { headerRow, dataRows, totalRow } = this.buildStandardFeeDetailsRows();

    const worksheet: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet([]);
    XLSX.utils.sheet_add_aoa(worksheet, [[schoolName]], { origin: 'A1' });
    worksheet['A1'].s = { font: { name: 'Arial', sz: 22, bold: true }, alignment: { horizontal: 'center' } };
    worksheet['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: headerRow.length - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: headerRow.length - 1 } },
    ];
    XLSX.utils.sheet_add_aoa(worksheet, [[this.currentAcademicYearLabel || '']], { origin: 'A2' });
    worksheet['A2'].s = { font: { name: 'Arial', sz: 12, bold: true }, alignment: { horizontal: 'center' } };
    XLSX.utils.sheet_add_aoa(worksheet, [headerRow], { origin: 'A3' });
    XLSX.utils.sheet_add_aoa(worksheet, dataRows, { origin: 'A4' });
    for (let col = 0; col < headerRow.length; col++) {
      const cellRef = XLSX.utils.encode_cell({ r: 2, c: col });
      if (worksheet[cellRef]) {
        worksheet[cellRef].s = { font: { bold: true, color: { rgb: '000000' } }, alignment: { horizontal: 'center' } };
      }
    }
    const totalRowIndex = 3 + dataRows.length;
    XLSX.utils.sheet_add_aoa(worksheet, [totalRow], { origin: { r: totalRowIndex, c: 0 } });
    for (let col = 0; col < headerRow.length; col++) {
      const cellRef = XLSX.utils.encode_cell({ r: totalRowIndex, c: col });
      if (worksheet[cellRef]) {
        worksheet[cellRef].s = { font: { bold: true, color: { rgb: '000000' } }, alignment: { horizontal: 'center' } };
      }
    }
    const workbook: XLSX.WorkBook = { Sheets: { 'Fees Details': worksheet }, SheetNames: ['Fees Details'] };
    const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob: Blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    FileSaver.saveAs(blob, 'Fees Details.xlsx');
    this.closeStandardFeeExportPopup();
  }

  exportStandardFeeDetailsToPrint(): void {
    const { headerRow, dataRows, totalRow } = this.buildStandardFeeDetailsRows();
    const schoolInfo = JSON.parse(localStorage.getItem('schoolAllInfo') || '{}');
    const schoolName = schoolInfo.schoolName || '';

    const rowsHtml = dataRows
      .map((r) => `<tr>${r.map((c) => `<td style="padding:6px 10px;border:1px solid #ccc;">${c}</td>`).join('')}</tr>`)
      .join('');
    const totalHtml = `<tr style="font-weight:bold;">${totalRow.map((c) => `<td style="padding:6px 10px;border:1px solid #ccc;">${c}</td>`).join('')}</tr>`;
    const headHtml = `<tr>${headerRow.map((h) => `<th style="padding:6px 10px;border:1px solid #ccc;background:#f4f6f9;">${h}</th>`).join('')}</tr>`;

    const html = `
      <html><head><title>Fees Details</title></head>
      <body style="font-family: Arial, sans-serif;">
        <h2 style="text-align:center;">${schoolName}</h2>
        <h4 style="text-align:center;">${this.currentAcademicYearLabel || ''}</h4>
        <table style="width:100%; border-collapse:collapse; margin-top:16px;">
          <thead>${headHtml}</thead>
          <tbody>${rowsHtml}${totalHtml}</tbody>
        </table>
      </body></html>`;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      return;
    }
    printWindow.document.write(html);
    printWindow.document.close();
    // Close only after the print dialog finishes: closing right after print() can leave a blank preview.
    printWindow.onafterprint = () => printWindow.close();
    setTimeout(() => { printWindow.focus(); printWindow.print(); }, 500);
    this.closeStandardFeeExportPopup();
  }

  // Class fee-configuration drill-down modal — ports
  // FeesManagementComponent's viewStudentFees()/filterFees() against the
  // same getAllStudentFeeDetails endpoint (feesmanage.component.ts:1764-
  // 1897). The old component computes per-student pending/collected
  // totals by directly overwriting DOM text via jQuery selectors after a
  // setTimeout — that was a legacy workaround, not a design choice, so
  // here it's replaced with plain computed fields built once when the
  // response arrives. The "select overdue students → send SMS" sub-flow
  // is NOT ported — messaging is a separate feature from fee
  // configuration, flagged as a deliberate scope cut, not an omission.
  classFeeDetailOpen = false;
  classFeeDetailLoading = false;
  classFeeDetailClass: ClassFeeRow | null = null;
  classFeeDetailHeaderFees: { feesId: number; FeeName: string }[] = [];
  classFeeDetailStudents: {
    studentId: number;
    studentName: string;
    parentName: string;
    structureName?: string;
    isBlocked: number;
    rollNumber?: number;
    feeByType: Record<number, { amount: number | null; collected: number; pending: number; isOverdue: boolean; isCollected: boolean }>;
    rawFees: { feesId: number; FeeName: string; TotalAmount: number; DueDate?: string; currentDate?: string }[];
    busFare: number | null;
    busCollected: number;
    busPending: number;
    totalCollected: number;
    totalPending: number;
  }[] = [];
  classFeeDetailFilter: 'ALL' | 'DUES' | 'COLLECTED' = 'ALL';
  // Raw collectedAmount/bus rows kept alongside the display-computed
  // fields above, since the export dialog uses a DIFFERENT formula than
  // the on-screen table (discount-aware: collected = total - discount,
  // vs the table's plain CollectedAmount) — matches the old component's
  // own split between viewStudentFees()'s DOM injection and
  // exportToExcel()/exportToPrint()'s separate computation exactly.
  private classFeeDetailCollectedRows = new Map<string, { CollectedAmount: number; discountAmount: number }>();
  private classFeeDetailBusFareRows: { studentId: number; BusFare: number }[] = [];
  private classFeeDetailBusCollectedRows: { fkStudentId: number; CollectedAmount: number }[] = [];

  get filteredClassFeeDetailStudents() {
    if (this.classFeeDetailFilter === 'ALL') {
      return this.classFeeDetailStudents;
    }
    const wantOverdue = this.classFeeDetailFilter === 'DUES';
    return this.classFeeDetailStudents.filter((s) =>
      Object.values(s.feeByType).some((f) => (wantOverdue ? f.isOverdue : f.isCollected))
    );
  }

  openClassFeeDetail(row: ClassFeeRow): void {
    this.classFeeDetailClass = row;
    this.classFeeDetailOpen = true;
    this.classFeeDetailFilter = 'ALL';
    this.classFeeDetailLoading = true;
    this.classFeeDetailHeaderFees = [];
    this.classFeeDetailStudents = [];
    this.fbResolveClassId(row.classNumber, row.classDivision, (classId) => {
      if (classId == null) {
        this.classFeeDetailLoading = false;
        return;
      }
      this.fbFetchClassFeeDetail(classId);
    });
  }

  // ClassFeeRow (from the Overview breakdown) doesn't carry classId — it's
  // keyed by class number/division only — so resolve it against
  // getClassListOfSchool (lightweight, used elsewhere for assign-student
  // flows) rather than depending on the Fees Board tab having been opened
  // first, or introducing yet another endpoint.
  private fbResolveClassId(classNumber: string, classDivision: string, callback: (classId: number | null) => void): void {
    if (this.fbClassListOfSchool.length > 0) {
      const match = this.fbClassListOfSchool.find((c) => c.classNumber === classNumber && c.classDivision === classDivision);
      callback(match ? match.classId : null);
      return;
    }
    this.feesService.getClassListOfSchool().subscribe({
      next: (data: any) => {
        this.fbClassListOfSchool = Array.isArray(data) ? data : [];
        const match = this.fbClassListOfSchool.find((c) => c.classNumber === classNumber && c.classDivision === classDivision);
        callback(match ? match.classId : null);
      },
      error: () => callback(null),
    });
  }

  private fbFetchClassFeeDetail(classId: number): void {
    this.feesService.getAllStudentFeeDetails(classId, this.getAuthToken, this.currentAcademicId).subscribe({
      next: (data: any) => {
        this.classFeeDetailLoading = false;
        this.classFeeDetailHeaderFees = data?.feesNames || [];

        const collectedByStudentFee = new Map<string, number>();
        const collectedRowByStudentFee = new Map<string, { CollectedAmount: number; discountAmount: number }>();
        (data?.collectedAmount || []).forEach((c: any) => {
          const key = `${c.fkStudentId}_${c.FeesId}`;
          collectedByStudentFee.set(key, c.CollectedAmount || 0);
          collectedRowByStudentFee.set(key, { CollectedAmount: c.CollectedAmount || 0, discountAmount: c.discountAmount || 0 });
        });
        this.classFeeDetailCollectedRows = collectedRowByStudentFee;
        this.classFeeDetailBusFareRows = data?.busFee || [];
        this.classFeeDetailBusCollectedRows = data?.busCollection || [];

        const busFareByStudent = new Map<number, number>();
        this.classFeeDetailBusFareRows.forEach((b: any) => busFareByStudent.set(b.studentId, b.BusFare));
        const busCollectedByStudent = new Map<number, number>();
        this.classFeeDetailBusCollectedRows.forEach((b: any) => busCollectedByStudent.set(b.fkStudentId, b.CollectedAmount || 0));

        const students = [...(data?.studentDetails || [])].sort((a: any, b: any) => (a.rollNumber ?? 0) - (b.rollNumber ?? 0));

        this.classFeeDetailStudents = students.map((student: any) => {
          const feeByType: Record<number, any> = {};
          let totalDue = 0;
          let totalCollected = 0;

          (student.fees || []).forEach((fee: any) => {
            const amount = fee.TotalAmount;
            const collected = collectedByStudentFee.get(`${student.studentId}_${fee.feesId}`) || 0;
            const pending = amount != null ? amount - collected : null;
            const isOverdue = new Date(fee.DueDate) < new Date(fee.currentDate) && amount !== 0;
            const isCollected = amount != null && amount !== 0 && amount === collected;
            feeByType[fee.feesId] = { amount, collected, pending, isOverdue, isCollected };
            totalDue += amount || 0;
            totalCollected += collected;
          });

          const busFare = busFareByStudent.has(student.studentId) ? busFareByStudent.get(student.studentId) : null;
          const busCollected = busCollectedByStudent.get(student.studentId) || 0;
          if (busFare != null) {
            totalDue += busFare;
            totalCollected += busCollected;
          }

          return {
            studentId: student.studentId,
            studentName: student.studentName,
            parentName: student.parentName,
            structureName: student.structureName,
            isBlocked: student.isBlocked,
            rollNumber: student.rollNumber,
            feeByType,
            rawFees: student.fees || [],
            busFare,
            busCollected,
            busPending: busFare != null ? busFare - busCollected : 0,
            totalCollected,
            totalPending: totalDue - totalCollected,
          };
        });
      },
      error: () => {
        this.classFeeDetailLoading = false;
      },
    });
  }

  closeClassFeeDetail(): void {
    this.classFeeDetailOpen = false;
  }

  setClassFeeDetailFilter(filter: 'ALL' | 'DUES' | 'COLLECTED'): void {
    this.classFeeDetailFilter = filter;
  }

  truncateStudentName(name: string): string {
    return name && name.length > 14 ? name.substring(0, 14) + '...' : name;
  }

  // Export inside the class fee-detail modal — ports
  // FeesManagementComponent's excelExportPopUp dialog verbatim: the same
  // toggle checkboxes (Standard Options / Advance Filter / Download
  // Pending Fees Report), the same cascading on/off behavior
  // (onStandardOptionChange/onDownloadPendingReportChange), and the same
  // default flag values (ts:7674-7727). The actual Excel/Print column
  // set is built from the SAME toggle flags, but against this component's
  // already-computed classFeeDetailStudents (per-fee-type pending/
  // collected + bus fare + totals) rather than the old method's raw
  // allStudentAllFee/other-fee-id-prefix parsing — a separate
  // per-student "other fees" breakdown was never fetched by this v2 drill
  // -down, so those specific columns are omitted rather than fabricated.
  // "Download Pending Fees Report" reuses this same dataset filtered to
  // pending-only rows (not the old component's separate whole-school
  // exportPendingFeesReportExcel()/exportMonthlyPendingReport() methods,
  // which pull in data well outside this one-class modal's scope).
  classFeeDetailExportOpen = false;
  cfdStandardOption = false;
  cfdDownloadPendingReport = false;
  cfdPendingActiveFilter = true;
  cfdPendingDeactiveFilter = false;
  cfdShowActualFees = false;
  cfdShowTotalCollected = true;
  cfdShowTotalPending = false;
  cfdShowRowLevelTotal = true;
  cfdShowColumnLevelTotal = true;
  cfdShowGrandTotal = true;
  cfdShowReceived = true;
  cfdShowBalance = true;

  openClassFeeDetailExport(): void {
    this.classFeeDetailExportOpen = true;
  }

  closeClassFeeDetailExport(): void {
    this.classFeeDetailExportOpen = false;
  }

  onCfdStandardOptionChange(newValue: boolean): void {
    if (newValue) {
      this.cfdShowTotalCollected = false;
      this.cfdShowRowLevelTotal = false;
      this.cfdShowColumnLevelTotal = false;
      this.cfdShowReceived = false;
      this.cfdShowBalance = false;
      this.cfdShowGrandTotal = false;
      this.cfdShowTotalPending = false;
      this.cfdShowActualFees = false;
    } else {
      this.cfdShowTotalCollected = true;
      this.cfdShowRowLevelTotal = true;
      this.cfdShowColumnLevelTotal = true;
      this.cfdShowReceived = true;
      this.cfdShowBalance = true;
      this.cfdShowGrandTotal = true;
    }
  }

  onCfdDownloadPendingReportChange(value: boolean): void {
    if (value) {
      this.cfdStandardOption = false;
    }
  }

  // Builds the export dataset EXACTLY like the old component's
  // exportToExcel()/exportToPrint() (ts:7730-8336): sorts fee columns by
  // each fee-type's earliest due date, splits each into Actual/Collected/
  // Pending sub-columns per toggle, and computes collected/pending with
  // the SAME discount-aware formula (collected = total - discountAmount,
  // capped at 0, with a 'D' marker for a 100%-discounted fee) — a
  // genuinely different rule from the on-screen table, which shows the
  // raw CollectedAmount with no discount adjustment, matching the old
  // component's own split between viewStudentFees() and these two export
  // methods. Per-student "other fees" columns are not produced — this
  // drill-down never fetches that separate dataset (see class field
  // comment on classFeeDetailStudents).
  private buildClassFeeDetailExportData(forPendingReport: boolean): { headerRow: string[]; rows: Record<string, any>[] } {
    const students = forPendingReport
      ? this.classFeeDetailStudents.filter((s) => {
          if (s.isBlocked === 1) {
            return false;
          }
          if (!this.cfdPendingActiveFilter && !this.cfdPendingDeactiveFilter) {
            return true;
          }
          const matchesActive = this.cfdPendingActiveFilter && s.isBlocked === 0;
          const matchesDeactive = this.cfdPendingDeactiveFilter && s.isBlocked === 1;
          return matchesActive || matchesDeactive;
        })
      : this.classFeeDetailStudents.filter((s) => s.isBlocked !== 1);

    const feesExactAmount = forPendingReport ? true : this.cfdShowActualFees;
    const isTotalCollected = forPendingReport ? true : this.cfdShowTotalCollected;
    const wantsTotalPending = forPendingReport || this.cfdShowTotalPending;
    const isRowLevelTotal = forPendingReport ? true : this.cfdShowRowLevelTotal;
    const isColumnLevelTotal = forPendingReport ? false : this.cfdShowColumnLevelTotal;
    const isGrandTotal = forPendingReport ? true : this.cfdShowGrandTotal;
    const isReceived = forPendingReport ? true : this.cfdShowReceived;
    const isBalance = forPendingReport ? true : this.cfdShowBalance;

    const feeEarliestDateMap = new Map<string, Date>();
    students.forEach((student) => {
      student.rawFees.forEach((fee) => {
        const dueDate = new Date(fee.DueDate);
        if (isNaN(dueDate.getTime())) {
          return;
        }
        const existing = feeEarliestDateMap.get(fee.FeeName);
        if (!existing || dueDate < existing) {
          feeEarliestDateMap.set(fee.FeeName, dueDate);
        }
      });
    });
    const sortedFeeNames = Array.from(feeEarliestDateMap.entries())
      .sort((a, b) => a[1].getTime() - b[1].getTime())
      .map(([name]) => name);

    const actualFeesLabel = 'Actual Fees';
    const pendingLabel = 'Pending';

    const headerRow: string[] = ['Roll No.', 'Student Name', 'Parent Name'];
    sortedFeeNames.forEach((feeName) => {
      if (feesExactAmount) {
        headerRow.push(`${feeName} ${actualFeesLabel}`);
      }
      if (isTotalCollected) {
        headerRow.push(feeName);
      }
      if (wantsTotalPending) {
        headerRow.push(`${feeName} ${pendingLabel}`);
      }
    });
    if (feesExactAmount) {
      headerRow.push('Bus Fee');
    }
    if (isTotalCollected) {
      headerRow.push('Bus Fee Collected');
    }
    if (wantsTotalPending) {
      headerRow.push('Bus Fee Pending');
    }
    if (isRowLevelTotal) {
      if (feesExactAmount) {
        headerRow.push('Total');
      }
      if (wantsTotalPending) {
        headerRow.push('Total Pending');
      }
    }
    if (isGrandTotal) {
      headerRow.push('Grand Total');
    }
    if (isReceived) {
      headerRow.push('Received');
    }
    if (isBalance) {
      headerRow.push('Balance');
    }

    const rows: Record<string, any>[] = students.map((student) => {
      const row: Record<string, any> = {
        'Roll No.': student.rollNumber || '',
        'Student Name': student.studentName,
        'Parent Name': student.parentName,
      };
      sortedFeeNames.forEach((feeName) => {
        if (feesExactAmount) {
          row[`${feeName} ${actualFeesLabel}`] = 'NA';
        }
        if (isTotalCollected) {
          row[feeName] = 'NA';
        }
        if (wantsTotalPending) {
          row[`${feeName} ${pendingLabel}`] = 'NA';
        }
      });
      if (feesExactAmount) {
        row['Bus Fee'] = 'NA';
      }
      if (isTotalCollected) {
        row['Bus Fee Collected'] = 'NA';
      }
      if (wantsTotalPending) {
        row['Bus Fee Pending'] = 'NA';
      }

      let totalAmount = 0;
      let collectedAmount = 0;
      let totalDiscount = 0;

      student.rawFees.forEach((fee) => {
        const feeName = fee.FeeName;
        if (!sortedFeeNames.includes(feeName)) {
          return;
        }
        const total = fee.TotalAmount || 0;
        const collectedRow = this.classFeeDetailCollectedRows.get(`${student.studentId}_${fee.feesId}`);
        const discountAmount = collectedRow?.discountAmount || 0;

        let collected = 0;
        let displayCollected: number | string = 0;
        if (collectedRow) {
          if (discountAmount >= total && total > 0) {
            collected = 0;
            displayCollected = 'D';
          } else {
            collected = Math.max(0, total - discountAmount);
            displayCollected = collected;
          }
          totalDiscount += discountAmount;
        }
        const pending = total - collected;

        if (feesExactAmount) {
          row[`${feeName} ${actualFeesLabel}`] = total;
        }
        if (isTotalCollected) {
          row[feeName] = displayCollected;
        }
        if (wantsTotalPending) {
          row[`${feeName} ${pendingLabel}`] = pending;
        }
        totalAmount += total;
        collectedAmount += collected;
      });

      const busFareRow = this.classFeeDetailBusFareRows.find((b) => b.studentId === student.studentId);
      const busCollectedRow = this.classFeeDetailBusCollectedRows.find((b) => b.fkStudentId === student.studentId);
      if (busFareRow) {
        const total = busFareRow.BusFare;
        const collected = busCollectedRow?.CollectedAmount || 0;
        const pending = total - collected;
        if (feesExactAmount) {
          row['Bus Fee'] = total;
        }
        if (isTotalCollected) {
          row['Bus Fee Collected'] = collected;
        }
        if (wantsTotalPending) {
          row['Bus Fee Pending'] = pending;
        }
        totalAmount += total;
        collectedAmount += collected;
      }

      const totalAfterDiscount = totalAmount - totalDiscount;
      const balanceAfterDiscount = totalAfterDiscount - collectedAmount;

      if (isRowLevelTotal) {
        if (feesExactAmount) {
          row['Total'] = totalAfterDiscount;
        }
        if (wantsTotalPending) {
          row['Total Pending'] = balanceAfterDiscount;
        }
      }
      if (isGrandTotal) {
        row['Grand Total'] = totalAfterDiscount;
      }
      if (isReceived) {
        row['Received'] = collectedAmount;
      }
      if (isBalance) {
        row['Balance'] = balanceAfterDiscount;
      }
      return row;
    });

    if (isColumnLevelTotal) {
      const totalsRow: Record<string, any> = {};
      headerRow.forEach((header) => {
        if (header === 'Roll No.' || header === 'Parent Name') {
          totalsRow[header] = '';
          return;
        }
        if (header === 'Student Name') {
          totalsRow[header] = 'Total';
          return;
        }
        let sum = 0;
        rows.forEach((row) => {
          const val = row[header];
          if (typeof val === 'number') {
            sum += val;
          }
        });
        totalsRow[header] = sum;
      });
      rows.push(totalsRow);
    }

    return { headerRow, rows };
  }

  exportClassFeeDetailToExcel(): void {
    const { headerRow, rows } = this.buildClassFeeDetailExportData(this.cfdDownloadPendingReport);
    const schoolInfo = JSON.parse(localStorage.getItem('schoolAllInfo') || '{}');
    const schoolName = schoolInfo.schoolName || '';
    const classLabel = `${this.classFeeDetailClass?.classNumber}-${this.classFeeDetailClass?.classDivision}`;

    const worksheet: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet([]);
    const titlePad = new Array(headerRow.length - 1).fill('');
    XLSX.utils.sheet_add_aoa(worksheet, [[...titlePad]], { origin: 'B1' });
    XLSX.utils.sheet_add_aoa(worksheet, [headerRow], { origin: 'A1' });
    const dataRows = rows.map((row) => headerRow.map((h) => row[h]));
    XLSX.utils.sheet_add_aoa(worksheet, dataRows, { origin: 'A2' });
    for (let col = 0; col < headerRow.length; col++) {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: col });
      if (worksheet[cellRef]) {
        worksheet[cellRef].s = { font: { bold: true, color: { rgb: 'FF0000' } }, alignment: { horizontal: 'center' } };
      }
    }
    const sheetName = this.cfdDownloadPendingReport ? 'Pending Fees' : 'Class Fee Details';
    const workbook: XLSX.WorkBook = { Sheets: { [sheetName]: worksheet }, SheetNames: [sheetName] };
    const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob: Blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    FileSaver.saveAs(blob, `${classLabel} ${this.cfdDownloadPendingReport ? 'Pending fees report' : 'Class fee details'}.xlsx`);
    this.closeClassFeeDetailExport();
  }

  // Prints via the app's existing #pf-print-area mechanism (index.html +
  // styles.css, already used by ParentFeesComponent.printReceipt()) rather
  // than a second, competing print-isolation rule of our own. That global
  // rule is `body > :not(#pf-print-area) { display: none !important }` —
  // an earlier version of this method built its own sibling host div
  // directly under <body>, which that !important rule hid along with
  // everything else, producing a blank printed page. #pf-print-area is
  // kept in normal layout via visibility (not display:none) specifically
  // so content inserted into it right before printing is never subject to
  // a layout/paint race against the synchronous window.print() call.
  exportClassFeeDetailToPrint(): void {
    const { headerRow, rows } = this.buildClassFeeDetailExportData(this.cfdDownloadPendingReport);
    const schoolInfo = JSON.parse(localStorage.getItem('schoolAllInfo') || '{}');
    const schoolName = schoolInfo.schoolName || '';
    const classLabel = `${this.classFeeDetailClass?.classNumber}-${this.classFeeDetailClass?.classDivision}`;

    const worksheet: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet([]);
    const titlePad = new Array(headerRow.length - 1).fill('');
    XLSX.utils.sheet_add_aoa(worksheet, [[...titlePad]], { origin: 'B1' });
    XLSX.utils.sheet_add_aoa(worksheet, [headerRow], { origin: 'A1' });
    const dataRows = rows.map((row) => headerRow.map((h) => row[h]));
    XLSX.utils.sheet_add_aoa(worksheet, dataRows, { origin: 'A2' });
    for (let col = 0; col < headerRow.length; col++) {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: col });
      if (worksheet[cellRef]) {
        worksheet[cellRef].s = { font: { bold: true, color: { rgb: 'FF0000' } }, alignment: { horizontal: 'center' } };
      }
    }

    const html = XLSX.utils.sheet_to_html(worksheet, { id: 'fv2-fee-report-table', header: '', footer: '', editable: false } as any);

    const printArea = document.getElementById('pf-print-area');
    if (!printArea) {
      window.print();
      this.closeClassFeeDetailExport();
      return;
    }

    const titleHtml = `<div style="font-family: Arial, sans-serif;">
      <div style="font-size:20px; font-weight:700; text-align:center;">${schoolName}</div>
      <div style="font-size:13px; text-align:center; color:#444;">Session : ${this.currentAcademicYearLabel || ''}</div>
      <div style="font-size:13px; text-align:center; color:#444;">${classLabel}</div>
      </div>`;
    printArea.innerHTML = titleHtml + html;

    const table = printArea.querySelector('#fv2-fee-report-table') as HTMLTableElement;
    if (table) {
      table.style.width = '100%';
      table.style.borderCollapse = 'collapse';
      table.style.marginTop = '16px';
      const tableRows = Array.from(table.rows);
      if (tableRows.length > 0) {
        const thead = document.createElement('thead');
        thead.appendChild(tableRows[0]);
        table.insertBefore(thead, table.firstChild);
        const tbody = document.createElement('tbody');
        tableRows.slice(1).forEach((r) => tbody.appendChild(r));
        table.appendChild(tbody);
      }
      table.querySelectorAll('th, td').forEach((cell) => {
        (cell as HTMLElement).style.border = '1px solid #ccc';
        (cell as HTMLElement).style.padding = '4px 8px';
        (cell as HTMLElement).style.fontSize = '11px';
      });
      table.querySelectorAll('td').forEach((td) => {
        if (td.textContent?.trim() === 'D') {
          (td as HTMLElement).style.color = 'red';
          (td as HTMLElement).style.fontWeight = 'bold';
          (td as HTMLElement).style.textAlign = 'center';
        }
      });
    }

    requestAnimationFrame(() => {
      // Clear only after printing finishes: Chrome builds the preview after print() returns,
      // so emptying the area straight away printed a blank page.
      window.addEventListener('afterprint', () => { printArea.innerHTML = ''; }, { once: true });
      window.print();
    });
    this.closeClassFeeDetailExport();
  }

  // ============================================================
  // Discounts tab — ports FeesManagementComponent's discountDetails()/
  // addDiscountpopup()/adddiscount()/openApplyDiscount()/
  // applyDiscountToStudent()/appliedStudentDiscount()/openDeleteDiscount()/
  // delete_discount() 1:1 against the same feeService endpoints, with the
  // new collapsible-filter-group/animated-row visual language instead of
  // the old Bootstrap table+modal styling. The "fee concession
  // form"/receipt drill-down from the Applied Students modal is NOT
  // ported in this pass — flagged as a follow-up, not silently dropped.
  // ============================================================
  discountsLoaded = false;
  discountsLoading = false;
  discountList: DiscountRow[] = [];

  feeStructureListForSelect: { pkFeeStructuresId: number; Name: string }[] = [];
  allFeeNames: { typeList: DiscountFeeType[]; termList: DiscountFeeTerm[]; busTermList: DiscountBusTerm[] } = {
    typeList: [],
    termList: [],
    busTermList: [],
  };

  addDiscountModalOpen = false;
  addDiscount = {
    feeStructuresId: 'general' as string | number,
    discountName: '',
    discountReason: '',
    discountAmount: null as number | null,
    discountAmountType: 'AMOUNT' as 'AMOUNT' | 'PERCENT',
  };
  addDiscountSaving = false;

  selectedDiscount: DiscountRow | null = null;

  applyDiscountModalOpen = false;
  classSectionsInStudentCategory: { classId: number; classNumber: string; classDivision: string }[] = [];
  selectedApplyDiscountSection: string = '';
  isStudentActiveForDiscount = true;
  discountLoader = false;
  discountStudentList: DiscountStudentRow[] = [];
  applyDiscountSaving = false;
  private sectionId: string | number | null = null;

  appliedDiscountModalOpen = false;
  appliedDiscountStudentList: AppliedDiscountStudentRow[] = [];

  deleteDiscountModalOpen = false;
  deleteDiscountSaving = false;

  truncateText(text: string, length: number): string {
    if (text && text.length > length) {
      return text.substr(0, length) + '..';
    }
    return text;
  }

  private loadDiscounts(): void {
    if (this.currentAcademicId === null) {
      return;
    }
    this.discountsLoading = true;
    this.feesService.fetchdiscount(this.getAuthToken, this.currentAcademicId).subscribe({
      next: (data: any) => {
        this.discountList = Array.isArray(data) ? data : [];
        this.discountsLoaded = true;
        this.discountsLoading = false;
      },
      error: () => {
        this.discountsLoading = false;
      },
    });
  }

  // ---- Add Discount ----
  openAddDiscountModal(): void {
    this.addDiscount = { feeStructuresId: 'general', discountName: '', discountReason: '', discountAmount: null, discountAmountType: 'AMOUNT' };
    this.allFeeNames = { typeList: [], termList: [], busTermList: [] };
    this.loadFeeStructureListForSelect();
    this.loadTermTypeBusTerms();
    this.addDiscountModalOpen = true;
  }

  closeAddDiscountModal(): void {
    this.addDiscountModalOpen = false;
  }

  private loadFeeStructureListForSelect(): void {
    this.feesService.getFeeStructureList_select(this.getAuthToken, this.currentAcademicId).subscribe({
      next: (data: any) => {
        this.feeStructureListForSelect = Array.isArray(data) ? data : [];
      },
      error: () => {
        this.feeStructureListForSelect = [];
      },
    });
  }

  private loadTermTypeBusTerms(): void {
    this.feesService.getTypeTermBusTerms(this.getAuthToken, this.currentAcademicId).subscribe({
      next: (data: any) => {
        this.allFeeNames = {
          typeList: data?.typeList || [],
          termList: data?.termList || [],
          busTermList: data?.busTermList || [],
        };
      },
      error: () => {},
    });
  }

  selectAllTermsInComponent(type: DiscountFeeType, selected: boolean): void {
    type.terms?.forEach((term) => {
      term.selected = selected;
    });
  }

  onDiscountTermToggle(type: DiscountFeeType, term: DiscountFeeTerm): void {
    if (!term.selected) {
      type.selected = false;
    }
  }

  async addDiscountSubmit(): Promise<void> {
    const hasName = !!this.addDiscount.discountName && this.addDiscount.discountName.trim().length > 0;
    const hasAmount = (this.addDiscount.discountAmount || 0) > 0;
    if (!hasName || !hasAmount) {
      const msg = await firstValueFrom(this.translate.get('pleaseFillAllTheInfo'));
      customToastr(msg, 'error');
      return;
    }

    const components: { termId: number | null; typeId: number | null; busTermId: number | null }[] = [];
    (this.allFeeNames.typeList || []).forEach((type) => {
      if (type.isIncludedInTerm) {
        (type.terms || []).forEach((term) => {
          if (term.selected) {
            components.push({ termId: term.pkFeesTermId, typeId: type.pkFeesTypeId, busTermId: null });
          }
        });
      } else if (type.selected) {
        components.push({ termId: null, typeId: type.pkFeesTypeId, busTermId: null });
      }
    });
    (this.allFeeNames.busTermList || []).forEach((busTerm) => {
      if (busTerm.selected) {
        components.push({ termId: null, typeId: null, busTermId: busTerm.pkBusStopTermId });
      }
    });

    if (components.length === 0) {
      const msg = await firstValueFrom(this.translate.get('pleaseSelectComponentsTermsToProceed'));
      customToastr(msg, 'error');
      return;
    }

    this.addDiscountSaving = true;
    this.feesService
      .addDiscount(
        components,
        this.addDiscount.discountAmountType,
        this.addDiscount.discountName,
        this.addDiscount.discountReason,
        this.addDiscount.discountAmount,
        this.addDiscount.feeStructuresId,
        this.getAuthToken,
        this.currentAcademicId
      )
      .subscribe({
        next: async (data: any) => {
          this.addDiscountSaving = false;
          if (data?.status === true) {
            const msg = await firstValueFrom(this.translate.get('discountIsAddedSuccessfully'));
            customToastr(msg, 'success');
            this.addDiscountModalOpen = false;
            this.loadDiscounts();
          } else if (data?.message) {
            customToastr(data.message, 'error');
          } else {
            const msg = await firstValueFrom(this.translate.get('discountIsNotAddedPleaseTryAgain'));
            customToastr(msg, 'error');
          }
        },
        error: async () => {
          this.addDiscountSaving = false;
          const msg = await firstValueFrom(this.translate.get('discountIsNotAddedPleaseTryAgain'));
          customToastr(msg, 'error');
        },
      });
  }

  // ---- Apply Discount ----
  openApplyDiscountModal(discount: DiscountRow): void {
    this.selectedDiscount = discount;
    this.isStudentActiveForDiscount = true;
    this.discountStudentList = [];
    this.classSectionsInStudentCategory = [];
    this.selectedApplyDiscountSection = discount.pkStudentsCategoryId ? 'all' : '';
    this.feesService.getSectionsListFromStudentsCategory(discount.fkFeeStructuresId, this.currentAcademicId).subscribe({
      next: (data: any) => {
        this.classSectionsInStudentCategory = Array.isArray(data) ? data : [];
      },
      error: () => {
        this.classSectionsInStudentCategory = [];
      },
    });
    if (discount.fkFeeStructuresId) {
      this.searchStudentsForApplyDiscount();
    }
    this.applyDiscountModalOpen = true;
  }

  closeApplyDiscountModal(): void {
    this.applyDiscountModalOpen = false;
  }

  toggleDiscountActiveFilter(): void {
    this.isStudentActiveForDiscount = !this.isStudentActiveForDiscount;
    this.searchStudentsForApplyDiscount();
  }

  searchStudentsForApplyDiscount(): void {
    const section = this.selectedApplyDiscountSection;
    this.sectionId = section;
    if (!section || section === '0' || !this.selectedDiscount) {
      return;
    }
    // Same branching as the old component's searchStudentsForApplyDiscount():
    // a category-scoped discount always loads (section 'all' = every class);
    // an unscoped one only loads for a specific section, never for 'all'.
    const categoryId = this.selectedDiscount.pkStudentsCategoryId || null;
    if (!categoryId && section === 'all') {
      return;
    }
    const classId: string | number | null = section === 'all' ? null : section;
    this.fetchDiscountStudentList(classId, this.selectedDiscount.pkStudentsCategoryId || null);
  }

  private fetchDiscountStudentList(classId: string | number | null, studentCategoryId: number | null): void {
    this.discountStudentList = [];
    this.discountLoader = true;
    const body = {
      classId,
      studentCategoryId,
      academicId: this.currentAcademicId,
      isStudentActive: this.isStudentActiveForDiscount,
      fkSchoolDiscountId: this.selectedDiscount?.pkSchoolDiscountId,
      lang: 'en',
    };
    const request$ = studentCategoryId
      ? this.feesService.getStudentCategoriesFeeStructureStudentListDiscount(body)
      : this.feesService.getStudentListDiscount(body);
    request$.subscribe({
      next: (response: any) => {
        this.discountLoader = false;
        if (response?.status && response?.body) {
          this.discountStudentList = response.body;
        }
      },
      error: () => {
        this.discountLoader = false;
        this.discountStudentList = [];
      },
    });
  }

  selectAllDiscountStudents(selected: boolean): void {
    this.discountStudentList.forEach((student) => {
      student.select = selected;
    });
  }

  async applyDiscountSubmit(): Promise<void> {
    if (!this.selectedDiscount) {
      return;
    }
    const selectedStudents = this.discountStudentList.filter((student) => student.select);
    if (selectedStudents.length === 0) {
      const msg = await firstValueFrom(this.translate.get('pleaseSelectStudentsToApplyDiscount'));
      customToastr(msg, 'error');
      return;
    }

    this.applyDiscountSaving = true;
    // Same v2-vs-legacy endpoint split as the old component's
    // applyDiscountToStudent() — discounts created after the 2017-07-25
    // cutoff use the newer, simpler applyDiscountv2 signature.
    const useV2 = !this.selectedDiscount.createdOn || new Date(this.selectedDiscount.createdOn) >= new Date('2017-07-25');
    const request$ = useV2
      ? this.feesService.applyDiscountv2(selectedStudents, this.selectedDiscount.pkSchoolDiscountId, this.getAuthToken, this.currentAcademicId)
      : this.feesService.applyDiscount(
          selectedStudents,
          this.selectedDiscount.pkSchoolDiscountId,
          this.sectionId,
          this.getAuthToken,
          this.currentAcademicId
        );
    request$.subscribe({
      next: async (data: any) => {
        this.applyDiscountSaving = false;
        if (data?.status === true) {
          this.applyDiscountModalOpen = false;
          const msg = await firstValueFrom(this.translate.get('discountIsAddedSuccessfully'));
          customToastr(msg, 'success');
          this.loadDiscounts();
        } else {
          const msg = await firstValueFrom(this.translate.get('discountIsNotAppliedPleaseTryAgain'));
          customToastr(msg, 'error');
        }
      },
      error: () => {
        this.applyDiscountSaving = false;
      },
    });
  }

  // ---- Applied students history ----
  openAppliedDiscountModal(discount: DiscountRow): void {
    this.selectedDiscount = discount;
    this.appliedDiscountStudentList = [];
    this.feesService.fetchAppliedDiscountStudent(discount.pkSchoolDiscountId, this.getAuthToken, this.currentAcademicId).subscribe({
      next: (data: any) => {
        this.appliedDiscountStudentList = Array.isArray(data) ? data : [];
      },
      error: () => {
        this.appliedDiscountStudentList = [];
      },
    });
    this.appliedDiscountModalOpen = true;
  }

  closeAppliedDiscountModal(): void {
    this.appliedDiscountModalOpen = false;
  }

  // ---- Delete Discount ----
  openDeleteDiscountModal(discount: DiscountRow): void {
    this.selectedDiscount = discount;
    this.deleteDiscountModalOpen = true;
  }

  closeDeleteDiscountModal(): void {
    this.deleteDiscountModalOpen = false;
  }

  deleteDiscountConfirm(): void {
    if (!this.selectedDiscount) {
      return;
    }
    this.deleteDiscountSaving = true;
    this.feesService.deleteDiscount(this.selectedDiscount.pkSchoolDiscountId, this.getAuthToken, this.currentAcademicId).subscribe({
      next: async (data: any) => {
        this.deleteDiscountSaving = false;
        if (data?.status) {
          const msg = await firstValueFrom(this.translate.get('discountSuccessfullyDeleted'));
          customToastr(msg, 'success');
          this.deleteDiscountModalOpen = false;
          this.loadDiscounts();
        } else {
          const msg = await firstValueFrom(this.translate.get('failedToDeleteDiscountPleaseTryAgain'));
          customToastr(msg, 'error');
        }
      },
      error: async () => {
        this.deleteDiscountSaving = false;
        const msg = await firstValueFrom(this.translate.get('failedToDeleteDiscountPleaseTryAgain'));
        customToastr(msg, 'error');
      },
    });
  }

  // ============================================================
  // Fee Collection tab — ports FeesManagementComponent's
  // searchStudent()/fetchFeeCollectionInfo()/getStudentFeeList()/
  // makePayment()/proceedToPaymentMode()/proceedToPayment()/
  // sendPaymentInfo()/getFeeReceipt()/getFeeCollectionHistory() against the
  // SAME feeService endpoints, using Angular state instead of the old
  // component's raw jQuery `$('#id').val()` reads. Scope for this pass:
  // search → select student → select fees to pay → payment wizard
  // (amount / late-fee waive-off / mode) → confirm → receipt, plus the
  // per-student payment history list. The school-wide admin "Fee
  // Collection History"/reports DataTable (date-range, mode-wise/by-admin
  // breakdown) is NOT ported in this pass — flagged as a follow-up, not
  // silently dropped, since it's a reporting feature distinct from the
  // actual collection workflow.
  // ============================================================
  fcSearchText = '';
  fcSearchBy: 'STUDENT_NAME' | 'REGISTRATION_ID' | 'MOBILE_NUMBER' = 'STUDENT_NAME';
  fcIsStudentDeactivated = false;
  fcIsSearchActive = true;
  fcStudentList: FeeCollectionStudent[] = [];
  fcSequenceFeeCollection = false;

  fcSelectedStudent: FeeCollectionStudent | null = null;
  fcStudentFeeDashDetails: any = null;
  fcFeeList: FeeCollectionLineItem[] = [];
  fcOtherFeeList: FeeCollectionLineItem[] = [];
  fcFeeSettingEnabled = false;

  fcWizardStep: 'select' | 'payment' | 'mode' = 'select';
  fcTotalSelectedAmount = 0;

  fcPayableAmount: number | null = null;
  // Computed balance of the selected fees; fcPayableAmount is the user-editable input.
  fcPayableMax = 0;
  fcPayableLateFee = 0;
  fcApplyLateFee = true;
  fcWaiveOffConfirmOpen = false;
  fcIsOldFee = false;
  fcOldFeeDate = '';
  fcOldFeeTime = '08:00';

  fcPaymentMode: 'Cash' | 'Card' | 'Card&Cash' | 'QRCode' | 'Bank-transfer' | 'DD' | 'Cheque' = 'Cash';
  fcCardReferenceNumber = '';
  fcQrReferenceNumber = '';
  fcQrDate = '';
  fcCashAmount: number | null = null;
  fcCardAmount: number | null = null;
  fcCardCashReferenceNumber = '';
  fcBankName = '';
  fcBankTransferDate = '';
  fcBankTransferNumber = '';
  fcDdDate = '';
  fcDdNumber = '';
  fcDdMicrCode = '';
  fcDdAmount: number | null = null;
  fcChequeDate = '';
  fcChequeNumber = '';
  fcChequeAmount: number | null = null;

  fcPreviewOpen = false;
  fcPreviewReqObj: any = null;
  fcPaySaving = false;
  private fcPayTimestamp = 0;

  fcReceiptOpen = false;
  fcReceipt: any[] = [];
  fcReceiptStudent: any = null;
  fcReceiptSetting: { isFeesModuleEnable: string } = { isFeesModuleEnable: '' };
  fcReceiptOpenedFrom: 'AFTER_PAYMENT' | 'FEE_HISTORY' = 'AFTER_PAYMENT';

  // Receipt totals — ported verbatim from FeesManagementComponent's own
  // getTotalAmount/getTotalLateFeeAmount/getTotalCardAmount/
  // getTotalCashAmount/getActualTotalAmount/getDiscountTotalAmount/
  // getTotalReceivedAmount/getChequeNumber/getModeOfPayDate/
  // getModeOfPayment (ts:4515-4625) — same colExists gating when
  // feeSetting.isFeesModuleEnable === 'ENABLED'.
  private fcSumReceipt(field: string): number {
    let amount = 0;
    if (this.fcReceiptSetting.isFeesModuleEnable === 'ENABLED') {
      this.fcReceipt.forEach((item) => {
        amount += item.colExists ? item[field] || 0 : 0;
      });
    } else {
      this.fcReceipt.forEach((item) => {
        amount += item[field] || 0;
      });
    }
    return amount;
  }

  fcGetTotalAmount(): number {
    return this.fcSumReceipt('Amount');
  }
  fcGetTotalLateFeeAmount(): number {
    return this.fcSumReceipt('lateFees');
  }
  fcGetTotalCardAmount(): number {
    return this.fcSumReceipt('cardAmount');
  }
  fcGetTotalCashAmount(): number {
    return this.fcSumReceipt('cashAmount');
  }
  fcGetActualTotalAmount(): number {
    return this.fcSumReceipt('ActualAmount');
  }
  fcGetDiscountTotalAmount(): number {
    return this.fcSumReceipt('DiscountAmount');
  }
  fcGetTotalReceivedAmount(): number {
    return this.fcGetTotalAmount() + this.fcGetTotalLateFeeAmount();
  }
  fcGetChequeNumber(): string {
    return this.fcReceipt[0]?.ChequeNumber || '';
  }
  fcGetModeOfPayDate(): string {
    return this.fcReceipt[0]?.ModeOfPayDate || '';
  }
  fcGetModeOfPayment(): string {
    return this.fcReceipt[0]?.ModeOfPayment || 'Cash';
  }

  fcHistoryTab = false;
  fcHistoryList: FeeCollectionHistoryRow[] = [];
  fcHistoryPage = 1;
  fcHistoryIsLastPage = true;
  fcHistoryLoading = false;

  // Placeholder follows the selected "Search By" mode.
  get fcSearchPlaceholderKey(): string {
    switch (this.fcSearchBy) {
      case 'REGISTRATION_ID':
        return 'fv2SearchByRegistrationId';
      case 'MOBILE_NUMBER':
        return 'fv2SearchByMobileNumber';
      default:
        return 'fv2SearchByStudentName';
    }
  }

  async fcSearchStudent(silent = false): Promise<void> {
    const text = (this.fcSearchText || '').trim();
    this.fcIsSearchActive = true;
    this.fcTotalSelectedAmount = 0;
    this.fcStudentList = [];
    if (!text) {
      if (!silent) {
        const msg = await firstValueFrom(this.translate.get('pleaseEnterStudentNameToSearch'));
        customToastr(msg, 'error');
      }
      return;
    }
    this.feesService
      .searchNewStudent(text, this.fcSearchBy === 'STUDENT_NAME' ? 'YES' : 'NO', this.fcSearchBy, this.currentAcademicId, this.fcIsStudentDeactivated)
      .subscribe({
        next: async (data: any) => {
          this.fcStudentList = data?.studentList || [];
          this.fcSequenceFeeCollection = data?.isFeeSequenceEnabled === 1;
          if (!(this.fcStudentList.length > 0)) {
            const msg = await firstValueFrom(this.translate.get('noStudentsFoundForYourSearch'));
            customToastr(msg, 'error');
          }
        },
        error: async () => {
          const msg = await firstValueFrom(this.translate.get('somethingWentWrongPleaseTryAgain'));
          customToastr(msg, 'error');
        },
      });
  }

  // Payable amount shown on the Card & Cash form: the entered payable amount plus late fee (if applied) — as the old screen's totalPayableAmount + payableLateFee.
  get fcPayableWithLateFee(): number {
    return (Number(this.fcPayableAmount) || 0) + (this.fcApplyLateFee ? Number(this.fcPayableLateFee) || 0 : 0);
  }

  fcModeLabelKey(mode: string): string {
    const keys: { [k: string]: string } = {
      Cash: 'cash', Card: 'card', 'Card&Cash': 'cardCash', QRCode: 'qrCode', 'Bank-transfer': 'bankTransfer', DD: 'dd', Cheque: 'cheque',
    };
    return keys[mode] || mode;
  }

  // ---- Term fee breakup (old showTermFeeBreakup): typename is "Name---Amount,Name---Amount" ----
  fcBreakupOpen = false;
  fcBreakupTitle = '';
  fcBreakupRows: { Name: string; Amount: string }[] = [];

  fcShowTermBreakup(item: any): void {
    this.fcBreakupTitle = item.TermName;
    this.fcBreakupRows = String(item.typename || '')
      .split(',')
      .filter((x) => x.trim() !== '')
      .map((x) => {
        const parts = x.split('---');
        return { Name: parts[0], Amount: parts[1] };
      });
    this.fcBreakupOpen = true;
  }

  // ---- Edit student info (same shared popup as the old Fee Collection) ----
  fcEditStudentOpen = false;

  // Profile editing is not allowed for students of a completed (old) academic year.
  get fcCanEditProfile(): boolean {
    return this.canEditStudent && !this.fcIsStudentDeactivated && this.currentAcademicState !== 'COMPLETED';
  }

  fcOpenEditStudent(): void {
    if (!this.fcCanEditProfile) {
      return;
    }
    this.fcEditStudentOpen = true;
  }

  fcCloseEditStudent(): void {
    this.fcEditStudentOpen = false;
  }

  // After a save, re-read the student's fee profile so edited details
  // (name, class, transport, structure) are reflected immediately.
  fcEditStudentSaved(): void {
    this.fcEditStudentOpen = false;
    if (this.fcSelectedStudent) {
      this.fcSelectStudent(this.fcSelectedStudent);
    }
  }

  // ---- Previous academic pending fee (ports old fetchStudentPreviousAcademicFees) ----
  get fcOldFeeBalance(): number {
    const o = this.fcStudentFeeDashDetails?.old_fee;
    return o ? (o.TermFees?.Balance || 0) + (o.TypeFees?.Balance || 0) : 0;
  }
  fcAllAcademicOpen = false;
  fcAllAcademicLoading = false;
  fcAllAcademicFee: any[] = [];

  fcSwitchAcademic(academicId: number): void {
    this.fcAllAcademicOpen = false;
    this.academicChanged(academicId);
  }

  fcFetchAllAcademicFees(): void {
    if (!this.fcStudentFeeDashDetails?.student || this.fcAllAcademicLoading) {
      return;
    }
    this.fcAllAcademicLoading = true;
    this.fcAllAcademicFee = [];
    this.feesService.studentAllAcademicFeeStatus(this.fcStudentFeeDashDetails.student.studentId).subscribe({
      next: async (data: any) => {
        this.fcAllAcademicLoading = false;
        this.fcAllAcademicFee = Array.isArray(data?.data) ? data.data : [];
        if (this.fcAllAcademicFee.length > 0) {
          this.fcAllAcademicOpen = true;
        } else {
          customToastr(await firstValueFrom(this.translate.get('studentDataNotAvailableInPreviousYears')), 'error');
        }
      },
      error: () => {
        this.fcAllAcademicLoading = false;
      },
    });
  }

  // ---- Delete (cancel) a collected fee (ports old openDeleteFeePopup/deleteFee) ----
  fcDeleteOpen = false;
  fcDeleteStep = 1;
  fcDeleteFee: FeeCollectionHistoryRow | null = null;
  fcDeleteReasons: any[] = [];
  fcDeleteReasonSel = '0';
  fcDeleteNewReason = '';
  fcDeletePassword = '';
  fcDeleteBusy = false;
  private fcDeleteReasonId: any = null;
  private fcDeleteReasonText: string | null = null;

  fcOpenDeleteFee(row: FeeCollectionHistoryRow): void {
    this.fcDeleteFee = row;
    if (!row.fkStudentId && row.studentId) {
      row.fkStudentId = row.studentId;
    }
    this.fcDeleteStep = 1;
    this.fcDeleteReasonSel = '0';
    this.fcDeleteNewReason = '';
    this.fcDeletePassword = '';
    this.fcDeleteReasonId = null;
    this.fcDeleteReasonText = null;
    this.fcDeleteReasons = [];
    this.fcDeleteOpen = true;
    this.feesService.getDeleteFeeCollectionReasons().subscribe({
      next: (data: any) => (this.fcDeleteReasons = Array.isArray(data) ? data : []),
      error: () => (this.fcDeleteReasons = []),
    });
  }

  async fcDeleteProceed(): Promise<void> {
    if (this.fcDeleteStep === 1) {
      if (this.fcDeleteReasonSel === '0') {
        customToastr(await firstValueFrom(this.translate.get('pleaseSelectReason')), 'error');
        return;
      }
      if (this.fcDeleteReasonSel === 'NEW') {
        if (!this.fcDeleteNewReason || !this.fcDeleteNewReason.trim()) {
          customToastr(await firstValueFrom(this.translate.get('pleaseProvideReason')), 'error');
          return;
        }
        this.fcDeleteReasonText = this.fcDeleteNewReason;
      } else {
        this.fcDeleteReasonId = this.fcDeleteReasonSel;
      }
      this.fcDeleteStep = 2;
      return;
    }
    if (!this.fcDeletePassword || !this.fcDeletePassword.trim()) {
      customToastr(await firstValueFrom(this.translate.get('pleaseEnterYourLoginPasswordToProceed')), 'error');
      return;
    }
    if (!this.fcDeleteFee) {
      return;
    }
    this.fcDeleteBusy = true;
    this.feesService
      .deleteCollectedFee(this.fcDeleteFee.pkFeesCollectionId, this.fcDeleteFee.fkStudentId, this.fcDeleteReasonId, this.fcDeleteReasonText, this.fcDeletePassword, this.currentAcademicId)
      .subscribe({
        next: async (data: any) => {
          this.fcDeleteBusy = false;
          if (data?.status) {
            this.fcDeleteOpen = false;
            customToastr(await firstValueFrom(this.translate.get('feeSuccessfullyCanceled')), 'success');
            this.fcLoadHistory();
            if (this.fcHistoryMode === 'updates') {
              this.fcLoadUpdateHistory();
            }
            if (this.fcSelectedStudent) {
              this.fcSelectStudent(this.fcSelectedStudent);
            }
          } else if (data?.errorcode === '10012') {
            customToastr(await firstValueFrom(this.translate.get('youEnteredAWrongPassword')), 'error');
          } else {
            customToastr(await firstValueFrom(this.translate.get('failedToCancelTheFeePleaseTryAgain')), 'error');
          }
        },
        error: async () => {
          this.fcDeleteBusy = false;
          customToastr(await firstValueFrom(this.translate.get('failedToCancelTheFeePleaseTryAgain')), 'error');
        },
      });
  }

  fcSelectStudent(student: FeeCollectionStudent): void {
    this.fcTotalSelectedAmount = 0;
    this.fcWizardStep = 'select';
    this.fcFeeList = [];
    this.fcOtherFeeList = [];
    student.isLoading = true;
    this.feesService.getStudentFeeDashInfo(student.studentId, student.classId, this.currentAcademicId).subscribe({
      next: (data: any) => {
        student.isLoading = false;
        this.fcStudentFeeDashDetails = JSON.parse(JSON.stringify(data || {}));
        if (this.fcStudentFeeDashDetails?.student) {
          this.fcStudentFeeDashDetails.student.mobileNumber = student.mobileNumber;
        }
        this.fcSelectedStudent = student;
        this.fcIsSearchActive = false;
        this.fcLoadStudentFeeList(student);
        // The student's fee profile replaces the search panel — start at its top.
        setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }));
      },
      error: async () => {
        student.isLoading = false;
        const msg = await firstValueFrom(this.translate.get('somethingWentWrongPleaseTryAgain'));
        customToastr(msg, 'error');
      },
    });
  }

  private fcLoadStudentFeeList(student: FeeCollectionStudent): void {
    this.feesService.getStudentFeeList_v2(student.studentId, student.classId, this.currentAcademicId).subscribe({
      next: (data: any) => {
        const termFees: FeeCollectionLineItem[] = (data?.termFees || []).map((t: any) => ({ ...t, feeType: 'TERM' }));
        const typeFees: FeeCollectionLineItem[] = (data?.typeFees || []).map((t: any) => ({ ...t, feeType: 'OTHER' }));
        this.fcOtherFeeList = (data?.otherFeeTerms || []).map((t: any) => ({ ...t, feeType: 'OTHER' }));
        this.fcFeeSettingEnabled = data?.feeSetting?.isFeesModuleEnable === 'ENABLED';

        const merged = [...termFees, ...typeFees];
        merged.sort((a: any, b: any) => new Date(a.DueDate || a.termDueDate || 0).getTime() - new Date(b.DueDate || b.termDueDate || 0).getTime());

        merged.forEach((item) => {
          item.feeFault = !!(
            item.Paid > 0 &&
            item.Balance > 0 &&
            item.expandedTerms?.length &&
            !item.expandedTerms.some((et) => et.Paid > 0)
          );
        });

        this.fcSeedSequenceGating(merged);
        this.fcSeedSequenceGating(this.fcOtherFeeList);

        this.fcFeeList = merged;
      },
      error: async () => {
        const msg = await firstValueFrom(this.translate.get('somethingWentWrongPleaseTryAgain'));
        customToastr(msg, 'error');
      },
    });
  }

  // Matches the old component's checkIsNextOnePaidOnChecked/
  // checkIsPreviousOnePaidOnChecked/checkDisableState exactly:
  // sequenceFeeCollection never limits HOW MANY rows can be selected —
  // multiple fees can always be checked and paid together in one
  // transaction. It only governs which row unlocks next: checking a row
  // enables the next not-yet-paid row for selection (recursing past
  // already-fully-paid rows), and leaves everything already selected
  // untouched.
  private fcSeedSequenceGating(list: FeeCollectionLineItem[]): void {
    if (this.fcSequenceFeeCollection) {
      let firstPendingFound = false;
      list.forEach((item) => {
        if (item.Balance > 0 && !firstPendingFound) {
          item.disabled = false;
          firstPendingFound = true;
        } else {
          item.disabled = item.Balance > 0;
        }
      });
    } else {
      list.forEach((item) => (item.disabled = false));
    }
  }

  fcToggleFeeSelection(item: FeeCollectionLineItem, list: FeeCollectionLineItem[]): void {
    if (item.disabled) {
      return;
    }
    item.selected = !item.selected;
    if (!item.selected) {
      item.isOldFee = false;
    }
    this.fcApplySequenceGating(list, list.indexOf(item), item.selected);
    this.fcRecalculateSelectedAmount();
  }

  private fcApplySequenceGating(list: FeeCollectionLineItem[], index: number, isSelected: boolean): void {
    const total = list.length;
    if (!this.fcSequenceFeeCollection) {
      list[index].disabled = false;
      if (index - 1 >= 0) {
        list[index - 1].disabled = false;
      }
      if (index + 1 < total) {
        list[index + 1].disabled = false;
      }
      return;
    }

    if (isSelected) {
      if (index - 1 >= 0) {
        if (list[index - 1].Balance <= 0) {
          this.fcCascadePrevious(list, index - 1, true);
        } else {
          list[index - 1].disabled = true;
        }
      }
      list[index].disabled = false;
      if (index + 1 < total) {
        if (list[index + 1].Balance <= 0) {
          this.fcCascadeNext(list, index + 1, false);
        } else {
          list[index + 1].disabled = false;
        }
      }
    } else {
      list[index].disabled = false;
      if (index - 1 >= 0) {
        if (list[index - 1].Balance <= 0) {
          this.fcCascadePrevious(list, index - 1, false);
        } else {
          list[index - 1].disabled = false;
        }
      }
      if (index + 1 < total) {
        if (list[index + 1].Balance <= 0) {
          this.fcCascadeNext(list, index + 1, true);
        } else {
          list[index + 1].disabled = true;
        }
      }
    }
  }

  private fcCascadeNext(list: FeeCollectionLineItem[], index: number, setStatus: boolean): void {
    if (index + 1 < list.length && list[index].Balance <= 0) {
      this.fcCascadeNext(list, index + 1, setStatus);
    } else {
      list[index].disabled = setStatus;
    }
  }

  private fcCascadePrevious(list: FeeCollectionLineItem[], index: number, setStatus: boolean): void {
    if (index - 1 >= 0 && list[index].Balance <= 0) {
      this.fcCascadePrevious(list, index - 1, setStatus);
    } else {
      list[index].disabled = setStatus;
    }
  }

  private fcRecalculateSelectedAmount(): void {
    const all = [...this.fcFeeList, ...this.fcOtherFeeList];
    this.fcTotalSelectedAmount = all.filter((i) => i.selected).reduce((sum, i) => sum + (i.Balance || 0), 0);
  }

  // Intake-normalized selection, built by fcMakePayment() — mirrors the
  // old component's own two-stage pipeline (makePayment() builds this
  // `data[]`, then proceedToPaymentMode() splits it into finalPaymentList).
  // Field names here (termId/termTypeId/typeId/...) are what the backend
  // actually expects — they do NOT exist under these names on the raw
  // fcFeeList/fcOtherFeeList items (those use fkFeesTermId/fkFeesTypeId/
  // busStopId/etc.), so this remapping step is required, not optional.
  fcSelectedPayment: any[] = [];

  async fcMakePayment(): Promise<void> {
    const selectedTerm = this.fcFeeList.filter((i) => i.selected);
    const selectedOther = this.fcOtherFeeList.filter((i) => i.selected);

    if (selectedTerm.some((i) => i.feeFault)) {
      const msg = await firstValueFrom(this.translate.get('thereIsADataIssueWithThisFeePleaseContactSupport'));
      customToastr(msg, 'error');
      return;
    }

    const data: any[] = [];
    selectedTerm.forEach((t: any) => {
      const lateFee = !t.waiverFees && t.lateFee > 0 ? t.lateFee : 0;
      const lateFeeId = !t.waiverFees && t.pkLateFeesId ? t.pkLateFeesId : null;
      if (t.isBusFee) {
        data.push({
          feeType: 'BUS',
          typeId: t.busStopId,
          Name: t.Name,
          Amount: t.feesTypeAmount,
          Balance: t.Balance,
          Discount: t.Discount,
          Paid: t.Paid,
          isBusFee: t.isBusFee,
          busId: t.busId,
          busTermId: t.busTermId,
          isOldFee: t.isOldFee,
          lateFee,
          lateFeeId,
        });
      } else if (t.TermName) {
        data.push({
          feeType: 'TERM',
          feeFault: t.feeFault,
          expandedTerms: t.expandedTerms,
          termId: t.fkFeesTermId,
          termTypeId: t.fkFeesTypeId,
          Name: t.TermName,
          Amount: t.feesTermAmount,
          Balance: t.Balance,
          Discount: t.discount,
          Paid: t.Paid,
          isOldFee: t.isOldFee,
          lateFee,
          lateFeeId,
        });
      } else {
        data.push({
          feeType: 'OTHER',
          typeId: t.fkFeesTypeId,
          Name: t.Name,
          Amount: t.feesTypeAmount,
          Balance: t.Balance,
          Discount: t.Discount,
          Paid: t.Paid,
          isBusFee: t.isBusFee,
          busId: t.busId,
          busTermId: t.busTermId,
          isOldFee: t.isOldFee,
          lateFee,
          lateFeeId,
        });
      }
    });

    selectedOther.forEach((f: any) => {
      data.push({
        feeType: 'OTHER-TERM',
        pkOtherFeesId: f.pkOtherFeesId,
        pkOtherFeeTermId: f.pkOtherFeeTermId,
        Name: f.Name,
        Amount: f.termAmount,
        Balance: f.Balance,
        Discount: f.Discount,
        Paid: f.Paid,
        isBusFee: f.isBusFee,
        busId: f.busId,
        busTermId: f.busTermId,
        isOldFee: f.isOldFee,
      });
    });

    if (data.length === 0) {
      const msg = await firstValueFrom(this.translate.get('pleaseSelectFeeSToProceed'));
      customToastr(msg, 'error');
      return;
    }

    const isOldFeeSelected = data.some((d) => d.isOldFee);
    if (isOldFeeSelected && data.some((d) => !d.isOldFee)) {
      const msg = await firstValueFrom(this.translate.get('pleaseSelectOldFeeOptionForAllTheFeesYouSelected'));
      customToastr(msg, 'error');
      return;
    }

    this.fcSelectedPayment = data;
    this.fcIsOldFee = isOldFeeSelected;
    this.fcOldFeeDate = '';
    this.fcOldFeeTime = '08:00';

    let payableAmount = 0;
    let payableLateFee = 0;
    data.forEach((item) => {
      payableAmount += item.Balance || 0;
      payableLateFee += item.lateFee || 0;
    });
    this.fcPayableAmount = payableAmount;
    this.fcPayableMax = payableAmount;
    this.fcPayableLateFee = payableLateFee;
    this.fcApplyLateFee = payableLateFee > 0;
    this.fcWizardStep = 'payment';
  }

  // The click's default toggle is cancelled so the checkbox only changes
  // when the data does (cancelling the waive-off modal must leave it ticked).
  fcCheckWaiveOffLateFee(event?: Event): void {
    event?.preventDefault();
    if (this.fcApplyLateFee) {
      this.fcWaiveOffConfirmOpen = true;
    } else if (this.fcPayableLateFee > 0) {
      this.fcApplyLateFee = true;
    }
  }

  closeWaiveOffConfirm(): void {
    this.fcWaiveOffConfirmOpen = false;
  }

  async fcConfirmWaiveOffLateFee(): Promise<void> {
    // Same payload as the old component's confirmWaiveOffLateFee(): the
    // studentId comes from the fee-dashboard's student (what the old read),
    // and only TERM / OTHER / BUS lines are sent.
    const studentId = this.fcStudentFeeDashDetails?.student?.studentId ?? this.fcSelectedStudent?.studentId;
    const dataToSend: any[] = [];
    this.fcSelectedPayment.forEach((t) => {
      if (!(t.lateFee > 0)) {
        return;
      }
      const base = {
        lateFeesId: t.lateFeeId,
        academicId: this.currentAcademicId,
        studentId,
        amount: t.lateFee,
      };
      if (t.feeType === 'TERM') {
        dataToSend.push({ ...base, feesTermId: t.termId, feesTypeId: null, fkBusId: null, fkBusStopId: null, fkBusTermId: null });
      } else if (t.feeType === 'OTHER') {
        dataToSend.push({ ...base, feesTermId: null, feesTypeId: t.typeId, fkBusId: null, fkBusStopId: null, fkBusTermId: null });
      } else if (t.feeType === 'BUS') {
        dataToSend.push({ ...base, feesTermId: null, feesTypeId: null, fkBusId: t.busId, fkBusStopId: t.typeId, fkBusTermId: t.busTermId });
      }
    });

    this.feesService.waiveOffLateFee({ data: dataToSend }).subscribe({
      next: async (data: any) => {
        if (data?.status) {
          this.fcApplyLateFee = false;
          this.fcPayableLateFee = 0;
          this.fcSelectedPayment.forEach((item) => {
            if (item.lateFee) {
              item.lateFee = 0;
            }
            if (item.lateFeeId) {
              item.lateFeeId = null;
            }
          });
          const msg = await firstValueFrom(this.translate.get('lateFeeWaivedOffSuccessfully'));
          customToastr(msg, 'success');
        } else {
          const msg = await firstValueFrom(this.translate.get('failedToWaiveOffLateFeePleaseTryAgain'));
          customToastr(msg, 'error');
        }
        this.fcWaiveOffConfirmOpen = false;
      },
      error: async () => {
        const msg = await firstValueFrom(this.translate.get('failedToWaiveOffLateFeePleaseTryAgain'));
        customToastr(msg, 'error');
        this.fcWaiveOffConfirmOpen = false;
      },
    });
  }

  fcFinalPaymentList: any[] = [];

  async fcProceedToPaymentMode(): Promise<void> {
    if (this.fcIsOldFee) {
      if (!this.fcOldFeeDate || !this.fcOldFeeTime) {
        const msg = await firstValueFrom(this.translate.get('pleaseSelectAValidDate'));
        customToastr(msg, 'error');
        return;
      }
      const oldFeeDateTime = new Date(`${this.fcOldFeeDate}T${this.fcOldFeeTime}:00`);
      if (oldFeeDateTime >= new Date()) {
        const msg = await firstValueFrom(this.translate.get('pleaseSelectPastDate'));
        customToastr(msg, 'error');
        return;
      }
    }

    const payAmount = Number(this.fcPayableAmount);
    if (!Number.isFinite(payAmount)) {
      const msg = await firstValueFrom(this.translate.get('invalidPayableAmount'));
      customToastr(msg, 'error');
      return;
    }
    if (!(payAmount > 0) || payAmount > this.fcPayableMax + 0.001) {
      const template = await firstValueFrom(this.translate.get('payAmountBetween'));
      customToastr(`${template} ${this.currencySymbol}1 - ${this.currencySymbol}${this.fcPayableMax}`, 'error');
      return;
    }

    let remaining = payAmount;
    const finalList: any[] = [];
    const oldFeeDateStr = this.fcIsOldFee ? `${this.fcOldFeeDate} ${this.fcOldFeeTime}:00` : null;

    for (const t of this.fcSelectedPayment) {
      if (remaining <= 0) {
        break;
      }
      // Late fee is only ever attributed to the FIRST chunk consumed for
      // a given selected item (zeroed after) — matches the old
      // component's own proceedToPaymentMode() exactly, not prorated.
      const lateFee = this.fcApplyLateFee ? t.lateFee || 0 : 0;

      if (t.Balance >= remaining) {
        if (t.feeType === 'TERM' && t.expandedTerms?.length) {
          let lateFeeAmountTerm = lateFee;
          let payAmountLeft = remaining;
          for (const et of t.expandedTerms) {
            if (payAmountLeft <= 0) {
              break;
            }
            let balance = et.feesTermAmount - ((et.Paid || 0) + (et.discount || 0));
            if (balance >= payAmountLeft) {
              balance = payAmountLeft;
            }
            payAmountLeft -= balance;
            if (balance > 0) {
              finalList.push({
                feeType: t.feeType,
                feeFault: t.feeFault,
                termId: t.termId,
                TypeIdInTerm: et.fkFeesTypeId,
                termTypeId: t.termTypeId,
                Balance: balance,
                Discount: 0,
                isOldFee: t.isOldFee,
                oldFeeDate: oldFeeDateStr,
                pkLateFeesId: lateFeeAmountTerm === 0 ? null : t.lateFeeId,
                lateFee: lateFeeAmountTerm,
              });
              lateFeeAmountTerm = 0;
            }
          }
        } else {
          finalList.push({
            feeType: t.feeType,
            feeFault: t.feeFault,
            termId: t.termId,
            termTypeId: t.termTypeId,
            typeId: t.typeId,
            pkOtherFeesId: t.pkOtherFeesId,
            pkOtherFeeTermId: t.pkOtherFeeTermId,
            Balance: remaining,
            Discount: 0,
            isBusFee: t.isBusFee,
            busId: t.busId,
            busTermId: t.busTermId,
            isOldFee: t.isOldFee,
            oldFeeDate: oldFeeDateStr,
            pkLateFeesId: t.lateFeeId,
            lateFee,
          });
        }
        remaining = 0;
      } else {
        if (t.feeType === 'TERM' && t.expandedTerms?.length) {
          let lateFeeAmountTerm = lateFee;
          for (const et of t.expandedTerms) {
            const balance = et.feesTermAmount - ((et.Paid || 0) + (et.discount || 0));
            if (balance > 0) {
              finalList.push({
                feeType: t.feeType,
                feeFault: t.feeFault,
                termId: t.termId,
                TypeIdInTerm: et.fkFeesTypeId,
                termTypeId: t.termTypeId,
                Balance: balance,
                Discount: 0,
                isOldFee: t.isOldFee,
                oldFeeDate: oldFeeDateStr,
                pkLateFeesId: lateFeeAmountTerm === 0 ? null : t.lateFeeId,
                lateFee: lateFeeAmountTerm,
              });
              lateFeeAmountTerm = 0;
            }
          }
        } else {
          finalList.push({
            feeType: t.feeType,
            feeFault: t.feeFault,
            termId: t.termId,
            termTypeId: t.termTypeId,
            typeId: t.typeId,
            pkOtherFeesId: t.pkOtherFeesId,
            pkOtherFeeTermId: t.pkOtherFeeTermId,
            Balance: t.Balance,
            Discount: 0,
            isBusFee: t.isBusFee,
            busId: t.busId,
            busTermId: t.busTermId,
            isOldFee: t.isOldFee,
            oldFeeDate: oldFeeDateStr,
            pkLateFeesId: t.lateFeeId,
            lateFee,
          });
        }
        remaining -= t.Balance;
      }
    }

    this.fcFinalPaymentList = finalList;
    this.fcPaymentMode = 'Cash';
    this.fcWizardStep = 'mode';
  }

  fcChangePaymentMode(mode: typeof this.fcPaymentMode): void {
    this.fcPaymentMode = mode;
    this.fcCardReferenceNumber = '';
    this.fcQrReferenceNumber = '';
    this.fcQrDate = '';
    this.fcCashAmount = null;
    this.fcCardAmount = null;
    this.fcCardCashReferenceNumber = '';
    this.fcBankName = '';
    this.fcBankTransferDate = '';
    this.fcBankTransferNumber = '';
    this.fcDdDate = '';
    this.fcDdNumber = '';
    this.fcDdMicrCode = '';
    this.fcDdAmount = null;
    this.fcChequeDate = '';
    this.fcChequeNumber = '';
    this.fcChequeAmount = null;
  }

  async fcProceedToPayment(): Promise<void> {
    const reqObj: any = {
      studentDetail: this.fcStudentFeeDashDetails?.student,
      discount: 0,
      fkClassId: this.fcStudentFeeDashDetails?.student?.classId,
      feesArray: [],
      fkStudentId: this.fcStudentFeeDashDetails?.student?.studentId,
      ModeOfPayment: this.fcPaymentMode,
      BankName: null,
      ModeOfPayDate: null,
      DDNumber: null,
      MICRCode: null,
      ChequeNumber: null,
      ReferenceNumber: null,
      fkFeeStructuresId: this.fcStudentFeeDashDetails?.TermFees?.feeStrutureId || null,
      chequeAmount: null,
      ddAmount: null,
    };

    const totalPayable = Number(this.fcPayableAmount) || 0;
    const lateFee = this.fcApplyLateFee ? this.fcPayableLateFee : 0;

    if (this.fcPaymentMode === 'Card') {
      if (!this.fcCardReferenceNumber) {
        const msg = await firstValueFrom(this.translate.get('pleaseProvideInvoiceReferenceNo'));
        customToastr(msg, 'error');
        return;
      }
      reqObj.ReferenceNumber = this.fcCardReferenceNumber;
    } else if (this.fcPaymentMode === 'QRCode') {
      if (!this.fcQrReferenceNumber) {
        const msg = await firstValueFrom(this.translate.get('pleaseProvideInvoiceReferenceNo'));
        customToastr(msg, 'error');
        return;
      }
      if (!this.fcQrDate) {
        const msg = await firstValueFrom(this.translate.get('pleaseProvideAValidDate'));
        customToastr(msg, 'error');
        return;
      }
      const qrDate = new Date(this.fcQrDate);
      if (qrDate > new Date()) {
        const msg = await firstValueFrom(this.translate.get('theDateCannotBeInTheFuture'));
        customToastr(msg, 'error');
        return;
      }
      reqObj.ReferenceNumber = this.fcQrReferenceNumber;
      reqObj.ModeOfPayDate = this.fcQrDate;
    } else if (this.fcPaymentMode === 'Card&Cash') {
      const cash = Number(this.fcCashAmount);
      const card = Number(this.fcCardAmount);
      if (!this.fcCardCashReferenceNumber || !(cash >= 0) || !(card >= 0)) {
        const msg = await firstValueFrom(this.translate.get('pleaseFillTheBankTransferDetails'));
        customToastr(msg, 'error');
        return;
      }
      if (lateFee > cash) {
        const msg = await firstValueFrom(this.translate.get('pleaseIncludeTheLateFeeInTheCashAmountOnly'));
        customToastr(msg, 'error');
        return;
      }
      if (Math.abs(card + cash - (totalPayable + lateFee)) > 0.001) {
        const msg = await firstValueFrom(this.translate.get('pleaseEnterSumOfExactPayableAmount'));
        customToastr(msg, 'error');
        return;
      }
      reqObj.ReferenceNumber = this.fcCardCashReferenceNumber;
      let cardRemaining = card;
      this.fcFinalPaymentList.forEach((item) => {
        const take = Math.min(item.Balance, cardRemaining);
        item.cardAmount = take;
        item.cashAmount = item.Balance - take;
        cardRemaining -= take;
      });
    } else if (this.fcPaymentMode === 'Bank-transfer') {
      if (!this.fcBankName || !this.fcBankTransferDate || !this.fcBankTransferNumber) {
        const msg = await firstValueFrom(this.translate.get('pleaseFillTheBankTransferDetails'));
        customToastr(msg, 'error');
        return;
      }
      reqObj.BankName = this.fcBankName;
      reqObj.ModeOfPayDate = this.fcBankTransferDate;
      reqObj.ReferenceNumber = this.fcBankTransferNumber;
    } else if (this.fcPaymentMode === 'DD') {
      if (!this.fcBankName || !this.fcDdDate || !this.fcDdNumber || !this.fcDdMicrCode) {
        const msg = await firstValueFrom(this.translate.get('pleaseFillTheDdDetails'));
        customToastr(msg, 'error');
        return;
      }
      if (!(Number(this.fcDdAmount) > 0)) {
        const msg = await firstValueFrom(this.translate.get('pleaseEnterAValidDdAmount'));
        customToastr(msg, 'error');
        return;
      }
      reqObj.BankName = this.fcBankName;
      reqObj.ModeOfPayDate = this.fcDdDate;
      reqObj.DDNumber = this.fcDdNumber;
      reqObj.MICRCode = this.fcDdMicrCode;
      reqObj.ddAmount = this.fcDdAmount;
    } else if (this.fcPaymentMode === 'Cheque') {
      if (!this.fcBankName || !this.fcChequeDate || !this.fcChequeNumber) {
        const msg = await firstValueFrom(this.translate.get('pleaseFillTheChequeDetails'));
        customToastr(msg, 'error');
        return;
      }
      if (!(Number(this.fcChequeAmount) > 0)) {
        const msg = await firstValueFrom(this.translate.get('pleaseEnterAValidChequeAmount'));
        customToastr(msg, 'error');
        return;
      }
      reqObj.BankName = this.fcBankName;
      reqObj.ModeOfPayDate = this.fcChequeDate;
      reqObj.ChequeNumber = this.fcChequeNumber;
      reqObj.chequeAmount = this.fcChequeAmount;
    }

    reqObj.feesArray = JSON.parse(JSON.stringify(this.fcFinalPaymentList));
    this.fcPreviewReqObj = reqObj;
    this.fcPreviewOpen = true;
  }

  closePreview(): void {
    this.fcPreviewOpen = false;
  }

  async fcYesProceedPay(): Promise<void> {
    const now = Date.now();
    if (now - this.fcPayTimestamp < 5000) {
      return;
    }
    this.fcPayTimestamp = now;
    this.fcPaySaving = true;
    const reqId = `REQID_${this.getSchoolId}_${now}`;
    const payload = { ...this.fcPreviewReqObj, reqId };

    this.feesService.sendPaymentInfo(payload, this.currentAcademicId).subscribe({
      next: async (data: any) => {
        this.fcPaySaving = false;
        this.fcPreviewOpen = false;
        if (data?.status) {
          this.fcGetFeeReceipt(data.status);
          this.loadStats();
        } else {
          // Surface the backend's reason (e.g. invalid transport options)
          // like the old component's error panel, instead of a generic toast.
          const generic = await firstValueFrom(this.translate.get('somethingWentWrongPleaseTryAgain'));
          const detail = (!data?.isValid && data?.reason) || data?.message;
          customToastr(detail ? `${generic}<br><b>${detail}</b>` : generic, 'error');
        }
      },
      error: async () => {
        this.fcPaySaving = false;
        const msg = await firstValueFrom(this.translate.get('somethingWentWrongPleaseTryAgain'));
        customToastr(msg, 'error');
      },
    });
  }

  private fcGetFeeReceipt(receiptId: number): void {
    this.feesService.getFeeReceipt(receiptId, this.currentAcademicId).subscribe({
      next: (data: any) => {
        this.fcReceipt = data?.receiptInfo || [];
        this.fcReceiptStudent = this.fcReceipt[0] || null;
        this.fcReceiptSetting = data?.feeSetting || { isFeesModuleEnable: '' };
        this.fcReceiptOpenedFrom = 'AFTER_PAYMENT';
        this.fcReceiptOpen = true;
      },
    });
  }

  fcShowReceiptFromHistory(row: FeeCollectionHistoryRow): void {
    row.isLoading = true;
    this.feesService.getFeeReceipt(row.ReceiptNumber, this.currentAcademicId).subscribe({
      next: (data: any) => {
        row.isLoading = false;
        this.fcReceipt = data?.receiptInfo || [];
        this.fcReceiptStudent = this.fcReceipt[0] || null;
        this.fcReceiptSetting = data?.feeSetting || { isFeesModuleEnable: '' };
        this.fcReceiptOpenedFrom = 'FEE_HISTORY';
        this.fcReceiptOpen = true;
        this.fcHistoryTab = false;
      },
      error: () => {
        row.isLoading = false;
      },
    });
  }

  fcCloseReceipt(): void {
    this.fcReceiptOpen = false;
    if (this.fcReceiptOpenedFrom === 'AFTER_PAYMENT') {
      this.fcResetSearch();
    } else {
      this.fcHistoryTab = true;
    }
  }

  fcPrintReceipt(): void {
    const container = document.getElementById('fv2-receipt-print-container');
    if (!container) {
      return;
    }
    const printWindow = window.open('', 'Receipt', 'width=900,height=1100,toolbars=no,scrollbars=yes,status=no');
    if (!printWindow) {
      return;
    }
    // The popup has none of the host page's stylesheets loaded, so the
    // receipt's layout (grid meta columns, table borders, fonts) must be
    // reproduced inline here rather than relying on fv2-* component CSS.
    const styles = `
      <style>
        * { box-sizing: border-box; }
        body { font-family: 'Plus Jakarta Sans', Arial, sans-serif; color: #232323; padding: 24px; }
        .fv2-receipt-head { text-align: center; margin-bottom: 18px; }
        .fv2-receipt-head h2 { margin: 0 0 4px; font-size: 20px; font-weight: 800; text-transform: uppercase; }
        .fv2-receipt-head p { margin: 0 0 10px; font-size: 12px; font-weight: 600; color: #555; text-transform: uppercase; }
        .fv2-receipt-title { display: inline-block; font-weight: 700; letter-spacing: .04em; text-decoration: underline; text-transform: uppercase; font-size: 13px; }
        .fv2-receipt-meta { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 18px; padding-bottom: 16px; border-bottom: 1px dashed #ccc; }
        .fv2-receipt-meta-col div { display: flex; justify-content: space-between; gap: 12px; padding: 3px 0; font-size: 13px; }
        .fv2-receipt-meta-col span { color: #555; }
        .fv2-receipt-meta-col b { font-weight: 700; text-align: right; }
        table { width: 100%; border-collapse: collapse; margin-top: 10px; }
        th, td { padding: 8px 10px; font-size: 13px; text-align: right; }
        th:first-child, td:first-child { text-align: left; }
        thead th { border-top: 1px solid #232323; border-bottom: 1px solid #232323; font-weight: 700; }
        tfoot td { white-space: nowrap; }
        .fv2-receipt-total-row td { border-top: 1px solid #232323; border-bottom: 1px solid #232323; font-weight: 700; }
        .fv2-receipt-footer { display: flex; justify-content: space-between; align-items: flex-end; margin-top: 40px; }
        .fv2-receipt-signatory { font-size: 13px; font-weight: 700; padding-top: 24px; border-top: 1px solid #232323; }
      </style>
    `;
    printWindow.document.write(`<html><head><title>Receipt</title>${styles}</head><body>${container.innerHTML}</body></html>`);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  }

  fcResetSearch(): void {
    this.fcSearchText = '';
    this.fcStudentList = [];
    this.fcSelectedStudent = null;
    this.fcStudentFeeDashDetails = null;
    this.fcFeeList = [];
    this.fcOtherFeeList = [];
    this.fcWizardStep = 'select';
    this.fcTotalSelectedAmount = 0;
    this.fcIsSearchActive = true;
    this.fcHistoryTab = false;
  }

  fcBackToSelect(): void {
    this.fcWizardStep = 'select';
  }

  fcBackToPaymentDetails(): void {
    this.fcWizardStep = 'payment';
  }

  fcOpenHistory(): void {
    this.fcHistoryMode = 'history';
    this.fcHistoryTab = true;
    this.fcHistoryPage = 1;
    this.fcLoadHistory();
  }

  fcCloseHistory(): void {
    this.fcHistoryTab = false;
    this.fcHistoryMode = 'history';
  }

  // "Payment Update History" (the old wizard's 4th tab): cancelled / changed payments for the student.
  fcHistoryMode: 'history' | 'updates' = 'history';
  fcUpdateHistoryList: any[] = [];
  fcUpdateHistoryPage = 1;
  fcUpdateHistoryIsLastPage = true;
  fcUpdateHistoryLoading = false;

  fcSetHistoryMode(mode: 'history' | 'updates'): void {
    this.fcHistoryMode = mode;
    if (mode === 'updates') {
      this.fcUpdateHistoryPage = 1;
      this.fcLoadUpdateHistory();
    }
  }

  private fcLoadUpdateHistory(): void {
    if (!this.fcSelectedStudent) {
      return;
    }
    this.fcUpdateHistoryLoading = true;
    this.feesService
      .getStudentUpdateFeeHistory(this.fcSelectedStudent.studentId, this.fcSelectedStudent.classId, this.fcUpdateHistoryPage, this.currentAcademicId)
      .subscribe({
        next: (data: any) => {
          this.fcUpdateHistoryLoading = false;
          this.fcUpdateHistoryList = !data?.errorcode && Array.isArray(data) ? data : [];
          this.fcUpdateHistoryIsLastPage = this.fcUpdateHistoryList.length < 10;
        },
        error: () => {
          this.fcUpdateHistoryLoading = false;
          this.fcUpdateHistoryList = [];
        },
      });
  }

  fcUpdateHistoryGo(delta: number): void {
    const next = this.fcUpdateHistoryPage + delta;
    if (next < 1 || (delta > 0 && this.fcUpdateHistoryIsLastPage)) {
      return;
    }
    this.fcUpdateHistoryPage = next;
    this.fcLoadUpdateHistory();
  }

  private fcLoadHistory(): void {
    if (!this.fcSelectedStudent) {
      return;
    }
    this.fcHistoryLoading = true;
    this.feesService
      .getFeeHistory(this.fcSelectedStudent.studentId, this.fcSelectedStudent.classId, this.fcHistoryPage, this.currentAcademicId)
      .subscribe({
        next: (data: any) => {
          this.fcHistoryLoading = false;
          this.fcHistoryList = Array.isArray(data) ? data : [];
          this.fcHistoryIsLastPage = this.fcHistoryList.length < 10;
        },
        error: () => {
          this.fcHistoryLoading = false;
          this.fcHistoryList = [];
        },
      });
  }

  fcNextHistoryPage(): void {
    if (this.fcHistoryIsLastPage) {
      return;
    }
    this.fcHistoryPage++;
    this.fcLoadHistory();
  }

  fcPreviousHistoryPage(): void {
    if (this.fcHistoryPage <= 1) {
      return;
    }
    this.fcHistoryPage--;
    this.fcLoadHistory();
  }

  // ============================================================
  // Settings tab — ports FeesManagementComponent's
  // getFeesSettings()/changeSetting()/getClassFeesSettings()/
  // updateClassFeeSettings()/fetchPaymentGateways()/openPopup()/
  // sendRequest()/onCheckboxChange()/getSupportedCurrency()/
  // handleSupportedCurrencyChange() against the SAME feeService
  // endpoints. Scope: the 4 sections that actually render in
  // production today — module toggles, per-class online payment,
  // payment gateway list, and currency selector. The legacy
  // Instamojo merchant-account/online-payment-purpose/payment-limits
  // sub-tree is NOT ported — confirmed dead code in the old component,
  // permanently gated behind a hardcoded `showOldMerchantAccountPayment
  // = false` that is never flipped anywhere, so it never renders for
  // any school today.
  // ============================================================
  settingsLoaded = false;
  settingsLoading = false;

  feesSettingsInfo: { SettingName: string; isFeesModuleEnable: 'ENABLED' | 'DISABLED' }[] = [];
  isMobileFeesModuleEnabled = false;
  isOnlinePaymentIndependentEnabled = false;
  isMobileFeesPaymentEnabled = false;
  isShowTermFeeBreakupEnabled = false;
  isGatewayEnable = false;
  settingSaving: Record<string, boolean> = {};

  classFeesSettingsOpen = false;
  classFeesSettings: { classId: number; classNumber: string; classDivision: string; IsEnabled: string; pkClassOnlinePaymentSettingId: number; selected?: boolean }[] = [];
  classFeesSettingsLoading = false;
  classFeesSettingsSaving = false;

  paymentGateways: any[] = [];
  paymentGatewaysLoading = false;
  gatewayRequestModalOpen = false;
  selectedGateway: any = null;
  gatewayRequestSaving = false;

  supportedCurrencyList: { pkSupportedCurrencyId: number; currencyType: string; isCurrentCurrency: number; loader?: boolean }[] = [];
  supportedCurrencyLoading = false;

  openFeesSettingsTab(): void {
    this.settingsLoaded = true;
    this.getFeesSettings();
    this.fetchPaymentGateways();
    this.getSupportedCurrency();
  }

  private getFeesSettings(): void {
    this.settingsLoading = true;
    this.feesService.getFeeModuleSettings(this.currentAcademicId).subscribe({
      next: (data: any) => {
        this.settingsLoading = false;
        this.feesSettingsInfo = data?.feesSettings || [];
        this.isMobileFeesModuleEnabled = false;
        this.isOnlinePaymentIndependentEnabled = false;
        this.isMobileFeesPaymentEnabled = false;
        this.isShowTermFeeBreakupEnabled = false;
        this.isGatewayEnable = false;
        this.feesSettingsInfo.forEach((item) => {
          const enabled = item.isFeesModuleEnable === 'ENABLED';
          if (item.SettingName === 'MOBILE_FEES_MODULE') {
            this.isMobileFeesModuleEnabled = enabled;
            this.isGatewayEnable = enabled;
          } else if (item.SettingName === 'ONLINE_PAYMENT_INDEPENDENT_TO_FEES_STRUCTURE') {
            this.isOnlinePaymentIndependentEnabled = enabled;
          } else if (item.SettingName === 'MOBILE_FEES_PAYMENT') {
            this.isMobileFeesPaymentEnabled = enabled;
          } else if (item.SettingName === 'SHOW_TERM_FEE_BREAKUP') {
            this.isShowTermFeeBreakupEnabled = enabled;
          }
        });
      },
      error: () => {
        this.settingsLoading = false;
      },
    });
  }

  async changeSetting(settingName: string, value: 'ENABLED' | 'DISABLED'): Promise<void> {
    this.settingSaving[settingName] = true;
    this.feesService.updateFeeModuleSettings(value, settingName, this.currentAcademicId).subscribe({
      next: async () => {
        this.settingSaving[settingName] = false;
        const msg = await firstValueFrom(this.translate.get('settingUpdated'));
        customToastr(msg, 'success');
        this.getFeesSettings();
      },
      error: async () => {
        this.settingSaving[settingName] = false;
        const msg = await firstValueFrom(this.translate.get('updateSettingFailedPleaseTryAgain'));
        customToastr(msg, 'error');
      },
    });
  }

  openClassFeesSettings(): void {
    this.classFeesSettingsOpen = true;
    this.classFeesSettingsLoading = true;
    this.feesService.getClassListWithOnlinePaymentSettings(this.currentAcademicId).subscribe({
      next: (data: any) => {
        this.classFeesSettingsLoading = false;
        this.classFeesSettings = (Array.isArray(data) ? data : []).map((it: any) => ({
          ...it,
          selected: it.IsEnabled === 'ENABLED',
        }));
      },
      error: () => {
        this.classFeesSettingsLoading = false;
        this.classFeesSettings = [];
      },
    });
  }

  closeClassFeesSettings(): void {
    this.classFeesSettingsOpen = false;
  }

  async updateClassFeeSettings(): Promise<void> {
    if (this.classFeesSettings.length === 0) {
      const msg = await firstValueFrom(this.translate.get('looksLikeThereAreNoClassesAvailable'));
      customToastr(msg, 'error');
      return;
    }
    const reqData = this.classFeesSettings.map((it) =>
      it.selected
        ? { isEnabled: 'ENABLED', classId: it.classId, settingId: it.pkClassOnlinePaymentSettingId, academicId: this.currentAcademicId }
        : { isEnabled: 'DISABLED', classId: it.classId, settingId: it.pkClassOnlinePaymentSettingId }
    );
    this.classFeesSettingsSaving = true;
    this.feesService.addOrRemoveClassOnlinePaySettings(reqData).subscribe({
      next: async (data: any) => {
        this.classFeesSettingsSaving = false;
        if (data?.status) {
          const msg = await firstValueFrom(this.translate.get('settingUpdated'));
          customToastr(msg, 'success');
          this.classFeesSettingsOpen = false;
        } else {
          const msg = await firstValueFrom(this.translate.get('updateSettingFailedPleaseTryAgain'));
          customToastr(msg, 'error');
        }
      },
      error: async () => {
        this.classFeesSettingsSaving = false;
        const msg = await firstValueFrom(this.translate.get('updateSettingFailedPleaseTryAgain'));
        customToastr(msg, 'error');
      },
    });
  }

  private fetchPaymentGateways(): void {
    this.paymentGatewaysLoading = true;
    this.feesService.gatewayList().subscribe({
      next: (data: any) => {
        this.paymentGatewaysLoading = false;
        if (data?.status) {
          this.paymentGateways = data?.body || [];
        } else {
          this.paymentGateways = [];
        }
      },
      error: () => {
        this.paymentGatewaysLoading = false;
        this.paymentGateways = [];
      },
    });
  }

  openGatewayRequestModal(gateway: any): void {
    this.selectedGateway = gateway;
    this.gatewayRequestModalOpen = true;
  }

  closeGatewayRequestModal(): void {
    this.gatewayRequestModalOpen = false;
  }

  async sendGatewayRequest(): Promise<void> {
    if (!this.selectedGateway) {
      return;
    }
    this.gatewayRequestSaving = true;
    const body = { fkPaymentGatewayId: this.selectedGateway.pkPaymentGatewayId, lang: 'en' };
    this.feesService.gatewaySendRequest(body).subscribe({
      next: (data: any) => {
        this.gatewayRequestSaving = false;
        this.gatewayRequestModalOpen = false;
        if (data?.status && data?.body?.message) {
          customToastr(data.body.message, 'success');
        } else if (data?.message) {
          customToastr(data.message, 'error');
        }
      },
      error: async () => {
        this.gatewayRequestSaving = false;
        this.gatewayRequestModalOpen = false;
        const msg = await firstValueFrom(this.translate.get('somethingWentWrongPleaseTryAgain'));
        customToastr(msg, 'error');
      },
    });
  }

  onGatewayCheckboxChange(gateway: any): void {
    gateway.loader = true;
    const nextActive = gateway.isActive === 1 ? 0 : 1;
    const body = { fkPaymentGatewayId: gateway.pkPaymentGatewayId, isActive: nextActive, lang: 'en' };
    this.feesService.updatePaymentGatewayStatus(body).subscribe({
      next: (data: any) => {
        gateway.loader = false;
        if (data?.status) {
          gateway.isActive = nextActive;
        }
      },
      error: () => {
        gateway.loader = false;
      },
    });
  }

  private getSupportedCurrency(): void {
    this.supportedCurrencyLoading = true;
    const body = { academicId: this.currentAcademicId, lang: 'en' };
    this.feesService.getSupportedCurrencyListAPI(body).subscribe({
      next: (data: any) => {
        this.supportedCurrencyLoading = false;
        this.supportedCurrencyList = data?.status ? data?.body || [] : [];
      },
      error: () => {
        this.supportedCurrencyLoading = false;
        this.supportedCurrencyList = [];
      },
    });
  }

  handleSupportedCurrencyChange(selectedCurrency: any): void {
    selectedCurrency.loader = true;
    this.supportedCurrencyList.forEach((currency) => {
      currency.isCurrentCurrency = currency === selectedCurrency ? 1 : 0;
    });
    const body = {
      lang: 'en',
      pkSupportedCurrencyId: selectedCurrency.pkSupportedCurrencyId,
      academicId: this.currentAcademicId,
    };
    this.feesService.updateSupportedCurrencyAPI(body).subscribe({
      next: (data: any) => {
        selectedCurrency.loader = false;
        if (data?.status && data?.body?.message) {
          customToastr(data.body.message, 'success');
          localStorage.setItem('currency', selectedCurrency.currencyType);
          this.currencyCode = selectedCurrency.currencyType;
        } else if (data?.message) {
          customToastr(data.message, 'error');
        }
      },
      error: async () => {
        selectedCurrency.loader = false;
        const msg = await firstValueFrom(this.translate.get('somethingWentWrongPleaseTryAgain'));
        customToastr(msg, 'error');
      },
    });
  }

  // ============================================================
  // Fees Board tab — ports FeesManagementComponent's #feeboard tab-pane
  // 1:1 against the same feeService endpoints (see interfaces above for
  // scope notes). 7 sub-tabs: Standards (view/edit board), Fee Structure,
  // Student Categories, Fee Components, Terms, Late Fees, Other Fees.
  // ============================================================
  feesBoardLoaded = false;
  feesBoardSubTab: FeesBoardSubTab = 'standards';

  openFeesBoardTab(): void {
    this.feesBoardLoaded = true;
    this.setFeesBoardSubTab('standards');
  }

  setFeesBoardSubTab(tab: FeesBoardSubTab): void {
    if (
      (tab === 'lateFees' && !this.canSeeLateFeeSubTab) ||
      (tab === 'otherFees' && !this.canSeeOtherFeesSubTab)
    ) {
      return;
    }
    this.feesBoardSubTab = tab;
    if (tab === 'standards') {
      // Old component loads the board only after an explicit click (showFeeBoardDetailView).
      return;
    } else if (tab === 'structures' && !this.fbStructuresLoaded) {
      this.fbLoadStructures();
    } else if (tab === 'categories' && !this.fbCategoriesLoaded) {
      this.fbLoadCategories();
    } else if (tab === 'components' && !this.fbComponentsLoaded) {
      this.fbLoadComponents();
    } else if (tab === 'terms' && !this.fbTermsLoaded) {
      this.fbLoadTerms();
    } else if (tab === 'lateFees' && !this.fbLateFeesLoaded) {
      this.fbOpenLateFeeTab();
    } else if (tab === 'otherFees' && !this.fbOtherFeesLoaded) {
      this.fbLoadOtherFees();
    }
  }

  // ---------- Standards (view/edit board) ----------
  fbStandardsRequested = false;
  fbShowStandards(): void {
    this.fbStandardsRequested = true;
    if (this.fbStandards.length === 0) {
      this.fbLoadStandards();
    }
  }
  fbStandards: FbStandardRow[] = [];
  // One column per fee-structure name, taken once from the first loaded
  // page — ports the old component's feesBoardDataAllClassesStrList
  // (feesmanage.component.ts:2085-2088), which assumes every class row
  // lists the same structures in the same order.
  fbStandardsStrList: { Name: string }[] = [];
  fbStandardsLoading = false;
  fbStandardsPage = 1;
  fbStandardsMaxPage = 1;

  private fbLoadStandards(): void {
    this.fbStandardsLoading = true;
    this.feesService.getFeeBoardFees_v2(this.currentAcademicId, this.fbStandardsPage).subscribe({
      next: (data: any) => {
        this.fbStandardsLoading = false;
        if (data?.errorcode) {
          this.fbStandards = [];
          return;
        }
        this.fbStandards = data?.feesData || [];
        this.fbStandardsMaxPage = data?.maxPageNumber || 1;
        if (this.fbStandardsStrList.length === 0 && this.fbStandards[0]) {
          this.fbStandardsStrList = this.fbStandards[0].structures || [];
        }
      },
      error: () => {
        this.fbStandardsLoading = false;
      },
    });
  }

  fbStandardStructureFor(row: FbStandardRow, structureName: string) {
    return (row.structures || []).find((s) => s.Name === structureName);
  }

  // Step pagination (Previous / 1 2 3 / Next), one server page at a time.
  get fbStandardsPageList(): number[] {
    const max = this.fbStandardsMaxPage;
    const start = Math.max(1, Math.min(this.fbStandardsPage - 2, max - 4));
    const end = Math.min(max, start + 4);
    const out: number[] = [];
    for (let i = start; i <= end; i++) {
      out.push(i);
    }
    return out;
  }

  fbStandardsGo(page: number): void {
    if (page < 1 || page > this.fbStandardsMaxPage || page === this.fbStandardsPage || this.fbStandardsLoading) {
      return;
    }
    this.fbStandardsPage = page;
    this.fbLoadStandards();
  }


  // ---------- Fee Board amount-entry modal (per class × structure × fee-type) ----------
  fbBoardModalOpen = false;
  fbBoardClassId: number | null = null;
  fbBoardClassLabel = '';
  fbBoardData: FbFeeBoardStructure[] = [];
  fbBoardLoading = false;
  fbBoardSaving = false;
  fbBoardCopyClassList: { classId: number; ClassNumber: string; classDivision: string }[] = [];
  fbBoardCopyFromClassId: number | null = null;

  openFeeBoard(row: FbStandardRow): void {
    this.fbBoardClassId = row.classId;
    this.fbBoardClassLabel = `${row.classNumber} ${row.classDivision}`;
    this.fbBoardCopyFromClassId = null;
    this.fbBoardModalOpen = true;
    this.fbLoadFeeBoard(row.classId);
    this.fbLoadClassListForCopy(row.classId);
  }

  closeFeeBoardModal(): void {
    this.fbBoardModalOpen = false;
  }

  private fbLoadFeeBoard(classId: number): void {
    this.fbBoardLoading = true;
    this.feesService.getFeeBoardFees(classId, this.currentAcademicId).subscribe({
      next: async (data: any) => {
        this.fbBoardLoading = false;
        if (data?.errorcode || data?.status === false) {
          this.fbBoardData = [];
          if (data?.status === false) {
            const msg = await firstValueFrom(this.translate.get('feeStructureNotFoundPleaseCreateFeeStructureByClickingOnAddNewStructure'));
            customToastr(msg, 'error');
          }
          return;
        }
        this.fbBoardData = Array.isArray(data) ? data : [];
      },
      error: () => {
        this.fbBoardLoading = false;
        this.fbBoardData = [];
      },
    });
  }

  private fbLoadClassListForCopy(classId: number): void {
    this.feesService.getClassList(classId).subscribe({
      next: (response: any) => {
        this.fbBoardCopyClassList = response?.status ? response?.body?.classListDataForFeesStructure || [] : [];
      },
      error: () => {
        this.fbBoardCopyClassList = [];
      },
    });
  }

  // Copies fee-type amounts from another class's board onto the currently
  // open board (matched by pkFeeStructuresId/pkFeesTypeId) — mirrors
  // handleFeeConfigCopy()+configFeeBoard() exactly, just without the old
  // component's extra confirm-modal step.
  fbCopyBoardFromClass(): void {
    if (!this.fbBoardCopyFromClassId) {
      return;
    }
    this.fbBoardLoading = true;
    this.feesService.getFeeBoardFees(this.fbBoardCopyFromClassId, this.currentAcademicId).subscribe({
      next: (sourceData: any) => {
        this.fbBoardLoading = false;
        const sourceBoards: FbFeeBoardStructure[] = Array.isArray(sourceData) ? sourceData : [];
        this.fbBoardData.forEach((board) => {
          const sourceBoard = sourceBoards.find((b) => b.pkFeeStructuresId === board.pkFeeStructuresId);
          if (!sourceBoard) {
            return;
          }
          board.feeTypes.forEach((feeType) => {
            const match = sourceBoard.feeTypes.find((f) => f.pkFeesTypeId === feeType.pkFeesTypeId);
            if (match) {
              feeType.Amount = match.Amount;
            }
          });
        });
      },
      error: () => {
        this.fbBoardLoading = false;
      },
    });
  }

  async saveFeeBoard(): Promise<void> {
    if (!this.fbBoardClassId) {
      return;
    }
    this.fbBoardSaving = true;
    const criteria = { classId: this.fbBoardClassId, feeBoard: this.fbBoardData };
    this.feesService.insertAndUpdateFeeBoard(criteria, this.currentAcademicId).subscribe({
      next: async (data: any) => {
        this.fbBoardSaving = false;
        if (Array.isArray(data) && data.length === 0) {
          const msg = await firstValueFrom(this.translate.get('feesBoardDetailsAddedSuccessfully'));
          customToastr(msg, 'success');
          this.fbBoardModalOpen = false;
        } else {
          const names = (data || []).map((item: any) => item.TermName).join(', ');
          const msg = await firstValueFrom(this.translate.get('someStudentAlreadyPaidFor'));
          customToastr(`${msg} <b>${names}</b>`, 'error');
        }
      },
      error: async () => {
        this.fbBoardSaving = false;
        const msg = await firstValueFrom(this.translate.get('somethingWentWrongPleaseTryAgain'));
        customToastr(msg, 'error');
      },
    });
  }

  // ---------- Fee Board history (read-only audit) ----------
  fbBoardHistoryOpen = false;
  fbBoardHistoryLoading = false;
  fbBoardHistoryList: any[] = [];
  fbBoardHistoryStructureName = '';

  openFeeBoardHistory(structure: FbFeeBoardStructure): void {
    if (!this.fbBoardClassId) {
      return;
    }
    this.fbBoardHistoryStructureName = structure.Name;
    this.fbBoardHistoryOpen = true;
    this.fbBoardHistoryLoading = true;
    this.feesService.fetchFeeBoardHistorydetailView(structure.pkFeeStructuresId, this.fbBoardClassId, this.currentAcademicId).subscribe({
      next: (data: any) => {
        this.fbBoardHistoryLoading = false;
        // API wraps the rows in an outer array (old component reads data[0]).
        this.fbBoardHistoryList = Array.isArray(data) ? (Array.isArray(data[0]) ? data[0] : data) : [];
      },
      error: () => {
        this.fbBoardHistoryLoading = false;
        this.fbBoardHistoryList = [];
      },
    });
  }

  closeFeeBoardHistory(): void {
    this.fbBoardHistoryOpen = false;
  }

  // ---------- Term Fee Configuration (term × fee-type amount matrix) ----------
  fbTermConfigModalOpen = false;
  fbTermConfigStructureId: number | null = null;
  fbTermConfigStructureName = '';
  fbTermConfigLoading = false;
  fbTermConfigSaving = false;
  fbTermConfigurations: FbTermFeeConfiguration[] = [];
  fbTermConfigTermList: { pkFeesTermId: number; TermName: string }[] = [];
  fbTermConfigCopyFromClassId: number | null = null;

  openTermFeeConfig(structure: FbFeeBoardStructure): void {
    if (!this.fbBoardClassId) {
      return;
    }
    this.fbTermConfigStructureId = structure.pkFeeStructuresId;
    this.fbTermConfigStructureName = structure.Name;
    this.fbTermConfigCopyFromClassId = null;
    this.fbTermConfigModalOpen = true;
    this.fbLoadTermFeeConfig(this.fbBoardClassId, structure.pkFeeStructuresId);
  }

  closeTermFeeConfigModal(): void {
    this.fbTermConfigModalOpen = false;
  }

  private fbLoadTermFeeConfig(classId: number, structureId: number): void {
    this.fbTermConfigLoading = true;
    this.feesService.getTermFeeConfig(classId, structureId, this.currentAcademicId).subscribe({
      next: (data: any) => {
        this.fbTermConfigLoading = false;
        if (data?.errorcode || !Array.isArray(data) || data.length === 0) {
          this.fbTermConfigurations = [];
          this.fbTermConfigTermList = [];
          return;
        }
        const termList: { pkFeesTermId: number; TermName: string }[] = [];
        data[0].termFees.forEach((t: any) => {
          if (!termList.some((x) => x.pkFeesTermId === t.pkFeesTermId)) {
            termList.push({ pkFeesTermId: t.pkFeesTermId, TermName: t.TermName });
          }
        });
        this.fbTermConfigTermList = termList;
        this.fbTermConfigurations = JSON.parse(JSON.stringify(data));
      },
      error: () => {
        this.fbTermConfigLoading = false;
        this.fbTermConfigurations = [];
      },
    });
  }

  fbCopyTermConfigFromClass(): void {
    if (!this.fbTermConfigCopyFromClassId || !this.fbTermConfigStructureId) {
      return;
    }
    this.fbTermConfigLoading = true;
    this.feesService.getTermFeeConfig(this.fbTermConfigCopyFromClassId, this.fbTermConfigStructureId, this.currentAcademicId).subscribe({
      next: (sourceData: any) => {
        this.fbTermConfigLoading = false;
        const source: FbTermFeeConfiguration[] = Array.isArray(sourceData) ? sourceData : [];
        this.fbTermConfigurations.forEach((config) => {
          const match = source.find((s) => s.pkFeesTypeId === config.pkFeesTypeId);
          if (!match) {
            return;
          }
          config.termFees.forEach((term) => {
            const matchTerm = match.termFees.find((t) => t.pkFeesTermId === term.pkFeesTermId);
            if (matchTerm) {
              term.PaymentAmount = matchTerm.PaymentAmount;
            }
          });
        });
      },
      error: () => {
        this.fbTermConfigLoading = false;
      },
    });
  }

  fbFindTermFee(config: FbTermFeeConfiguration, pkFeesTermId: number): FbTermFeeTerm | undefined {
    return config.termFees.find((t) => t.pkFeesTermId === pkFeesTermId);
  }

  fbSetTermFeeAmount(config: FbTermFeeConfiguration, pkFeesTermId: number, value: number): void {
    const term = this.fbFindTermFee(config, pkFeesTermId);
    if (term) {
      term.PaymentAmount = value;
    }
  }

  async saveTermFeeConfig(): Promise<void> {
    for (const config of this.fbTermConfigurations) {
      const sum = config.termFees.reduce((acc, t) => acc + (Number(t.PaymentAmount) || 0), 0);
      if (sum !== config.Amount) {
        const msg = await firstValueFrom(this.translate.get('sumOfAllTermAmountsMustEqualTheFeeTypeTotal'));
        customToastr(`${msg}: ${config.Name} (${this.currencySymbol}${config.Amount})`, 'error');
        return;
      }
    }
    this.fbTermConfigSaving = true;
    const reqObj = { termFeeConfigurations: this.fbTermConfigurations, fkFeeStructuresId: this.fbTermConfigStructureId };
    this.feesService.updateTermConfigForStruc(reqObj, this.currentAcademicId).subscribe({
      next: async (data: any) => {
        this.fbTermConfigSaving = false;
        if (data?.isValidationError) {
          const names = (data.responses || []).filter((r: any) => r.error !== 'DB_ERROR').map((r: any) => r.tr?.TermName).filter(Boolean);
          const msg = await firstValueFrom(this.translate.get('someStudentAlreadyPaidFor'));
          customToastr(`${msg} <b>${names.join(', ')}</b>`, 'error');
          return;
        }
        const msg = await firstValueFrom(this.translate.get('settingUpdated'));
        customToastr(msg, 'success');
        this.fbTermConfigModalOpen = false;
      },
      error: async () => {
        this.fbTermConfigSaving = false;
        const msg = await firstValueFrom(this.translate.get('somethingWentWrongPleaseTryAgain'));
        customToastr(msg, 'error');
      },
    });
  }

  // ---------- Fee Structure ----------
  fbStructuresLoaded = false;
  fbStructuresLoading = false;
  fbStructureList: FbFeeStructureRow[] = [];
  fbStudentCategoryListForSelect: { pkStudentsCategoryId: number; Name: string }[] = [];
  fbStructureModalOpen = false;
  fbStructureSaving = false;
  fbStructureForm: { name: string; description: string; studentCategoryId: number | string; feeStructureId: number | null; appliesTo?: string; collectedAmount?: number } = {
    name: '',
    description: '',
    studentCategoryId: '',
    feeStructureId: null,
  };

  private fbLoadStructures(): void {
    this.fbStructuresLoaded = true;
    this.fbStructuresLoading = true;
    this.feesService.fetchFeeStructure(this.currentAcademicId).subscribe({
      next: (data: any) => {
        this.fbStructuresLoading = false;
        this.fbStructureList = Array.isArray(data) ? data : [];
      },
      error: () => {
        this.fbStructuresLoading = false;
        this.fbStructureList = [];
      },
    });
  }

  private fbLoadStudentCategoryListForSelect(): void {
    this.feesService.getStudentCategoryList(null, this.currentAcademicId).subscribe({
      next: (data: any) => {
        this.fbStudentCategoryListForSelect = Array.isArray(data) ? data : [];
      },
      error: () => {
        this.fbStudentCategoryListForSelect = [];
      },
    });
  }

  openCreateFeeStructure(): void {
    this.fbStructureForm = { name: '', description: '', studentCategoryId: '', feeStructureId: null };
    this.fbLoadStudentCategoryListForSelect();
    this.fbStructureModalOpen = true;
  }

  openEditFeeStructure(structure: FbFeeStructureRow): void {
    // The default structure comes back with isEditable === false and must stay read-only.
    if ((structure as any).isEditable === false) {
      return;
    }
    this.fbLoadStudentCategoryListForSelect();
    this.fbStructureForm = {
      name: structure.Name,
      description: structure.Description || '',
      studentCategoryId: structure.fkStudentsCategoryId || '',
      feeStructureId: structure.pkFeeStructuresId,
      appliesTo: structure.appliesTo,
      collectedAmount: structure.collectedAmount,
    };
    this.fbStructureModalOpen = true;
  }

  closeFeeStructureModal(): void {
    this.fbStructureModalOpen = false;
  }

  async saveFeeStructure(): Promise<void> {
    if (!this.fbStructureForm.name || !this.fbStructureForm.studentCategoryId) {
      const msg = await firstValueFrom(this.translate.get('pleaseFillAllRequiredFields'));
      customToastr(msg, 'error');
      return;
    }
    this.fbStructureSaving = true;
    if (this.fbStructureForm.feeStructureId) {
      const criteria = {
        name: this.fbStructureForm.name,
        description: this.fbStructureForm.description,
        studentCategoryId: this.fbStructureForm.studentCategoryId,
        feeStructureId: this.fbStructureForm.feeStructureId,
      };
      this.feesService.updateFeeStructure(criteria, this.currentAcademicId).subscribe({
        next: async (data: any) => {
          this.fbStructureSaving = false;
          const msg = await firstValueFrom(this.translate.get(data?.status === false ? 'somethingWentWrongPleaseTryAgain' : 'settingUpdated'));
          customToastr(msg, data?.status === false ? 'error' : 'success');
          if (data?.status !== false) {
            this.fbStructureModalOpen = false;
            this.fbLoadStructures();
          }
        },
        error: async () => {
          this.fbStructureSaving = false;
          const msg = await firstValueFrom(this.translate.get('somethingWentWrongPleaseTryAgain'));
          customToastr(msg, 'error');
        },
      });
    } else {
      this.feesService
        .createFeeStructure(this.fbStructureForm.name, this.fbStructureForm.studentCategoryId, this.fbStructureForm.description, this.currentAcademicId)
        .subscribe({
          next: async (data: any) => {
            this.fbStructureSaving = false;
            const msg = await firstValueFrom(this.translate.get(data?.status === false ? 'somethingWentWrongPleaseTryAgain' : 'settingUpdated'));
            customToastr(msg, data?.status === false ? 'error' : 'success');
            if (data?.status !== false) {
              this.fbStructureModalOpen = false;
              this.fbLoadStructures();
            }
          },
          error: async () => {
            this.fbStructureSaving = false;
            const msg = await firstValueFrom(this.translate.get('somethingWentWrongPleaseTryAgain'));
            customToastr(msg, 'error');
          },
        });
    }
  }

  // ---------- Student Categories ----------
  fbCategoriesLoaded = false;
  fbCategoriesLoading = false;
  fbCategoryList: FbStudentCategoryRow[] = [];
  fbCategoryModalOpen = false;
  fbCategorySaving = false;
  fbCategoryForm: { name: string; description: string; studentCategoryId: number | null } = { name: '', description: '', studentCategoryId: null };
  fbDeleteCategoryTarget: FbStudentCategoryRow | null = null;
  fbDeleteCategoryModalOpen = false;
  fbDeleteCategorySaving = false;

  private fbLoadCategories(): void {
    this.fbCategoriesLoaded = true;
    this.fbCategoriesLoading = true;
    this.feesService.getStudentCategoryList(null, this.currentAcademicId).subscribe({
      next: (data: any) => {
        this.fbCategoriesLoading = false;
        this.fbCategoryList = (Array.isArray(data) ? data : []).map((c: any) => ({
          ...c,
          studentCount: c.studentCount ?? c.totalStudents,
        }));
      },
      error: () => {
        this.fbCategoriesLoading = false;
        this.fbCategoryList = [];
      },
    });
  }

  openCreateStudentCategory(): void {
    this.fbCategoryForm = { name: '', description: '', studentCategoryId: null };
    this.fbCategoryModalOpen = true;
  }

  openEditStudentCategory(category: FbStudentCategoryRow): void {
    this.fbCategoryForm = { name: category.Name, description: category.Description || '', studentCategoryId: category.pkStudentsCategoryId };
    this.fbCategoryModalOpen = true;
  }

  closeStudentCategoryModal(): void {
    this.fbCategoryModalOpen = false;
  }

  async saveStudentCategory(): Promise<void> {
    if (!this.fbCategoryForm.name) {
      const msg = await firstValueFrom(this.translate.get('pleaseFillAllRequiredFields'));
      customToastr(msg, 'error');
      return;
    }
    this.fbCategorySaving = true;
    const obs = this.fbCategoryForm.studentCategoryId
      ? this.feesService.editStudentCategory(this.fbCategoryForm.name, this.fbCategoryForm.description, this.fbCategoryForm.studentCategoryId, this.currentAcademicId)
      : this.feesService.createStudentCategory(this.fbCategoryForm.name, this.fbCategoryForm.description, this.currentAcademicId);
    obs.subscribe({
      next: async (data: any) => {
        this.fbCategorySaving = false;
        const msg = await firstValueFrom(this.translate.get(data?.status === false ? 'somethingWentWrongPleaseTryAgain' : 'settingUpdated'));
        customToastr(msg, data?.status === false ? 'error' : 'success');
        if (data?.status !== false) {
          this.fbCategoryModalOpen = false;
          this.fbLoadCategories();
        }
      },
      error: async () => {
        this.fbCategorySaving = false;
        const msg = await firstValueFrom(this.translate.get('somethingWentWrongPleaseTryAgain'));
        customToastr(msg, 'error');
      },
    });
  }

  openDeleteStudentCategory(category: FbStudentCategoryRow): void {
    this.fbDeleteCategoryTarget = category;
    this.fbDeleteCategoryModalOpen = true;
  }

  closeDeleteStudentCategory(): void {
    this.fbDeleteCategoryModalOpen = false;
  }

  deleteStudentCategoryConfirm(): void {
    if (!this.fbDeleteCategoryTarget) {
      return;
    }
    this.fbDeleteCategorySaving = true;
    this.feesService.deleteStudentCategory(this.fbDeleteCategoryTarget.pkStudentsCategoryId, this.currentAcademicId).subscribe({
      next: async (data: any) => {
        this.fbDeleteCategorySaving = false;
        const msg = await firstValueFrom(this.translate.get(data?.status === false ? 'somethingWentWrongPleaseTryAgain' : 'settingUpdated'));
        customToastr(msg, data?.status === false ? 'error' : 'success');
        if (data?.status !== false) {
          this.fbDeleteCategoryModalOpen = false;
          this.fbLoadCategories();
        }
      },
      error: async () => {
        this.fbDeleteCategorySaving = false;
        const msg = await firstValueFrom(this.translate.get('somethingWentWrongPleaseTryAgain'));
        customToastr(msg, 'error');
      },
    });
  }

  // ---------- Fee Components/Types ----------
  fbComponentsLoaded = false;
  fbComponentsLoading = false;
  fbComponentList: FbFeeComponentRow[] = [];
  fbComponentModalOpen = false;
  fbComponentSaving = false;
  fbComponentForm: { Name: string; includeInTerm: boolean; AppliesTo: string; duedate: string; feesTypeId: number | null } = {
    Name: '',
    includeInTerm: false,
    AppliesTo: 'ALL_STUDENTS',
    duedate: '',
    feesTypeId: null,
  };

  private fbLoadComponents(): void {
    this.fbComponentsLoaded = true;
    this.fbComponentsLoading = true;
    this.feesService.feeComponentList(this.currentAcademicId).subscribe({
      next: (data: any) => {
        this.fbComponentsLoading = false;
        // The API names these pkFeesTypeId / isIncludedInTerm (see old
        // component's feeComponentList reads); normalise to the row shape here.
        this.fbComponentList = (Array.isArray(data) ? data : []).map((c: any) => ({
          ...c,
          feesTypeId: c.feesTypeId ?? c.pkFeesTypeId,
          includeInTerm: c.includeInTerm ?? (c.isIncludedInTerm ? 1 : 0),
        }));
      },
      error: () => {
        this.fbComponentsLoading = false;
        this.fbComponentList = [];
      },
    });
  }

  openCreateFeeComponent(): void {
    this.fbComponentForm = { Name: '', includeInTerm: false, AppliesTo: 'ALL_STUDENTS', duedate: '', feesTypeId: null };
    this.fbComponentModalOpen = true;
  }

  // Name + Due Date only — Include-in-Term and Applies-To are locked once
  // created, matching the old component's offcanvas-manage-feeComponent
  // (both fields rendered [disabled]="true" there).
  openEditFeeComponent(component: FbFeeComponentRow): void {
    this.fbComponentForm = {
      Name: component.Name,
      includeInTerm: component.includeInTerm === 1,
      AppliesTo: component.AppliesTo || 'ALL_STUDENTS',
      duedate: this.toDateInputValue(component.DueDate),
      feesTypeId: component.feesTypeId,
    };
    this.fbComponentModalOpen = true;
  }

  // <input type="date"> only accepts/displays a strictly yyyy-MM-dd bound
  // value — the API's DueDate is some other parseable date string (the
  // old component reformats the same field to d/mm/yyyy for its own text-
  // based jQuery datepicker, feesmanage.component.ts:1956-1962), so
  // without this conversion the native input silently shows empty on
  // edit even though a real due date exists. Mirrors the old component's
  // own sentinel-value guard for missing dates.
  private toDateInputValue(raw: string | null | undefined): string {
    if (!raw || raw === '0000-00-00' || raw === '-') {
      return '';
    }
    const d = new Date(raw);
    if (isNaN(d.getTime())) {
      return '';
    }
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  closeFeeComponentModal(): void {
    this.fbComponentModalOpen = false;
  }

  async saveFeeComponent(): Promise<void> {
    if (!this.fbComponentForm.Name || (!this.fbComponentForm.includeInTerm && !this.fbComponentForm.duedate)) {
      const msg = await firstValueFrom(this.translate.get('pleaseFillAllRequiredFields'));
      customToastr(msg, 'error');
      return;
    }
    this.fbComponentSaving = true;
    if (this.fbComponentForm.feesTypeId) {
      this.feesService.updateFeeTypeComponent(this.fbComponentForm.Name, this.fbComponentForm.duedate, this.fbComponentForm.feesTypeId, this.currentAcademicId).subscribe({
        next: async (data: any) => {
          this.fbComponentSaving = false;
          const msg = await firstValueFrom(this.translate.get(data?.status === false ? 'somethingWentWrongPleaseTryAgain' : 'settingUpdated'));
          customToastr(msg, data?.status === false ? 'error' : 'success');
          if (data?.status !== false) {
            this.fbComponentModalOpen = false;
            this.fbLoadComponents();
          }
        },
        error: async () => {
          this.fbComponentSaving = false;
          const msg = await firstValueFrom(this.translate.get('somethingWentWrongPleaseTryAgain'));
          customToastr(msg, 'error');
        },
      });
    } else {
      this.feesService
        .addFeeTypeComponent(this.fbComponentForm.Name, this.fbComponentForm.includeInTerm ? 1 : 0, this.currentAcademicId, this.fbComponentForm.AppliesTo, this.fbComponentForm.duedate, this.currentAcademicId)
        .subscribe({
          next: async (data: any) => {
            this.fbComponentSaving = false;
            const msg = await firstValueFrom(this.translate.get(data?.status === false ? 'somethingWentWrongPleaseTryAgain' : 'settingUpdated'));
            customToastr(msg, data?.status === false ? 'error' : 'success');
            if (data?.status !== false) {
              this.fbComponentModalOpen = false;
              this.fbLoadComponents();
            }
          },
          error: async () => {
            this.fbComponentSaving = false;
            const msg = await firstValueFrom(this.translate.get('somethingWentWrongPleaseTryAgain'));
            customToastr(msg, 'error');
          },
        });
    }
  }

  // ---------- Fee Terms ----------
  fbTermsLoaded = false;
  fbTermsLoading = false;
  fbTermList: FbTermRow[] = [];
  fbTermModalOpen = false;
  fbTermSaving = false;
  fbTermForm: { TermName: string; DueDate: string; pkFeesTermId: number | null } = { TermName: '', DueDate: '', pkFeesTermId: null };

  private fbLoadTerms(): void {
    this.fbTermsLoaded = true;
    this.fbTermsLoading = true;
    this.feesService.fetchSchoolFeeTerms(this.currentAcademicId).subscribe({
      next: (data: any) => {
        this.fbTermsLoading = false;
        this.fbTermList = Array.isArray(data) ? data : [];
      },
      error: () => {
        this.fbTermsLoading = false;
        this.fbTermList = [];
      },
    });
  }

  // ---- Student Categories: assign / move students (ports the old "Assign Students" popup) ----
  fbAssignOpen = false;
  fbAssignCategory: any = null;
  fbAssignClassId: any = '0';
  fbAssignQuery = '';
  fbAssignResults: any[] = [];
  fbAssignSearching = false;
  fbAssignSaving = false;
  fbAssignSelectAll = false;

  get fbAssignSelectedCount(): number {
    return this.fbAssignResults.filter((r) => r.selected).length;
  }

  // Students already in the category (server-side paged, like the old DataTable)
  fbMemberRows: any[] = [];
  fbMemberTotal = 0;
  fbMemberPage = 1;
  fbMemberSize = 10;
  fbMemberSearch = '';
  fbMemberLoading = false;

  // Move a student to another category
  fbMoveOpen = false;
  fbMoveStudent: any = null;
  fbMoveCategoryId: any = '0';
  fbMoveSaving = false;

  openAssignStudentsToCategory(category: any): void {
    this.fbAssignCategory = { ...category };
    this.fbAssignClassId = '0';
    this.fbAssignQuery = '';
    this.fbAssignResults = [];
    this.fbAssignSelectAll = false;
    this.fbMemberSearch = '';
    this.fbMemberPage = 1;
    this.fbAssignOpen = true;
    this.feesService.getClassListOfSchool().subscribe({
      next: (data: any) => (this.fbClassListOfSchool = Array.isArray(data) ? data : []),
      error: () => (this.fbClassListOfSchool = []),
    });
    this.fbLoadMembers();
  }

  closeAssignStudentsToCategory(): void {
    this.fbAssignOpen = false;
    this.fbLoadCategories();
  }

  fbLoadMembers(): void {
    if (!this.fbAssignCategory) {
      return;
    }
    this.fbMemberLoading = true;
    this.feesService
      .getStudentsInCategory({
        schoolId: localStorage.getItem('schoolinfo'),
        current_academic: this.currentAcademicId,
        studentCategoryId: this.fbAssignCategory.pkStudentsCategoryId,
        countFrom: (this.fbMemberPage - 1) * this.fbMemberSize,
        lengthOf: this.fbMemberSize,
        search: this.fbMemberSearch,
        sort: [],
      })
      .subscribe({
        next: (res: any) => {
          this.fbMemberLoading = false;
          this.fbMemberRows = Array.isArray(res?.data) ? res.data : [];
          this.fbMemberTotal = Number(res?.recordsFiltered ?? res?.recordsTotal ?? this.fbMemberRows.length) || 0;
        },
        error: () => {
          this.fbMemberLoading = false;
          this.fbMemberRows = [];
          this.fbMemberTotal = 0;
        },
      });
  }

  get fbMemberPages(): number {
    return Math.max(1, Math.ceil(this.fbMemberTotal / this.fbMemberSize));
  }

  fbMemberGo(page: number): void {
    this.fbMemberPage = Math.min(Math.max(1, page), this.fbMemberPages);
    this.fbLoadMembers();
  }

  fbMemberSearchChanged(): void {
    this.fbMemberPage = 1;
    this.fbLoadMembers();
  }

  async fbSearchStudentsToAssign(): Promise<void> {
    const text = (this.fbAssignQuery || '').trim();
    if (!text) {
      customToastr(await firstValueFrom(this.translate.get('pleaseEnterStudentNameToSearch')), 'error');
      return;
    }
    const classId = this.fbAssignClassId == 0 ? null : this.fbAssignClassId;
    this.fbAssignSearching = true;
    this.fbAssignSelectAll = false;
    this.feesService.searchStudentToAssignCategory(classId, text, this.currentAcademicId).subscribe({
      next: (data: any) => {
        this.fbAssignSearching = false;
        this.fbAssignResults = Array.isArray(data) ? data : [];
      },
      error: () => {
        this.fbAssignSearching = false;
        this.fbAssignResults = [];
      },
    });
  }

  fbToggleAssignAll(checked: boolean): void {
    this.fbAssignResults.forEach((r) => (r.selected = r.paidFees > 0 ? false : checked));
  }

  async fbAssignSelected(): Promise<void> {
    const students = this.fbAssignResults.filter((r) => r.selected);
    if (students.length === 0) {
      customToastr(await firstValueFrom(this.translate.get('youHaveNotSelectedAnyStudents')), 'error');
      return;
    }
    this.fbAssignSaving = true;
    this.feesService.addStudentsToStudentCategory(students, this.fbAssignCategory.pkStudentsCategoryId, this.currentAcademicId).subscribe({
      next: async (data: any) => {
        this.fbAssignSaving = false;
        if (data?.status) {
          customToastr(await firstValueFrom(this.translate.get('studentsAreSuccessfullyAssigned')), 'success');
          this.fbAssignCategory.studentCount = (Number(this.fbAssignCategory.studentCount) || 0) + students.length;
          this.fbAssignSelectAll = false;
          this.fbSearchStudentsToAssign();
          this.fbMemberPage = 1;
          this.fbLoadMembers();
        } else {
          customToastr(await firstValueFrom(this.translate.get('failedToAssignStudents')), 'error');
        }
      },
      error: async () => {
        this.fbAssignSaving = false;
        customToastr(await firstValueFrom(this.translate.get('failedToAssignStudents')), 'error');
      },
    });
  }

  fbOpenMoveStudent(student: any): void {
    this.fbMoveStudent = student;
    this.fbMoveCategoryId = String(this.fbAssignCategory?.pkStudentsCategoryId ?? '0');
    this.fbMoveOpen = true;
  }

  async fbMoveStudentConfirm(): Promise<void> {
    const student = this.fbMoveStudent;
    const studentId = student?.studentId;
    const memberId = student?.pkStudentCategoryMembersId;
    const isNum = (v: any) => v !== null && v !== undefined && v !== '' && !isNaN(Number(v));
    if (!(isNum(studentId) && isNum(this.fbMoveCategoryId) && isNum(memberId))) {
      customToastr(await firstValueFrom(this.translate.get('movingStudentToAnotherCategoryFailedPleaseTryAgain')), 'error');
      return;
    }
    this.fbMoveSaving = true;
    // Same arguments as the old moveStudentToAnotherCategory(): the category the student is moving
    // from is the one currently open (null when it is the default category, id "0").
    const oldCategoryId = this.fbAssignCategory?.pkStudentsCategoryId == '0' ? null : this.fbAssignCategory?.pkStudentsCategoryId;
    this.feesService.moveStudentToAnotherStudentCategory(studentId, this.fbMoveCategoryId, memberId, oldCategoryId, this.currentAcademicId).subscribe({
      next: async (data: any) => {
        this.fbMoveSaving = false;
        if (data?.status) {
          customToastr(await firstValueFrom(this.translate.get('studentHasBeenSuccessfullyMoved')), 'success');
          this.fbMoveOpen = false;
          this.fbLoadMembers();
          this.fbLoadCategories();
        } else if (data?.message) {
          customToastr(data.message, 'error');
        } else {
          customToastr(await firstValueFrom(this.translate.get('failedToMoveStudentToAnotherCategory')), 'error');
        }
      },
      error: async () => {
        this.fbMoveSaving = false;
        customToastr(await firstValueFrom(this.translate.get('failedToMoveStudentToAnotherCategory')), 'error');
      },
    });
  }

  // ---- Search box above each Fees Board list (categories / components / structures / terms) ----
  fbSearch: { [key: string]: string } = { struct: '', cat: '', comp: '', term: '' };

  fbFilter<T>(list: T[], key: string): T[] {
    const q = (this.fbSearch[key] || '').trim().toLowerCase();
    if (!q) {
      return list;
    }
    return (list || []).filter((item: any) =>
      Object.values(item || {}).some((v) => (typeof v === 'string' || typeof v === 'number') && String(v).toLowerCase().includes(q))
    );
  }

  // Step pagination (Previous / 1 2 3 / Next) for the Fees Board lists, like the old DataTables.
  fbListPage: { [key: string]: number } = { struct: 1, cat: 1, comp: 1, term: 1 };
  readonly fbListPageSize = 10;

  fbPaged<T>(list: T[], key: string): T[] {
    const all = this.fbFilter(list, key);
    const pages = this.fbListPages(list, key);
    if ((this.fbListPage[key] || 1) > pages) {
      this.fbListPage[key] = pages;
    }
    const start = ((this.fbListPage[key] || 1) - 1) * this.fbListPageSize;
    return all.slice(start, start + this.fbListPageSize);
  }

  fbListPages<T>(list: T[], key: string): number {
    return Math.max(1, Math.ceil(this.fbFilter(list, key).length / this.fbListPageSize));
  }

  fbListPageNumbers<T>(list: T[], key: string): number[] {
    const max = this.fbListPages(list, key);
    const cur = this.fbListPage[key] || 1;
    const start = Math.max(1, Math.min(cur - 2, max - 4));
    const out: number[] = [];
    for (let i = start; i <= Math.min(max, start + 4); i++) {
      out.push(i);
    }
    return out;
  }

  fbListGo<T>(list: T[], key: string, page: number): void {
    this.fbListPage[key] = Math.min(Math.max(1, page), this.fbListPages(list, key));
  }

  openCreateTerm(): void {
    this.fbTermForm = { TermName: '', DueDate: '', pkFeesTermId: null };
    this.fbTermModalOpen = true;
  }

  openEditTerm(term: FbTermRow): void {
    this.fbTermForm = { TermName: term.TermName, DueDate: this.toDateInputValue(term.DueDate), pkFeesTermId: term.pkFeesTermId };
    this.fbTermModalOpen = true;
  }

  closeTermModal(): void {
    this.fbTermModalOpen = false;
  }

  async saveTerm(): Promise<void> {
    if (!this.fbTermForm.TermName || !this.fbTermForm.DueDate) {
      const msg = await firstValueFrom(this.translate.get('pleaseFillAllRequiredFields'));
      customToastr(msg, 'error');
      return;
    }
    this.fbTermSaving = true;
    if (this.fbTermForm.pkFeesTermId) {
      // Same body as the old component's updateTerm(): name / duedate
      // (yyyy-mm-dd) / pkFeesTermId — the API ignores TermName / DueDate here.
      const reqObj = { name: this.fbTermForm.TermName, duedate: this.fbTermForm.DueDate, pkFeesTermId: this.fbTermForm.pkFeesTermId };
      this.feesService.updateTerm(reqObj, this.currentAcademicId).subscribe({
        next: async (data: any) => {
          this.fbTermSaving = false;
          this.fbLoadTerms();
          if (data?.message === 'Success') {
            customToastr(await firstValueFrom(this.translate.get('termSuccessfullyUpdated')), 'success');
            this.fbTermModalOpen = false;
          } else if (data?.message === 'AlreadyExist') {
            customToastr(await firstValueFrom(this.translate.get('termAlreadyExists')), 'error');
          } else {
            customToastr(await firstValueFrom(this.translate.get('feesTermNotUpdatedPleaseTryAgain')), 'error');
          }
        },
        error: async () => {
          this.fbTermSaving = false;
          const msg = await firstValueFrom(this.translate.get('somethingWentWrongPleaseTryAgain'));
          customToastr(msg, 'error');
        },
      });
    } else {
      // Same body as the old component's createTerm(): TermName, academicId,
      // the (always empty) duedate key from its form model, and DueDate as
      // yyyy-mm-dd; the service adds current_academic.
      const term = { TermName: this.fbTermForm.TermName, academicId: this.currentAcademicId, duedate: '', DueDate: this.fbTermForm.DueDate };
      this.feesService.createTerm(term, this.currentAcademicId).subscribe({
        next: async (data: any) => {
          this.fbTermSaving = false;
          // The API reports success as status:true (old screen's check) — accept
          // message "Success" too — and the list is re-read either way so what
          // is on screen always matches the server.
          const created = !data?.errorcode && (data?.status || data?.message === 'Success');
          if (created) {
            customToastr(await firstValueFrom(this.translate.get('termSuccessfullyCreated')), 'success');
            this.fbTermModalOpen = false;
          } else if (data?.errorcode) {
            customToastr(await firstValueFrom(this.translate.get('failedToCreateTermPleaseTryAgain')), 'error');
          } else {
            customToastr(data?.message || (await firstValueFrom(this.translate.get('failedToCreateTermPleaseTryAgain'))), 'error');
          }
          this.fbLoadTerms();
        },
        error: async () => {
          this.fbTermSaving = false;
          const msg = await firstValueFrom(this.translate.get('somethingWentWrongPleaseTryAgain'));
          customToastr(msg, 'error');
        },
      });
    }
  }

  // ---------- Late Fees (configuration — distinct from the Fee Collection
  // tab's waive-OFF action already ported there) ----------
  fbLateFeesLoaded = false;
  fbLateFeesLoading = false;
  fbLateFeeList: FbLateFeeRow[] = [];
  fbIsLateFeeIncludedInCurrentMonth = false;

  fbOpenLateFeeTab(): void {
    this.fbLateFeesLoaded = true;
    this.fbLateFeesLoading = true;
    this.feesService.newFetchLateFeeConfig(this.currentAcademicId).subscribe({
      next: (data: any) => {
        this.fbLateFeesLoading = false;
        if (data?.errorcode) {
          return;
        }
        this.fbLateFeeList = data?.lateFeeList || [];
        this.fbIsLateFeeIncludedInCurrentMonth = data?.isLateFeeIncludedInCurrentMonth === 1;
      },
      error: () => {
        this.fbLateFeesLoading = false;
      },
    });
  }

  fbToggleLateFeeIncludedInCurrentMonth(): void {
    this.fbIsLateFeeIncludedInCurrentMonth = !this.fbIsLateFeeIncludedInCurrentMonth;
    this.feesService.updateIsLateFeeIncludedInCurrentMonth({ isLateFeeIncludedInCurrentMonth: this.fbIsLateFeeIncludedInCurrentMonth ? 1 : 0, lang: 'en' }).subscribe();
  }

  // Add/Edit late fee dialog state — ported from openCreateLateFee()/
  // getAllFeesComponent()/getLateFeeInfo()/addLateFees()/updateLateFees(),
  // including the 3-tier cascading select-all logic
  // (checkSelectAllStatus/checkSelectAllTermStatus/checkSelectAllTypeStatus/
  // checkSelectAllTransportStatus) verbatim.
  fbLateFeeModalOpen = false;
  fbLateFeeLoading = false;
  fbLateFeeSaving = false;
  fbSelectedLateFeesId: number | null = null;
  fbLateFeesDays: number | null = null;
  fbLateFeesAmount: number | null = null;
  fbFeesList: { feeTermId: number; feeTermName: string; isFeeSelected: boolean; termFeeList?: { feeTypeId: number; feeTypeName: string }[] }[] = [];
  fbTermFeeList: { feeTypeId: number; feeTypeName: string; isFeeSelected: boolean }[] = [];
  fbTransportList: { busStopTermId: number; busStopTermName: string; isFeeSelected: boolean }[] = [];
  fbIncludeLateFeeInTransportFee = 0;
  fbSelectAllFees = false;
  fbSelectAllTermFees = false;
  fbSelectAllTypeFees = false;
  fbSelectAllTransportFees = false;
  fbExpandedItems: boolean[] = [];

  openCreateLateFee(data: FbLateFeeRow | null): void {
    if (data) {
      this.fbSelectedLateFeesId = data.pkLateFeesId;
      this.fbLateFeesDays = data.lateDays;
      this.fbLateFeesAmount = data.lateFees;
      this.fbLateFeeModalOpen = true;
      this.fbGetLateFeeInfo();
    } else {
      this.fbSelectedLateFeesId = null;
      this.fbLateFeesDays = null;
      this.fbLateFeesAmount = null;
      this.fbSelectAllFees = false;
      this.fbSelectAllTermFees = false;
      this.fbSelectAllTypeFees = false;
      this.fbSelectAllTransportFees = false;
      this.fbLateFeeModalOpen = true;
      this.fbGetAllFeesComponent();
    }
  }

  closeLateFeeModal(): void {
    this.fbLateFeeModalOpen = false;
  }

  private fbGetAllFeesComponent(): void {
    this.fbLateFeeLoading = true;
    const payload = { academicId: this.currentAcademicId, lang: 'en' };
    this.feesService.getAllFeesComponent(payload).subscribe({
      next: (response: any) => {
        this.fbLateFeeLoading = false;
        if (response?.status && response?.body) {
          this.fbTermFeeList = response.body.typeFeeList || [];
          this.fbFeesList = response.body.feesTerms || [];
          this.fbTransportList = response.body.busTermList || [];
          this.fbIncludeLateFeeInTransportFee = response.body.includeLateFeeInTransportFee || 0;
          this.fbExpandedItems = this.fbFeesList.map(() => false);
          this.fbSelectAllFees = false;
        }
      },
      error: () => {
        this.fbLateFeeLoading = false;
      },
    });
  }

  private fbGetLateFeeInfo(): void {
    this.fbLateFeeLoading = true;
    const payload = { lang: 'en', academicId: this.currentAcademicId, lateFeeId: this.fbSelectedLateFeesId };
    this.feesService.getLateFeesComponent(payload).subscribe({
      next: (response: any) => {
        this.fbLateFeeLoading = false;
        if (response?.body) {
          this.fbTermFeeList = response.body.typeFeeList || [];
          this.fbFeesList = response.body.feesTerms || [];
          this.fbTransportList = response.body.busTermList || [];
          this.fbExpandedItems = this.fbFeesList.map(() => false);
          this.fbOnIncludeLateFeeInTransportFeeChange(response.body.includeLateFeeInTransportFee || 0);
          this.fbCheckSelectAllStatus();
        }
      },
      error: () => {
        this.fbLateFeeLoading = false;
      },
    });
  }

  fbExpandItem(index: number): void {
    this.fbExpandedItems[index] = !this.fbExpandedItems[index];
  }

  fbOnFeeTermChange(fee: any): void {
    fee.isFeeSelected = fee.termFeeList?.length ? fee.termFeeList.every(() => fee.isFeeSelected) : fee.isFeeSelected;
    this.fbCheckSelectAllStatus();
  }

  fbOnIncludeLateFeeInTransportFeeChange(value: number): void {
    this.fbIncludeLateFeeInTransportFee = value;
    if (value === 0) {
      this.fbSelectAllTransportFees = false;
      this.fbTransportList.forEach((fee) => (fee.isFeeSelected = false));
    }
  }

  fbOnSelectAllTermFeesChange(): void {
    this.fbFeesList.forEach((fee) => (fee.isFeeSelected = this.fbSelectAllTermFees));
    this.fbCheckSelectAllStatus();
  }

  fbOnSelectAllTypeFeesChange(): void {
    this.fbTermFeeList.forEach((fee) => (fee.isFeeSelected = this.fbSelectAllTypeFees));
    this.fbCheckSelectAllStatus();
  }

  fbOnSelectAllTransportFeesChange(): void {
    this.fbTransportList.forEach((fee) => (fee.isFeeSelected = this.fbSelectAllTransportFees));
    this.fbCheckSelectAllStatus();
  }

  fbOnSelectAllFeesChange(): void {
    this.fbFeesList.forEach((fee) => (fee.isFeeSelected = this.fbSelectAllFees));
    this.fbTermFeeList.forEach((fee) => (fee.isFeeSelected = this.fbSelectAllFees));
    if (this.fbIncludeLateFeeInTransportFee === 1) {
      this.fbTransportList.forEach((fee) => (fee.isFeeSelected = this.fbSelectAllFees));
    }
    this.fbSelectAllTermFees = this.fbSelectAllFees && this.fbFeesList.length > 0;
    this.fbSelectAllTypeFees = this.fbSelectAllFees && this.fbTermFeeList.length > 0;
    if (this.fbIncludeLateFeeInTransportFee === 1) {
      this.fbSelectAllTransportFees = this.fbSelectAllFees && this.fbTransportList.length > 0;
    }
  }

  fbCheckSelectAllStatus(): void {
    const hasAnyItems = this.fbFeesList.length > 0 || this.fbTermFeeList.length > 0 || this.fbTransportList.length > 0;
    if (!hasAnyItems) {
      this.fbSelectAllFees = false;
      return;
    }
    const feesListSelected = this.fbFeesList.length > 0 ? this.fbFeesList.every((f) => f.isFeeSelected) : true;
    const termFeeListSelected = this.fbTermFeeList.length > 0 ? this.fbTermFeeList.every((f) => f.isFeeSelected) : true;
    const transportListSelected =
      this.fbTransportList.length > 0 && this.fbIncludeLateFeeInTransportFee === 1 ? this.fbTransportList.every((f) => f.isFeeSelected) : true;
    this.fbSelectAllFees = feesListSelected && termFeeListSelected && transportListSelected;
    this.fbSelectAllTermFees = this.fbFeesList.length === 0 ? false : this.fbFeesList.every((f) => f.isFeeSelected);
    this.fbSelectAllTypeFees = this.fbTermFeeList.length === 0 ? false : this.fbTermFeeList.every((f) => f.isFeeSelected);
    this.fbSelectAllTransportFees = this.fbTransportList.length > 0 ? this.fbTransportList.every((f) => f.isFeeSelected) : false;
  }

  private fbGetSelectedTermsFees(): number[] {
    return this.fbFeesList.filter((f) => f.isFeeSelected).map((f) => f.feeTermId);
  }
  private fbGetSelectedTypeFees(): number[] {
    return this.fbTermFeeList.filter((f) => f.isFeeSelected).map((f) => f.feeTypeId);
  }
  private fbGetBusTermIds(): number[] {
    return this.fbTransportList.filter((f) => f.isFeeSelected).map((f) => f.busStopTermId);
  }

  async saveLateFee(): Promise<void> {
    if (!this.fbLateFeesDays || !this.fbLateFeesAmount) {
      const msg = await firstValueFrom(this.translate.get('pleaseFillAllRequiredFields'));
      customToastr(msg, 'error');
      return;
    }
    this.fbLateFeeSaving = true;
    const body: any = {
      lang: 'en',
      lateDays: this.fbLateFeesDays,
      lateFee: this.fbLateFeesAmount,
      academicId: this.currentAcademicId,
      busFeeTerms: this.fbGetBusTermIds(),
      typeFees: this.fbGetSelectedTypeFees(),
      feesTerms: this.fbGetSelectedTermsFees(),
    };
    const obs = this.fbSelectedLateFeesId
      ? this.feesService.updtaeLateFeesData({ ...body, lateFeeId: this.fbSelectedLateFeesId })
      : this.feesService.addLateFeesData(body);
    obs.subscribe({
      next: (response: any) => {
        this.fbLateFeeSaving = false;
        if (response?.status && response?.body) {
          customToastr(response.body.message, 'success');
          this.fbLateFeeModalOpen = false;
          this.fbOpenLateFeeTab();
        }
      },
      error: async (error: any) => {
        this.fbLateFeeSaving = false;
        if (error?.error?.message) {
          customToastr(error.error.message, 'error');
        } else {
          const msg = await firstValueFrom(this.translate.get('somethingWentWrongPleaseTryAgain'));
          customToastr(msg, 'error');
        }
      },
    });
  }

  fbDeleteLateFeeTarget: FbLateFeeRow | null = null;
  fbDeleteLateFeeModalOpen = false;
  fbDeleteLateFeeSaving = false;

  openDeleteLateFee(lateFee: FbLateFeeRow): void {
    this.fbDeleteLateFeeTarget = lateFee;
    this.fbDeleteLateFeeModalOpen = true;
  }

  closeDeleteLateFee(): void {
    this.fbDeleteLateFeeModalOpen = false;
  }

  deleteLateFeeConfirm(): void {
    if (!this.fbDeleteLateFeeTarget) {
      return;
    }
    this.fbDeleteLateFeeSaving = true;
    this.feesService.deleteLateFee(this.fbDeleteLateFeeTarget.pkLateFeesId).subscribe({
      next: async (data: any) => {
        this.fbDeleteLateFeeSaving = false;
        const msg = await firstValueFrom(this.translate.get(data?.status === false ? 'somethingWentWrongPleaseTryAgain' : 'settingUpdated'));
        customToastr(msg, data?.status === false ? 'error' : 'success');
        this.fbDeleteLateFeeModalOpen = false;
        this.fbOpenLateFeeTab();
      },
      error: async () => {
        this.fbDeleteLateFeeSaving = false;
        const msg = await firstValueFrom(this.translate.get('somethingWentWrongPleaseTryAgain'));
        customToastr(msg, 'error');
      },
    });
  }

  // ---------- Other Fees ----------
  fbOtherFeesLoaded = false;
  fbOtherFeesLoading = false;
  fbOtherFeeList: FbOtherFeeRow[] = [];
  fbOtherFeeModalOpen = false;
  fbOtherFeeSaving = false;
  fbOtherFeeForm: { name: string; amount: number | null; pkOtherFeesId: number | null } = { name: '', amount: null, pkOtherFeesId: null };
  private fbOtherFeeOriginal: { name: string; amount: number } | null = null;

  private fbLoadOtherFees(): void {
    this.fbOtherFeesLoaded = true;
    this.fbOtherFeesLoading = true;
    this.feesService.getOtherFee(this.currentAcademicId).subscribe({
      next: (data: any) => {
        this.fbOtherFeesLoading = false;
        this.fbOtherFeeList = Array.isArray(data) ? data : [];
      },
      error: () => {
        this.fbOtherFeesLoading = false;
        this.fbOtherFeeList = [];
      },
    });
  }

  openCreateOtherFee(): void {
    this.fbOtherFeeForm = { name: '', amount: null, pkOtherFeesId: null };
    this.fbOtherFeeOriginal = null;
    this.fbOtherFeeModalOpen = true;
  }

  openEditOtherFee(otherFee: FbOtherFeeRow): void {
    this.fbOtherFeeForm = { name: otherFee.Name, amount: otherFee.amount, pkOtherFeesId: otherFee.pkOtherFeesId };
    this.fbOtherFeeOriginal = { name: otherFee.Name, amount: otherFee.amount };
    this.fbOtherFeeModalOpen = true;
  }

  closeOtherFeeModal(): void {
    this.fbOtherFeeModalOpen = false;
  }

  async saveOtherFee(): Promise<void> {
    if (!this.fbOtherFeeForm.name || !(Number(this.fbOtherFeeForm.amount) > 0)) {
      const msg = await firstValueFrom(this.translate.get('pleaseFillAllRequiredFields'));
      customToastr(msg, 'error');
      return;
    }
    if (
      this.fbOtherFeeOriginal &&
      this.fbOtherFeeOriginal.name === this.fbOtherFeeForm.name &&
      this.fbOtherFeeOriginal.amount === this.fbOtherFeeForm.amount
    ) {
      this.fbOtherFeeModalOpen = false;
      return;
    }
    this.fbOtherFeeSaving = true;
    const obs = this.fbOtherFeeForm.pkOtherFeesId
      ? this.feesService.editOtherFee(this.fbOtherFeeForm.name, this.fbOtherFeeForm.amount, this.fbOtherFeeForm.pkOtherFeesId, this.currentAcademicId)
      : this.feesService.createOtherFee(this.fbOtherFeeForm.name, this.fbOtherFeeForm.amount, this.currentAcademicId);
    obs.subscribe({
      next: async (data: any) => {
        this.fbOtherFeeSaving = false;
        const isEdit = !!this.fbOtherFeeForm.pkOtherFeesId;
        if (data?.message === 'Success') {
          const msg = await firstValueFrom(this.translate.get(isEdit ? 'otherFeeUpdatedSuccessfully' : 'otherFeeAddedSuccessfully'));
          customToastr(msg, 'success');
          this.fbOtherFeeModalOpen = false;
          this.fbLoadOtherFees();
        } else if (data?.message === 'AlreadyExist') {
          const msg = await firstValueFrom(this.translate.get('alreadyExistPleaseTryDifferentName'));
          customToastr('<b>' + this.fbOtherFeeForm.name + '</b> ' + msg, 'error');
        } else {
          const msg = await firstValueFrom(this.translate.get(isEdit ? 'failedToUpdateOtherFeePleaseTryAgain' : 'failedToAddOtherFeePleaseTryAgain'));
          customToastr(msg, 'error');
        }
      },
      error: async () => {
        this.fbOtherFeeSaving = false;
        const msg = await firstValueFrom(this.translate.get('somethingWentWrongPleaseTryAgain'));
        customToastr(msg, 'error');
      },
    });
  }

  fbDeleteOtherFeeTarget: FbOtherFeeRow | null = null;
  fbDeleteOtherFeeModalOpen = false;
  fbDeleteOtherFeeSaving = false;

  openDeleteOtherFee(otherFee: FbOtherFeeRow): void {
    this.fbDeleteOtherFeeTarget = otherFee;
    this.fbDeleteOtherFeeModalOpen = true;
  }

  closeDeleteOtherFee(): void {
    this.fbDeleteOtherFeeModalOpen = false;
  }

  deleteOtherFeeConfirm(): void {
    if (!this.fbDeleteOtherFeeTarget) {
      return;
    }
    this.fbDeleteOtherFeeSaving = true;
    this.feesService.deleteOtherFee(this.fbDeleteOtherFeeTarget.pkOtherFeesId).subscribe({
      next: async (data: any) => {
        this.fbDeleteOtherFeeSaving = false;
        const msg = await firstValueFrom(this.translate.get(data?.status === false ? 'somethingWentWrongPleaseTryAgain' : 'settingUpdated'));
        customToastr(msg, data?.status === false ? 'error' : 'success');
        if (data?.status !== false) {
          this.fbDeleteOtherFeeModalOpen = false;
          this.fbLoadOtherFees();
        }
      },
      error: async () => {
        this.fbDeleteOtherFeeSaving = false;
        const msg = await firstValueFrom(this.translate.get('somethingWentWrongPleaseTryAgain'));
        customToastr(msg, 'error');
      },
    });
  }

  // ---------- Other Fee Terms (per-other-fee installment terms) ----------
  fbOtherFeeTermsModalOpen = false;
  fbOtherFeeTermsSaving = false;
  fbOtherFeeTermsTarget: FbOtherFeeRow | null = null;
  fbOtherFeeTermsList: FbOtherFeeTermRow[] = [];
  private fbDeletedOtherFeeTermsList: FbOtherFeeTermRow[] = [];

  openManageOtherFeeTerms(otherFee: FbOtherFeeRow): void {
    this.fbOtherFeeTermsTarget = otherFee;
    this.fbDeletedOtherFeeTermsList = [];
    this.fbOtherFeeTermsModalOpen = true;
    this.feesService.getOtherFeeTerms(otherFee.pkOtherFeesId).subscribe({
      next: (data: any) => {
        this.fbOtherFeeTermsList =
          Array.isArray(data) && data.length > 0
            ? data.map((row: any) => ({ ...row, termDueDate: this.toDateInputValue(row.termDueDate) }))
            : [{ termName: '', termAmount: null, termDueDate: '', termStatus: 'NEW' }];
      },
      error: () => {
        this.fbOtherFeeTermsList = [{ termName: '', termAmount: null, termDueDate: '', termStatus: 'NEW' }];
      },
    });
  }

  closeOtherFeeTermsModal(): void {
    this.fbOtherFeeTermsModalOpen = false;
  }

  addOtherFeeTermRow(): void {
    this.fbOtherFeeTermsList.push({ termName: '', termAmount: null, termDueDate: '', termStatus: 'NEW' });
  }

  removeOtherFeeTermRow(index: number): void {
    const row = this.fbOtherFeeTermsList[index];
    if (row.pkOtherFeeTermId) {
      this.fbDeletedOtherFeeTermsList.push({ ...row, termStatus: 'DELETE' });
    }
    this.fbOtherFeeTermsList.splice(index, 1);
  }

  async saveOtherFeeTerms(): Promise<void> {
    if (!this.fbOtherFeeTermsTarget) {
      return;
    }
    for (const row of this.fbOtherFeeTermsList) {
      if (!row.termName || !(Number(row.termAmount) > 0) || !row.termDueDate) {
        const msg = await firstValueFrom(this.translate.get('pleaseProvideAllTermDetails'));
        customToastr(msg, 'error');
        return;
      }
    }
    const sum = this.fbOtherFeeTermsList.reduce((acc, r) => acc + (Number(r.termAmount) || 0), 0);
    if (sum !== this.fbOtherFeeTermsTarget.amount) {
      const msg = await firstValueFrom(this.translate.get('sumOfAllTermAmountsMustEqualTheFeeTypeTotal'));
      customToastr(`${msg}: ${this.currencySymbol}${this.fbOtherFeeTermsTarget.amount}`, 'error');
      return;
    }
    this.fbOtherFeeTermsSaving = true;
    const termsList = [
      ...this.fbOtherFeeTermsList.map((r) => ({ ...r, termStatus: r.termStatus || (r.pkOtherFeeTermId ? 'UPDATE' : 'NEW') })),
      ...this.fbDeletedOtherFeeTermsList,
    ];
    this.feesService.manageOtherFeeTerms(termsList, this.fbOtherFeeTermsTarget.pkOtherFeesId).subscribe({
      next: async (data: any) => {
        this.fbOtherFeeTermsSaving = false;
        const msg = await firstValueFrom(this.translate.get(data?.status === false ? 'somethingWentWrongPleaseTryAgain' : 'settingUpdated'));
        customToastr(msg, data?.status === false ? 'error' : 'success');
        if (data?.status !== false) {
          this.fbOtherFeeTermsModalOpen = false;
          this.fbLoadOtherFees();
        }
      },
      error: async () => {
        this.fbOtherFeeTermsSaving = false;
        const msg = await firstValueFrom(this.translate.get('somethingWentWrongPleaseTryAgain'));
        customToastr(msg, 'error');
      },
    });
  }

  // ---------- Assign students to Other Fee ----------
  fbAssignOtherFeeModalOpen = false;
  fbAssignOtherFeeTarget: FbOtherFeeRow | null = null;
  fbAssignOtherFeeSearchText = '';
  fbAssignOtherFeeSearchClassId: number | null = null;
  fbAssignOtherFeeStudentList: { studentId: number; studentName: string; registrationId?: string; className?: string; select?: boolean }[] = [];
  fbAssignOtherFeeExistingList: { studentId: number; studentName: string; registrationId?: string; className?: string }[] = [];
  fbAssignOtherFeeSaving = false;
  fbAssignOtherFeeTab: 'add' | 'existing' = 'add';
  fbClassListOfSchool: { classId: number; classNumber: string; classDivision: string }[] = [];

  openAssignStudentsToOtherFee(otherFee: FbOtherFeeRow): void {
    this.fbAssignOtherFeeTarget = otherFee;
    this.fbAssignOtherFeeTab = 'add';
    this.fbAssignOtherFeeSearchText = '';
    this.fbAssignOtherFeeStudentList = [];
    this.fbAssignOtherFeeModalOpen = true;
    this.feesService.getClassListOfSchool().subscribe({
      next: (data: any) => {
        this.fbClassListOfSchool = Array.isArray(data) ? data : [];
      },
      error: () => {
        this.fbClassListOfSchool = [];
      },
    });
    this.fbLoadStudentsInOtherFee();
  }

  closeAssignOtherFeeModal(): void {
    this.fbAssignOtherFeeModalOpen = false;
  }

  setAssignOtherFeeTab(tab: 'add' | 'existing'): void {
    this.fbAssignOtherFeeTab = tab;
  }

  searchStudentForOtherFee(): void {
    if (!this.fbAssignOtherFeeTarget || !this.fbAssignOtherFeeSearchClassId) {
      return;
    }
    this.feesService
      .searchStudentToAssignOtherFee(this.fbAssignOtherFeeSearchClassId, this.fbAssignOtherFeeSearchText, this.fbAssignOtherFeeTarget.pkOtherFeesId, this.currentAcademicId)
      .subscribe({
        next: (data: any) => {
          this.fbAssignOtherFeeStudentList = Array.isArray(data) ? data : [];
        },
        error: () => {
          this.fbAssignOtherFeeStudentList = [];
        },
      });
  }

  selectAllOtherFeeStudents(selected: boolean): void {
    this.fbAssignOtherFeeStudentList.forEach((s) => (s.select = selected));
  }

  // The backend expects the full selected student row objects here (the
  // same shape searchStudentToAssignOtherFee() returns), not bare student
  // IDs — matches FeesManagementComponent.assignStudentsToOtherFee()
  // exactly (feesmanage.component.ts:6857-6867), which pushes the whole
  // `data` row, not `data.studentId`, into the students[] array it posts.
  assignStudentsToOtherFee(): void {
    if (!this.fbAssignOtherFeeTarget) {
      return;
    }
    const students = this.fbAssignOtherFeeStudentList.filter((s) => s.select);
    if (students.length === 0) {
      return;
    }
    this.fbAssignOtherFeeSaving = true;
    this.feesService.addStudentsToOtherFee(students, this.fbAssignOtherFeeTarget.pkOtherFeesId).subscribe({
      next: async (data: any) => {
        this.fbAssignOtherFeeSaving = false;
        const msg = await firstValueFrom(this.translate.get(data?.status === false ? 'somethingWentWrongPleaseTryAgain' : 'settingUpdated'));
        customToastr(msg, data?.status === false ? 'error' : 'success');
        if (data?.status !== false) {
          this.fbLoadStudentsInOtherFee();
          this.fbLoadOtherFees();
        }
      },
      error: async () => {
        this.fbAssignOtherFeeSaving = false;
        const msg = await firstValueFrom(this.translate.get('somethingWentWrongPleaseTryAgain'));
        customToastr(msg, 'error');
      },
    });
  }

  private fbLoadStudentsInOtherFee(): void {
    if (!this.fbAssignOtherFeeTarget) {
      return;
    }
    this.feesService.getStudentsInOtherFee(this.fbAssignOtherFeeTarget.pkOtherFeesId).subscribe({
      next: (data: any) => {
        this.fbAssignOtherFeeExistingList = Array.isArray(data) ? data : [];
      },
      error: () => {
        this.fbAssignOtherFeeExistingList = [];
      },
    });
  }
  // ============================================================
  // Fee Collection — History & Reports sections. Ports
  // FeesManagementComponent's #fee-collection-history / #fee-collection-report
  // panes (getFeeCollectionHistoryForStudent(), initDataTableServerProcess(),
  // getPaidLateFeeList(), fetchAdminCollectedFeeHeadwise(),
  // fetchAdminCancelledFee(), exportHeadwiseToExcel()) against the SAME
  // endpoints. The old by-student table was a jQuery DataTables server-side
  // table; here the same /feeCollection/byDate_v2/datatable request is paged,
  // searched and sorted directly with plain Angular state instead.
  // ============================================================
  fcSection: 'collect' | 'history' | 'reports' = 'collect';

  fcInitSection(): void {
    // Matches feesCollectionTabClicked(): an admin who can only VIEW fee
    // collection (not collect) lands on History instead of Collect.
    if (this.canCollectFees) {
      this.fcSection = 'collect';
    } else if (this.canViewFeeCollectionHistory) {
      this.fcSection = 'history';
    }
  }

  fcSetSection(section: 'collect' | 'history' | 'reports'): void {
    // A view-only (sub) admin can still open Collect to look a student up and read
    // his/her payment / payment-update history; only the collect steps are gated.
    if (section === 'collect' && !(this.canCollectFees || this.canViewFeeCollectionHistory)) {
      return;
    }
    if ((section === 'history' || section === 'reports') && !this.canViewFeeCollectionHistory) {
      return;
    }
    this.fcSection = section;
  }

  // ---- History ----
  fchStart = '';
  fchEnd = '';
  fchLateFee = false;
  fchSummaryLoading = false;
  fchTableLoading = false;
  fchLoaded = false;
  fchModes: any = {};
  fchAdmins: any[] = [];
  fchRows: any[] = [];
  fchTotal = 0;
  fchPage = 1;
  fchPageSize = 25;
  fchSearch = '';
  fchSortCol = 0;
  fchSortDir: 'asc' | 'desc' = 'asc';
  readonly fchPageSizes = [25, 50, 100];
  readonly fchModeCards: { key: string; amount: string; students: string; icon: string }[] = [
    { key: 'cash', amount: 'totalCashAmount', students: 'totalCashPaidStudents', icon: 'ph-money' },
    { key: 'card', amount: 'totalCardAmount', students: 'totalCardPaidStudents', icon: 'ph-credit-card' },
    { key: 'qrCode', amount: 'totalQRCodeAmount', students: 'totalQRCodePaidStudents', icon: 'ph-qr-code' },
    { key: 'bankTransfer', amount: 'totalBankTransferAmount', students: 'totalBankTransferStudents', icon: 'ph-bank' },
    { key: 'dd', amount: 'totalDDAmount', students: 'totalDDStudents', icon: 'ph-newspaper' },
    { key: 'cheque', amount: 'totalChequeAmount', students: 'totalChequeStudents', icon: 'ph-note' },
    { key: 'online', amount: 'totalOnlineAmount', students: 'totalOnlinePaidStudents', icon: 'ph-globe' },
    { key: 'total', amount: 'totalAmount', students: 'studentsPaid', icon: 'ph-coins' },
  ];
  // Column index -> sortable server field order is the same one the old
  // DataTable declared (studentName ... ModeOfPayment).
  readonly fchColumns: { label: string; field: string }[] = [
    { label: 'studentName', field: 'studentName' },
    { label: 'parentName', field: 'parentName' },
    { label: 'mobile', field: 'mobileNumber' },
    { label: 'amount', field: 'Amount' },
    { label: 'lateFees', field: 'lateFees' },
    { label: 'class', field: 'className' },
    { label: 'feeName', field: 'feeName' },
    { label: 'feeType', field: 'feeType' },
    { label: 'date', field: 'collectionDate' },
    { label: 'creditDate', field: 'creditDate' },
    { label: 'paymentMode', field: 'ModeOfPayment' },
  ];

  get fchTotalPages(): number {
    return Math.max(1, Math.ceil(this.fchTotal / this.fchPageSize));
  }

  async fchToggleLateFee(): Promise<void> {
    if (!this.fchStart || !this.fchEnd) {
      const msg = await firstValueFrom(this.translate.get('pleaseSelectTheDateRange'));
      customToastr(msg, 'error');
      return;
    }
    this.fchLateFee = !this.fchLateFee;
  }

  async fchViewDetails(): Promise<void> {
    this.fchModes = {};
    this.fchAdmins = [];
    if (!this.fchStart || !this.fchEnd) {
      const msg = await firstValueFrom(this.translate.get('pleaseSelectTheDateRange'));
      customToastr(msg, 'error');
      return;
    }
    this.fchLoaded = true;
    this.fchPage = 1;
    this.fchSearch = '';
    this.fchSummaryLoading = true;
    this.fchLoadTable();
    this.feesService.getFeeHistoryForStudent(this.fchStart, this.fchEnd, this.currentAcademicId, this.fchLateFee).subscribe({
      next: (data: any) => {
        this.fchSummaryLoading = false;
        this.fchModes = data?.collectionModes || {};
        this.fchAdmins = data?.adminCollection || [];
      },
      error: () => {
        this.fchSummaryLoading = false;
      },
    });
  }

  private fchBody(countFrom: number, lengthOf: number) {
    return {
      schoolId: localStorage.getItem('schoolinfo'),
      startDate: this.fchStart,
      endDate: this.fchEnd,
      countFrom,
      lengthOf,
      academicId: this.currentAcademicId,
      search: this.fchSearch,
      sort: [{ column: this.fchSortCol, dir: this.fchSortDir }],
    };
  }

  fchLoadTable(): void {
    this.fchTableLoading = true;
    this.feesService.getFeeCollectionDatatable(this.fchBody((this.fchPage - 1) * this.fchPageSize, this.fchPageSize)).subscribe({
      next: (data: any) => {
        this.fchTableLoading = false;
        this.fchRows = data?.data || [];
        this.fchTotal = Number(data?.recordsFiltered ?? data?.recordsTotal ?? 0);
      },
      error: () => {
        this.fchTableLoading = false;
        this.fchRows = [];
        this.fchTotal = 0;
      },
    });
  }

  fchSort(index: number): void {
    if (this.fchSortCol === index) {
      this.fchSortDir = this.fchSortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.fchSortCol = index;
      this.fchSortDir = 'asc';
    }
    this.fchPage = 1;
    this.fchLoadTable();
  }

  fchSearchChanged(): void {
    this.fchPage = 1;
    this.fchLoadTable();
  }

  fchPageSizeChanged(): void {
    this.fchPage = 1;
    this.fchLoadTable();
  }

  fchGoToPage(page: number): void {
    if (page < 1 || page > this.fchTotalPages) {
      return;
    }
    this.fchPage = page;
    this.fchLoadTable();
  }

  private fchFormatDate(value: any, withTime: boolean): string {
    if (!value || value === '-') {
      return '-';
    }
    const d = new Date(value);
    if (isNaN(d.getTime())) {
      return String(value);
    }
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    let out = `${String(d.getDate()).padStart(2, '0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
    if (withTime) {
      const h = d.getHours();
      out += ` ${String(h % 12 || 12).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')} ${h >= 12 ? 'pm' : 'am'}`;
    }
    return out;
  }

  // Same export the old DataTable's Excel/CSV buttons produced: ALL rows
  // for the chosen range (old code swapped "show all" for recordsTotal), titled
  // "Fees collection export sheet <start> to <end>".
  fchExport(format: 'xlsx' | 'csv'): void {
    if (!this.fchLoaded || this.fchTotal === 0) {
      return;
    }
    this.feesService.getFeeCollectionDatatable(this.fchBody(0, this.fchTotal)).subscribe({
      next: async (data: any) => {
        const rows: any[] = data?.data || [];
        const headers = this.fchColumns.map((c) => c.field);
        const labels: string[] = [];
        for (const c of this.fchColumns) {
          labels.push(await firstValueFrom(this.translate.get(c.label)));
        }
        const title = `Fees collection export sheet ${this.fchStart} to ${this.fchEnd}`;
        const body = rows.map((r) =>
          headers.map((h) => {
            if (h === 'collectionDate') {
              return this.fchFormatDate(r[h], true);
            }
            if (h === 'creditDate') {
              return this.fchFormatDate(r[h], false);
            }
            return r[h] ?? '';
          })
        );
        const sheet: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet([[title], labels, ...body]);
        const book: XLSX.WorkBook = { Sheets: { 'Fee Collection': sheet }, SheetNames: ['Fee Collection'] };
        if (format === 'csv') {
          const csv = XLSX.utils.sheet_to_csv(sheet);
          FileSaver.saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), `${title}.csv`);
        } else {
          const buffer: any = XLSX.write(book, { bookType: 'xlsx', type: 'array' });
          FileSaver.saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${title}.xlsx`);
        }
      },
    });
  }

  // ---- Paid late fee by student (modal) ----
  fchPaidLateFeeOpen = false;
  fchPaidLateFeeLoading = false;
  fchPaidLateFeeList: any[] = [];

  async fchOpenPaidLateFee(): Promise<void> {
    if (!this.fchStart || !this.fchEnd) {
      const msg = await firstValueFrom(this.translate.get('pleaseSelectTheDateRange'));
      customToastr(msg, 'error');
      return;
    }
    this.fchPaidLateFeeOpen = true;
    this.fchPaidLateFeeLoading = true;
    this.fchPaidLateFeeList = [];
    this.feesService
      .fetchPaidLateFeeList({ academicId: this.currentAcademicId, stDate: this.fchStart, edDate: this.fchEnd })
      .subscribe({
        next: (data: any) => {
          this.fchPaidLateFeeLoading = false;
          const list: any[] = Array.isArray(data) ? data : [];
          this.fchPaidLateFeeList = list.map((tr) => ({
            ...tr,
            displayFeeName: tr.TermName ? tr.TermName : tr.FeesTypeName ? tr.FeesTypeName : tr.transport ? tr.transport : '',
            displayFeeType: tr.fkFeesTermId ? 'Term' : tr.fkFeesTypeId ? 'Other' : tr.transport ? 'Transport' : '',
            displayDate: this.fchFormatDate(tr.CreatedDate, false),
          }));
        },
        error: () => {
          this.fchPaidLateFeeLoading = false;
        },
      });
  }

  fchClosePaidLateFee(): void {
    this.fchPaidLateFeeOpen = false;
  }

  // ---- Reports ----
  fcrStart = '';
  fcrEnd = '';
  fcrLateFee = false;

  get fcrDateRangeSelected(): boolean {
    return !!this.fcrStart && !!this.fcrEnd;
  }

  async fcrToggleLateFee(): Promise<void> {
    if (!this.fcrDateRangeSelected) {
      const msg = await firstValueFrom(this.translate.get('pleaseSelectTheDateRange'));
      customToastr(msg, 'error');
      return;
    }
    this.fcrLateFee = !this.fcrLateFee;
  }

  // ---- Head-wise collection modal (admin-wise from History, plus the three
  // report types from Reports) ----
  fcHeadwiseOpen = false;
  fcHeadwiseLoading = false;
  fcFeeReportType: 'ADMIN_WISE' | 'SINGLE_ACADEMIC' | 'ALL_ACADEMICS' | 'ACADEMIC_WISE' = 'ADMIN_WISE';
  fcHeadwiseTitle = '';
  fcSelectedAdminName = '';
  fcAdminCollectedFees: { allFeeNames: string[]; data: any[] } = { allFeeNames: [], data: [] };

  fcOpenHeadwise(reportType: 'ADMIN_WISE' | 'SINGLE_ACADEMIC' | 'ALL_ACADEMICS' | 'ACADEMIC_WISE', adminId: any, adminName: string, title: string): void {
    this.fcFeeReportType = reportType;
    this.fcHeadwiseTitle = title;
    this.fcSelectedAdminName = adminName;
    this.fcAdminCollectedFees = { allFeeNames: [], data: [] };
    this.fcHeadwiseOpen = true;
    this.fcHeadwiseLoading = true;

    const done = {
      next: (data: any) => {
        this.fcHeadwiseLoading = false;
        this.fcAdminCollectedFees = { allFeeNames: data?.allFeeNames || [], data: data?.data || [] };
      },
      error: () => {
        this.fcHeadwiseLoading = false;
      },
    };

    if (reportType === 'ADMIN_WISE') {
      this.feesService
        .getFeeHistoryHeadsCollectedByAdmin(this.fchStart, this.fchEnd, adminId, this.currentAcademicId, this.fchLateFee)
        .subscribe(done);
    } else if (reportType === 'SINGLE_ACADEMIC') {
      this.feesService
        .getFeeHistoryHeadsCollectedFYByAdmin(this.fcrStart, this.fcrEnd, this.currentAcademicId, this.fcrLateFee)
        .subscribe(done);
    } else if (reportType === 'ALL_ACADEMICS') {
      this.feesService.getFeeHistoryHeadsCollectedFY(this.fcrStart, this.fcrEnd, this.fcrLateFee).subscribe(done);
    } else {
      this.feesService.getFeeHistoryHeadsCollectedAllAcademics(this.fcrStart, this.fcrEnd, this.fcrLateFee).subscribe(done);
    }
  }

  fcCloseHeadwise(): void {
    this.fcHeadwiseOpen = false;
  }

  headwiseAcademicsText(row: any): string {
    return Array.isArray(row?.academics) ? row.academics.join(' ') : '';
  }

  async fcExportHeadwiseToExcel(): Promise<void> {
    const t: any = await firstValueFrom(this.translate.get('feesData'));
    const headerRow: string[] = [];
    headerRow.push(this.fcFeeReportType !== 'ACADEMIC_WISE' ? 'Date' : 'Academic Year');
    if (this.fcFeeReportType === 'ALL_ACADEMICS') {
      headerRow.push('Academic');
    }
    this.fcAdminCollectedFees.allFeeNames.forEach((n) => headerRow.push(n));
    const c = this.currencyCode;
    headerRow.push(
      `${t.totalCashValue} (${c})`,
      `${t.totalCardValue} (${c})`,
      `${t.totalQRCode} (${c})`,
      `${t.totalBankTransfer} (${c})`,
      `${t.totalDD} (${c})`,
      `${t.totalCheque} (${c})`,
      `${t.totalOnline} (${c})`,
      `${t.totalFeeValue} (${c})`,
      `${t.totalLateFees} (${c})`
    );
    const dataRows = this.fcAdminCollectedFees.data.map((f) => {
      const row: any[] = [f.date || f.year];
      if (this.fcFeeReportType === 'ALL_ACADEMICS') {
        row.push((f.academics || []).join(', '));
      }
      this.fcAdminCollectedFees.allFeeNames.forEach((n) => row.push(f[n] || 0));
      row.push(
        f.totalCash || 0,
        f.totalCard || 0,
        f.collectedAsQRCode || 0,
        f.collectedAsBankTransfer || 0,
        f.collectedAsDD || 0,
        f.collectedAsCheque || 0,
        f.collectedAsOnline || 0,
        f.totalFee || 0,
        f.lateFees || 0
      );
      return row;
    });
    const sheet: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet([]);
    XLSX.utils.sheet_add_aoa(sheet, [[this.translate.instant(this.fcHeadwiseTitle)]], { origin: 'A1' });
    sheet['A1'].s = { font: { name: 'Arial', sz: 22, bold: true }, alignment: { horizontal: 'center' } };
    sheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: headerRow.length - 1 } }];
    XLSX.utils.sheet_add_aoa(sheet, [headerRow], { origin: 'A2' });
    XLSX.utils.sheet_add_aoa(sheet, dataRows, { origin: 'A3' });
    for (let col = 0; col < headerRow.length; col++) {
      const ref = XLSX.utils.encode_cell({ r: 1, c: col });
      if (sheet[ref]) {
        sheet[ref].s = { font: { bold: true, color: { rgb: '000000' } }, alignment: { horizontal: 'center' } };
      }
    }
    const book: XLSX.WorkBook = { Sheets: { 'Fee Collection Headwise': sheet }, SheetNames: ['Fee Collection Headwise'] };
    const buffer: any = XLSX.write(book, { bookType: 'xlsx', type: 'array' });
    FileSaver.saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${t.feesHeadWiseReport}.xlsx`);
  }

  // ---- Fees deleted by an admin (modal) ----
  fcDeletedFeeOpen = false;
  fcDeletedFeeLoading = false;
  fcAdminCancelledFees: any[] = [];

  fcOpenDeletedFee(adminId: any, adminName: string): void {
    this.fcSelectedAdminName = adminName;
    this.fcAdminCancelledFees = [];
    this.fcDeletedFeeOpen = true;
    this.fcDeletedFeeLoading = true;
    this.feesService.getAdminCancelledFeeHistory(adminId, this.fchStart, this.fchEnd, this.currentAcademicId).subscribe({
      next: (data: any) => {
        this.fcDeletedFeeLoading = false;
        this.fcAdminCancelledFees = Array.isArray(data) ? data : [];
      },
      error: () => {
        this.fcDeletedFeeLoading = false;
      },
    });
  }

  fcCloseDeletedFee(): void {
    this.fcDeletedFeeOpen = false;
  }
}

